import React, { useState, useEffect, useMemo, useRef } from "react";
import { supabase } from "./supabaseClient";
import { useTable } from "./dataLayer";

// ── Brand palette (Wilco Data Solutions) ───────────────────────────────────
const C = {
  bg: "#F9F8F6", surface: "#FFFFFF", border: "#E4E1DB",
  text: "#233142", textSoft: "#5C6B7A", muted: "#8A93A0",
  green: "#3A5F43", greenDark: "#2E4D36", greenTint: "#EAF0EB",
  slate: "#233142", slateSoft: "#5C6B7A",
  gold: "#3A5F43", goldDark: "#2E4D36", goldLight: "#EAF0EB",
  danger: "#B23A33", dangerLight: "#F7E9E8",
};

const styles = {
  app: { minHeight: "100vh", background: C.bg, color: C.text, fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' },
  main: { maxWidth: 1080, margin: "0 auto", padding: "24px 20px 80px" },
  card: { background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 20, marginBottom: 16, boxShadow: "0 2px 10px rgba(35,49,66,0.04)" },
  input: { width: "100%", padding: "10px 12px", border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 14, color: C.text, background: "#FFF", boxSizing: "border-box", fontFamily: "inherit" },
  label: { display: "block", fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 6 },
  grid2: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 },
  sectionTitle: { fontSize: 18, fontWeight: 700, color: C.text, marginBottom: 14 },
  empty: { padding: 28, textAlign: "center", color: C.muted, fontSize: 14 },
  btn: (kind = "primary") => {
    const base = { padding: "10px 16px", borderRadius: 9, fontSize: 14, fontWeight: 650, cursor: "pointer", border: "none", fontFamily: "inherit", transition: "background .15s,border-color .15s" };
    if (kind === "primary") return { ...base, background: C.green, color: "#FFF" };
    if (kind === "ghost") return { ...base, background: "transparent", color: C.text, border: `1.5px solid ${C.border}` };
    if (kind === "danger") return { ...base, background: C.dangerLight, color: C.danger };
    return base;
  },
  badge: (kind) => {
    const map = {
      invited: { background: "#EEF1F4", color: C.slate },
      yes: { background: C.greenTint, color: C.greenDark },
      maybe: { background: "#FBF3E7", color: "#8A6D2E" },
      no: { background: C.dangerLight, color: C.danger },
      invoiced: { background: "#FBF3E7", color: "#8A6D2E" },
      paid: { background: C.greenTint, color: C.greenDark },
      attended: { background: C.green, color: "#FFF" },
    };
    return { display: "inline-block", padding: "3px 10px", borderRadius: 20, fontSize: 12, fontWeight: 650, ...(map[kind] || map.invited) };
  },
};

// ── Shared UI ───────────────────────────────────────────────────────────────
function Field({ label, children }) {
  return <div style={{ marginBottom: 14 }}><label style={styles.label}>{label}</label>{children}</div>;
}

function Modal({ title, onClose, children, wide }) {
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(35,49,66,0.45)", display: "flex", alignItems: "flex-start", justifyContent: "center", padding: 20, zIndex: 100, overflowY: "auto" }}>
      <div onClick={e => e.stopPropagation()} style={{ background: C.surface, borderRadius: 16, padding: 28, maxWidth: wide ? 720 : 480, width: "100%", marginTop: 40, boxShadow: "0 24px 60px rgba(35,49,66,0.25)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
          <h2 style={{ fontSize: 20, fontWeight: 750, color: C.text, margin: 0 }}>{title}</h2>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 24, color: C.muted, cursor: "pointer", lineHeight: 1 }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Table({ rows, columns, actions, emptyText }) {
  if (!rows || rows.length === 0) return <div style={styles.empty}>{emptyText || "Nothing here yet."}</div>;
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
        <thead>
          <tr style={{ borderBottom: `2px solid ${C.border}` }}>
            {columns.map(c => <th key={c.key} style={{ textAlign: c.align || "left", padding: "10px 12px", color: C.textSoft, fontWeight: 650, fontSize: 12, textTransform: "uppercase", letterSpacing: "0.04em", whiteSpace: "nowrap" }}>{c.header}</th>)}
            {actions && <th style={{ width: 1 }}></th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={row.id || i} style={{ borderBottom: `1px solid ${C.border}` }}>
              {columns.map(c => (
                <td key={c.key} style={{ padding: "11px 12px", textAlign: c.align || "left", fontWeight: c.primary ? 650 : 400, color: c.primary ? C.text : C.text }}>
                  {c.render ? c.render(row) : (row[c.key] ?? "—")}
                </td>
              ))}
              {actions && <td style={{ padding: "8px 12px", whiteSpace: "nowrap", textAlign: "right" }}>{actions(row)}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Small helper to format a date string nicely.
const fmtDate = (d) => {
  if (!d) return "—";
  try { return new Date(d + (d.length === 10 ? "T00:00:00" : "")).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
  catch { return d; }
};
const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;

// Append a row to the payments ledger (mbr_payments) when a payment completes.
function recordPayment(dispatch, { amount, kind, eventId, memberId, sourceType, sourceId, label, method } = {}) {
  if (!dispatch || !(Number(amount) > 0)) return;
  dispatch({ type: "ADD", item: { amount: Number(amount) || 0, kind: kind || "other", eventId: eventId || null, memberId: memberId || null, sourceType: sourceType || "", sourceId: sourceId || null, label: label || "", method: method || "card", paidAt: new Date().toISOString() } });
}

// Build the cancellation checklist from an event's current state (deposits, payments, vendors).
function buildCancelChecklist(event, vendors = [], payments = []) {
  const evVen = vendors.filter(v => String(v.eventId) === String(event.id));
  const evPay = payments.filter(pm => String(pm.eventId) === String(event.id) && !pm.refunded && Number(pm.amount) > 0);
  const depVen = evVen.filter(v => v.depositPaid && Number(v.depositAmount) > 0);
  const depTotal = depVen.reduce((sum, v) => sum + (Number(v.depositAmount) || 0), 0);
  const payTotal = evPay.reduce((sum, pm) => sum + (Number(pm.amount) || 0), 0);
  const items = [{ key: "notify", label: "Notify all invitees that the event is cancelled", done: false }];
  if (depVen.length) items.push({ key: "refund_deposits", label: `Refund vendor deposits — ${money(depTotal)} across ${depVen.length} vendor${depVen.length === 1 ? "" : "s"}`, done: false });
  if (evPay.length) items.push({ key: "refund_payments", label: `Refund attendee payments — ${money(payTotal)} across ${evPay.length} payment${evPay.length === 1 ? "" : "s"}`, done: false });
  if (evVen.length) items.push({ key: "notify_vendors", label: `Inform ${evVen.length} vendor${evVen.length === 1 ? "" : "s"} the event is cancelled`, done: false });
  items.push({ key: "listing", label: "Remove the event from any public calendar or website listing", done: false });
  return items;
}

// ── Due-date highlighting: amber within 30 days, vibrant red once overdue ─────
// Dietary preference options + allergy handling (member profile → event catering).
const DIET_OPTIONS = [
  { key: "none", label: "No restriction" },
  { key: "vegetarian", label: "Vegetarian" },
  { key: "vegan", label: "Vegan" },
  { key: "pescatarian", label: "Pescatarian" },
  { key: "gluten_free", label: "Gluten-free" },
  { key: "kosher", label: "Kosher" },
  { key: "halal", label: "Halal" },
  { key: "other", label: "Other" },
];
const dietLabel = (k) => (DIET_OPTIONS.find(o => o.key === k) || {}).label || "";
const DIET_DISCLAIMER = "Informational only — not a substitute for attendees confirming dietary needs and allergies directly with catering.";

function dueInfo(dateStr) {
  if (!dateStr) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const due = new Date(dateStr); if (isNaN(due)) return null; due.setHours(0, 0, 0, 0);
  const days = Math.round((due - today) / 86400000);
  if (days < 0) return { days, tone: "overdue", label: `${Math.abs(days)}d overdue` };
  if (days <= 30) return { days, tone: "soon", label: days === 0 ? "due today" : `due in ${days}d` };
  return { days, tone: "ok", label: `renews ${fmtDate(dateStr)}` };
}
function DueBadge({ date, showOk = false }) {
  const info = dueInfo(date);
  if (!info) return null;
  if (info.tone === "ok" && !showOk) return null;
  const toneStyle = {
    overdue: { background: "#DC2626", color: "#FFF", border: "1px solid #B91C1C" },
    soon: { background: "#FEF3C7", color: "#92400E", border: "1px solid #FCD34D" },
    ok: { background: C.bg, color: C.textSoft, border: `1px solid ${C.border}` },
  }[info.tone];
  return <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 10, whiteSpace: "nowrap", ...toneStyle }}>{info.tone === "overdue" ? "⚠ " : ""}{info.label}</span>;
}

// ── Add-to-calendar: download a .ics or open a Google Calendar template ───────
function _icsYMD(d) { const p = n => String(n).padStart(2, "0"); return `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}`; }
function downloadICS(ev) {
  const start = ev.eventDate ? new Date(ev.eventDate) : null;
  if (!start || isNaN(start)) return;
  const end = new Date(start); end.setDate(end.getDate() + 1);
  const esc = s => String(s || "").replace(/([,;\\])/g, "\\$1").replace(/\n/g, "\\n");
  const now = new Date(); const p = n => String(n).padStart(2, "0");
  const stamp = `${_icsYMD(now)}T${p(now.getHours())}${p(now.getMinutes())}${p(now.getSeconds())}`;
  const desc = (ev.program && ev.program.length) ? "Program: " + ev.program.map(x => x.title || x).join(", ") : `You're registered for ${ev.name}.`;
  const lines = [
    "BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//Wilco Data Solutions//Membership//EN", "CALSCALE:GREGORIAN", "BEGIN:VEVENT",
    `UID:${ev.id || Math.random().toString(36).slice(2)}@wilcodatasolutions`,
    `DTSTAMP:${stamp}`, `DTSTART;VALUE=DATE:${_icsYMD(start)}`, `DTEND;VALUE=DATE:${_icsYMD(end)}`,
    `SUMMARY:${esc(ev.name)}`, ev.location ? `LOCATION:${esc(ev.location)}` : null, `DESCRIPTION:${esc(desc)}`,
    "END:VEVENT", "END:VCALENDAR",
  ].filter(Boolean);
  const blob = new Blob([lines.join("\r\n")], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = `${(ev.name || "event").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}.ics`;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
function googleCalUrl(ev) {
  const start = ev.eventDate ? new Date(ev.eventDate) : null; if (!start || isNaN(start)) return "#";
  const end = new Date(start); end.setDate(end.getDate() + 1);
  const params = new URLSearchParams({ action: "TEMPLATE", text: ev.name || "Event", dates: `${_icsYMD(start)}/${_icsYMD(end)}` });
  if (ev.location) params.set("location", ev.location);
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

// ── Name tags: pick fields, then print tag cards or export a CSV ──────────────
function escapeHtml(s) { return String(s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;"); }
function NameTagsModal({ members, tiers = [], label = "All members", onClose }) {
  const [tierFilter, setTierFilter] = useState("all");
  const [fields, setFields] = useState({ tier: true, groups: false, region: false, status: false, email: false });
  const tierLabel = (id) => (tiers.find(t => String(t.id) === String(id)) || {}).label || "";
  const list = members.filter(m => tierFilter === "all" ? true : String(m.tierId) === String(tierFilter));
  const nameOf = (m) => `${m.firstName || ""} ${m.lastName || ""}`.trim() || "(no name)";
  const fieldDefs = [
    { key: "tier", label: "Membership tier" },
    { key: "groups", label: "Groups (region / category)" },
    { key: "region", label: "City / State" },
    { key: "status", label: "Status" },
    { key: "email", label: "Email" },
  ];
  const linesFor = (m) => {
    const parts = [];
    if (fields.tier && m.tierId) parts.push(tierLabel(m.tierId));
    if (fields.groups && m.tags && m.tags.length) parts.push(m.tags.join(", "));
    if (fields.region) { const r = [m.addrCity, m.addrState].filter(Boolean).join(", "); if (r) parts.push(r); }
    if (fields.status && m.status) parts.push(m.status.charAt(0).toUpperCase() + m.status.slice(1));
    if (fields.email && m.email) parts.push(m.email);
    return parts;
  };
  const printTags = () => {
    const cards = list.map(m => {
      const secondary = linesFor(m).map(x => `<div class="line">${escapeHtml(x)}</div>`).join("");
      return `<div class="tag"><div class="org">Wilco Members</div><div class="name">${escapeHtml(nameOf(m))}</div>${secondary}</div>`;
    }).join("");
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Name Tags</title><style>`
      + `*{box-sizing:border-box}body{font-family:-apple-system,Segoe UI,Roboto,sans-serif;margin:0;padding:16px;background:#f3f4f6}`
      + `.grid{display:grid;grid-template-columns:repeat(2,1fr);gap:12px}`
      + `.tag{border:1px solid #cbd5e1;border-radius:10px;padding:18px;min-height:2in;display:flex;flex-direction:column;justify-content:center;align-items:center;text-align:center;background:#fff;page-break-inside:avoid}`
      + `.org{font-size:11px;letter-spacing:.08em;text-transform:uppercase;color:#2E9E5B;font-weight:700;margin-bottom:8px}`
      + `.name{font-size:26px;font-weight:800;color:#111}.line{font-size:14px;color:#475569;margin-top:4px}`
      + `.bar{padding:12px 16px;background:#fff;border:1px solid #e5e7eb;border-radius:8px;margin-bottom:16px;display:flex;justify-content:space-between;align-items:center}`
      + `button{font:inherit;padding:8px 16px;border-radius:8px;border:0;background:#2E9E5B;color:#fff;font-weight:700;cursor:pointer}`
      + `@media print{.bar{display:none}body{background:#fff;padding:0}.tag{border:1px dashed #999}}`
      + `</style></head><body><div class="bar"><span>${list.length} name tag(s)</span><button onclick="window.print()">Print</button></div><div class="grid">${cards}</div></body></html>`;
    const w = window.open("", "_blank");
    if (w && w.document) { w.document.write(html); w.document.close(); }
    else {
      const blob = new Blob([html], { type: "text/html" }); const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = "name-tags.html"; a.click(); setTimeout(() => URL.revokeObjectURL(url), 1000);
    }
  };
  const exportCSV = () => {
    const cols = ["Name"].concat(fields.tier ? ["Tier"] : [], fields.groups ? ["Groups"] : [], fields.region ? ["City/State"] : [], fields.status ? ["Status"] : [], fields.email ? ["Email"] : []);
    const rows = list.map(m => {
      const r = [nameOf(m)];
      if (fields.tier) r.push(tierLabel(m.tierId));
      if (fields.groups) r.push((m.tags || []).join("; "));
      if (fields.region) r.push([m.addrCity, m.addrState].filter(Boolean).join(", "));
      if (fields.status) r.push(m.status || "");
      if (fields.email) r.push(m.email || "");
      return r;
    });
    const csv = [cols].concat(rows).map(r => r.map(c => `"${String(c == null ? "" : c).replace(/"/g, '""')}"`).join(",")).join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" }); const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "name-tags.csv"; a.click(); setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  return (
    <Modal title="Name tags" onClose={onClose} wide>
      <div style={{ fontSize: 13, color: C.textSoft, marginBottom: 14 }}>Choose who to include and what appears under each name, then print tags or export a CSV for label sheets.</div>
      <Field label="Members">
        <select style={styles.input} value={tierFilter} onChange={e => setTierFilter(e.target.value)}>
          <option value="all">{label} ({members.length})</option>
          {tiers.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
        </select>
      </Field>
      <div style={{ fontSize: 13, fontWeight: 700, color: C.text, margin: "12px 0 8px" }}>Show under each name:</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
        {fieldDefs.map(f => (
          <label key={f.key} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, color: C.text, cursor: "pointer" }}>
            <input type="checkbox" checked={!!fields[f.key]} onChange={e => setFields({ ...fields, [f.key]: e.target.checked })} /> {f.label}
          </label>
        ))}
      </div>
      <div style={{ fontSize: 12, color: C.textSoft, marginBottom: 16 }}>{list.length} member{list.length === 1 ? "" : "s"} will be included.</div>
      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", flexWrap: "wrap" }}>
        <button style={styles.btn("ghost")} onClick={exportCSV}>Export CSV</button>
        <button style={styles.btn("primary")} onClick={printTags}>Print name tags</button>
      </div>
    </Modal>
  );
}


// ── Demo "Invoice & Charge" flow ─────────────────────────────────────────────
// A simulated invoice -> pay-online -> success experience. DEMO ONLY: no real
// money moves. In a live build this would connect to the org's Square / Stripe /
// PayPal account. Reused for both vendors and member event payments.
function PayDemoFlow({ title, recipientName, recipientEmail, lineItem, amount, onPaid, onClose }) {
  const [stage, setStage] = useState("invoice"); // invoice -> pay -> processing -> done
  const ref = "INV-" + Math.random().toString(36).slice(2, 7).toUpperCase();

  const pay = () => {
    setStage("processing");
    setTimeout(() => { setStage("done"); }, 1400);
  };
  const finish = () => { if (onPaid) onPaid(); onClose(); };

  return (
    <Modal title={title || "Invoice & Online Payment"} onClose={onClose} wide>
      {/* Demo banner */}
      <div style={{ background: C.greenTint, border: `1px solid ${C.green}`, borderRadius: 8, padding: "8px 12px", fontSize: 12, color: C.greenDark, marginBottom: 16 }}>
        🔒 <strong>Demo preview.</strong> No real payment is processed. In a live setup this connects to your Square, Stripe, or PayPal account.
      </div>

      {stage === "invoice" && (
        <>
          <div style={{ border: `1px solid ${C.border}`, borderRadius: 10, padding: 20, marginBottom: 18 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
              <div>
                <div style={{ fontSize: 18, fontWeight: 800, color: C.slate }}>Wilco <span style={{ color: C.green }}>Data Solutions</span></div>
                <div style={{ fontSize: 12, color: C.muted }}>Invoice {ref}</div>
              </div>
              <div style={{ textAlign: "right", fontSize: 12, color: C.textSoft }}>
                <div>Billed to:</div>
                <div style={{ fontWeight: 700, color: C.text }}>{recipientName || "—"}</div>
                {recipientEmail && <div>{recipientEmail}</div>}
              </div>
            </div>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                  <th style={{ textAlign: "left", padding: "8px 0", color: C.textSoft, fontSize: 12, textTransform: "uppercase" }}>Description</th>
                  <th style={{ textAlign: "right", padding: "8px 0", color: C.textSoft, fontSize: 12, textTransform: "uppercase" }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                  <td style={{ padding: "12px 0" }}>{lineItem || "Amount due"}</td>
                  <td style={{ padding: "12px 0", textAlign: "right" }}>{money(amount)}</td>
                </tr>
              </tbody>
              <tfoot>
                <tr><td style={{ padding: "12px 0", fontWeight: 750 }}>Total Due</td><td style={{ padding: "12px 0", textAlign: "right", fontWeight: 800, fontSize: 18, color: C.green }}>{money(amount)}</td></tr>
              </tfoot>
            </table>
          </div>
          <p style={{ fontSize: 13, color: C.muted, marginBottom: 16 }}>This is the invoice that would be emailed to {recipientName || "the recipient"}{recipientEmail ? ` at ${recipientEmail}` : ""}, with a secure link to pay online.</p>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <button style={styles.btn("ghost")} onClick={onClose}>Cancel</button>
            <button style={styles.btn("primary")} onClick={() => setStage("pay")}>Send &amp; Open Pay Link →</button>
          </div>
        </>
      )}

      {stage === "pay" && (
        <>
          <div style={{ textAlign: "center", marginBottom: 6, fontSize: 13, color: C.textSoft }}>Secure payment · powered by online payments</div>
          <div style={{ border: `1px solid ${C.border}`, borderRadius: 10, padding: 22, marginBottom: 18, maxWidth: 420, margin: "0 auto 18px" }}>
            <div style={{ textAlign: "center", marginBottom: 16 }}>
              <div style={{ fontSize: 13, color: C.textSoft }}>Amount due</div>
              <div style={{ fontSize: 32, fontWeight: 800, color: C.slate }}>{money(amount)}</div>
              <div style={{ fontSize: 12, color: C.muted }}>{recipientName} · {ref}</div>
            </div>
            <Field label="Card number"><input style={styles.input} placeholder="4242 4242 4242 4242" defaultValue="4242 4242 4242 4242" /></Field>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <Field label="Expiry"><input style={styles.input} placeholder="12 / 28" defaultValue="12 / 28" /></Field>
              <Field label="CVC"><input style={styles.input} placeholder="123" defaultValue="123" /></Field>
            </div>
            <button style={{ ...styles.btn("primary"), width: "100%", padding: 13, fontSize: 16, marginTop: 6 }} onClick={pay}>Pay {money(amount)}</button>
            <div style={{ fontSize: 11, color: C.muted, textAlign: "center", marginTop: 10 }}>🔒 Demo only — prefilled test card, nothing is charged.</div>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <button style={styles.btn("ghost")} onClick={() => setStage("invoice")}>← Back to invoice</button>
          </div>
        </>
      )}

      {stage === "processing" && (
        <div style={{ textAlign: "center", padding: "40px 0" }}>
          <div style={{ fontSize: 15, color: C.textSoft }}>Processing payment…</div>
          <div style={{ marginTop: 12, fontSize: 28 }}>⏳</div>
        </div>
      )}

      {stage === "done" && (
        <>
          <div style={{ textAlign: "center", padding: "24px 0" }}>
            <div style={{ fontSize: 44, marginBottom: 10 }}>✅</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: C.green, marginBottom: 6 }}>Payment received</div>
            <div style={{ fontSize: 14, color: C.textSoft }}>{money(amount)} from {recipientName} · {ref}</div>
            <div style={{ fontSize: 12, color: C.muted, marginTop: 8 }}>A receipt would be emailed automatically. Marked as paid.</div>
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <button style={styles.btn("primary")} onClick={finish}>Done</button>
          </div>
        </>
      )}
    </Modal>
  );
}

// Compute the full financial picture for one event.
// Income = member fees collected + vendor fees collected (incl. deposits).
// Outstanding = invited-but-unpaid member fees + unpaid vendor balances.
// Projected = income + outstanding (if everything still owed comes in).
function eventFinancials(event, invitations, expenses, vendors) {
  const price = Number(event.price) || 0;
  const guestPrice = Number(event.guestPrice) || 0;
  const evInv = invitations.filter(i => String(i.eventId) === String(event.id));
  const evExp = expenses.filter(x => String(x.eventId) === String(event.id));
  const evVen = vendors.filter(v => String(v.eventId) === String(event.id));

  // What one invitation owes: the member's fee + their guests' fees.
  const owedFor = (i) => price + (event.allowGuests ? (Number(i.guestCount) || 0) * guestPrice : 0);

  // Member attendance income (members + their guests)
  const paidInv = evInv.filter(i => i.paid);
  const memberCollected = paidInv.reduce((s, i) => s + owedFor(i), 0);
  // Outstanding: invited or coming, not paid (potential revenue)
  const unpaidInv = evInv.filter(i => !i.paid && i.rsvp !== "no");
  const memberOutstanding = unpaidInv.reduce((s, i) => s + owedFor(i), 0);
  const paidMembers = paidInv.length;
  const unpaidMembers = unpaidInv.length;

  // Vendor income: deposits collected + full fees where paid
  let vendorCollected = 0, vendorOutstanding = 0;
  evVen.forEach(v => {
    const fee = Number(v.fee) || 0;
    const dep = Number(v.depositAmount) || 0;
    if (v.paid) { vendorCollected += fee; }
    else if (v.depositPaid) { vendorCollected += dep; vendorOutstanding += Math.max(0, fee - dep); }
    else { vendorOutstanding += fee; }
  });

  const expenseTotal = evExp.reduce((s, x) => s + (Number(x.amount) || 0), 0);
  const collected = memberCollected + vendorCollected;
  const outstanding = memberOutstanding + vendorOutstanding;
  const net = collected - expenseTotal;
  const projectedNet = (collected + outstanding) - expenseTotal;

  return {
    price, guestPrice, paidMembers, unpaidMembers, memberCollected, memberOutstanding,
    vendorCollected, vendorOutstanding, vendorCount: evVen.length,
    expenseTotal, collected, outstanding, net, projectedNet,
  };
}

const EXPENSE_CATEGORIES = ["Venue", "Speaker", "Catering", "Equipment", "Marketing", "Staff", "Travel", "Other"];

// ── Demo role previews ───────────────────────────────────────────────────────
// NOTE: this is a DEMO preview of role-based access — it adapts what the
// interface shows. It is not a security boundary on its own; a real deployment
// enforces these limits in the database. The switcher lets you show each view.
const ROLES = {
  admin:  { label: "Admin",  blurb: "Full access — financials, contact details, everything." },
  staff:  { label: "Staff",  blurb: "Events, attendance, check-in & meals. No financials or contact info." },
  viewer: { label: "Viewer", blurb: "Names and attendance summaries only. No contact info, money, or financials." },
};
const can = (role) => ({
  financials: role === "admin",
  money: role === "admin",                 // dollar amounts: prices, amounts owed, totals
  paidStatus: role === "admin" || role === "staff", // paid/unpaid status (staff work the door)
  collectPayment: role === "admin" || role === "staff", // take payment at the door
  contact: role === "admin",               // email, phone, address
  editMembers: role === "admin",
  manageEvents: role === "admin",
  checkIn: role === "admin" || role === "staff",
  meals: role === "admin" || role === "staff",
  vendors: role === "admin",
  providers: role === "admin",           // assign caterers/vendors from the directory
});

// Scoped permission set for the simulated "Event Manager" preview: can work
// the event's checklist, RSVPs, and door, but no financials, contact, or approvals.
const MANAGER_PERMS = { financials: false, money: false, paidStatus: true, collectPayment: true, contact: false, editMembers: false, manageEvents: false, checkIn: true, meals: true, vendors: true, providers: true };

// ── Member Import (CSV upload or paste, with column mapping) ──────────────────
const IMPORT_FIELDS = [
  { key: "firstName", label: "First Name" },
  { key: "lastName", label: "Last Name" },
  { key: "email", label: "Email" },
  { key: "phone", label: "Phone" },
  { key: "addrStreet", label: "Street Address" },
  { key: "addrCity", label: "City" },
  { key: "addrState", label: "State" },
  { key: "addrZip", label: "ZIP" },
  { key: "tags", label: "Groups/Tags (comma-separated)" },
  { key: "notes", label: "Notes" },
];

// Minimal, robust CSV/TSV line parser (handles quoted fields and commas/tabs).
function parseDelimited(text) {
  const rows = [];
  const lines = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n").split("\n").filter(l => l.trim() !== "");
  if (lines.length === 0) return rows;
  // Detect delimiter: tab if present in first line, else comma.
  const delim = lines[0].includes("\t") ? "\t" : ",";
  for (const line of lines) {
    const out = []; let cur = ""; let inQ = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (inQ) {
        if (ch === '"' && line[i + 1] === '"') { cur += '"'; i++; }
        else if (ch === '"') inQ = false;
        else cur += ch;
      } else {
        if (ch === '"') inQ = true;
        else if (ch === delim) { out.push(cur); cur = ""; }
        else cur += ch;
      }
    }
    out.push(cur);
    rows.push(out.map(c => c.trim()));
  }
  return rows;
}

// Guess which column maps to which field, from header names.
function autoGuessMapping(headers) {
  const map = {};
  const norm = (s) => s.toLowerCase().replace(/[^a-z]/g, "");
  headers.forEach((h, idx) => {
    const n = norm(h);
    const match = (cands) => cands.some(c => n === c || n.includes(c));
    if (map.firstName == null && match(["firstname", "fname", "first", "givenname"])) map.firstName = idx;
    else if (map.lastName == null && match(["lastname", "lname", "last", "surname", "familyname"])) map.lastName = idx;
    else if (map.email == null && match(["email", "emailaddress", "mail"])) map.email = idx;
    else if (map.phone == null && match(["phone", "mobile", "cell", "telephone", "tel"])) map.phone = idx;
    else if (map.addrStreet == null && match(["street", "address", "addressline", "addr"])) map.addrStreet = idx;
    else if (map.addrCity == null && match(["city", "town"])) map.addrCity = idx;
    else if (map.addrState == null && match(["state", "province", "region"])) map.addrState = idx;
    else if (map.addrZip == null && match(["zip", "zipcode", "postal", "postalcode"])) map.addrZip = idx;
    else if (map.tags == null && match(["tags", "groups", "group", "tier", "category", "chapter"])) map.tags = idx;
    else if (map.notes == null && match(["notes", "note", "comment", "comments"])) map.notes = idx;
  });
  return map;
}

function ImportMembers({ onClose, onImport, existingEmails }) {
  const [step, setStep] = useState("input"); // input -> map -> done
  const [raw, setRaw] = useState("");
  const [rows, setRows] = useState([]);
  const [hasHeader, setHasHeader] = useState(true);
  const [mapping, setMapping] = useState({});
  const [result, setResult] = useState(null);

  const handleFile = (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => { setRaw(String(reader.result || "")); };
    reader.readAsText(f);
  };

  const parseNow = () => {
    const parsed = parseDelimited(raw);
    if (parsed.length === 0) return;
    setRows(parsed);
    const headers = hasHeader ? parsed[0] : parsed[0].map((_, i) => `Column ${i + 1}`);
    setMapping(hasHeader ? autoGuessMapping(headers) : {});
    setStep("map");
  };

  const headers = rows.length ? (hasHeader ? rows[0] : rows[0].map((_, i) => `Column ${i + 1}`)) : [];
  const dataRows = hasHeader ? rows.slice(1) : rows;

  const buildMembers = () => {
    const out = [];
    for (const r of dataRows) {
      const m = { firstName: "", lastName: "", email: "", phone: "", addrStreet: "", addrCity: "", addrState: "", addrZip: "", tags: [], notes: "", diet: "none", allergies: "", noContact: false };
      for (const f of IMPORT_FIELDS) {
        const col = mapping[f.key];
        if (col == null || col === "") continue;
        const val = (r[col] || "").trim();
        if (f.key === "tags") m.tags = val ? val.split(",").map(t => t.trim()).filter(Boolean) : [];
        else m[f.key] = val;
      }
      // Skip empty rows (no name at all)
      if (!m.firstName && !m.lastName && !m.email) continue;
      out.push(m);
    }
    return out;
  };

  const doImport = () => {
    const all = buildMembers();
    const seen = new Set((existingEmails || []).map(e => e.toLowerCase()));
    let added = 0, skipped = 0;
    const toAdd = [];
    for (const m of all) {
      const key = (m.email || "").toLowerCase();
      if (key && seen.has(key)) { skipped++; continue; }
      if (key) seen.add(key);
      toAdd.push(m); added++;
    }
    onImport(toAdd);
    setResult({ added, skipped, total: all.length });
    setStep("done");
  };

  const mappedCount = Object.values(mapping).filter(v => v != null && v !== "").length;
  const previewMembers = step === "map" ? buildMembers().slice(0, 5) : [];

  return (
    <Modal title="Import Members" onClose={onClose} wide>
      {step === "input" && (
        <>
          <p style={{ fontSize: 13, color: C.muted, marginBottom: 14 }}>Bring in your existing member list. Upload a CSV file, or paste rows copied straight from a spreadsheet.</p>
          <Field label="Upload a CSV file">
            <input type="file" accept=".csv,.tsv,.txt,text/csv" onChange={handleFile} style={{ ...styles.input, padding: 8 }} />
          </Field>
          <div style={{ textAlign: "center", color: C.muted, fontSize: 12, margin: "6px 0" }}>— or —</div>
          <Field label="Paste from a spreadsheet">
            <textarea style={{ ...styles.input, minHeight: 120, fontFamily: "monospace", fontSize: 12 }} value={raw} onChange={e => setRaw(e.target.value)} placeholder={"First Name, Last Name, Email, Phone\nJane, Doe, jane@example.com, 555-1234"} />
          </Field>
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, marginBottom: 16, cursor: "pointer" }}>
            <input type="checkbox" checked={hasHeader} onChange={e => setHasHeader(e.target.checked)} />
            First row is a header (column names)
          </label>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <button style={styles.btn("ghost")} onClick={onClose}>Cancel</button>
            <button style={styles.btn("primary")} onClick={parseNow} disabled={!raw.trim()}>Next: Map Columns →</button>
          </div>
        </>
      )}

      {step === "map" && (
        <>
          <p style={{ fontSize: 13, color: C.muted, marginBottom: 14 }}>Match your columns to the right fields. We guessed where we could — adjust anything that's off. Leave a field as "—" to skip it.</p>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 }}>
            {IMPORT_FIELDS.map(f => (
              <div key={f.key} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 13, width: 130, color: C.text, fontWeight: 600 }}>{f.label}</span>
                <select style={{ ...styles.input, padding: "6px 8px" }} value={mapping[f.key] ?? ""} onChange={e => setMapping({ ...mapping, [f.key]: e.target.value === "" ? null : Number(e.target.value) })}>
                  <option value="">—</option>
                  {headers.map((h, i) => <option key={i} value={i}>{h}</option>)}
                </select>
              </div>
            ))}
          </div>

          {previewMembers.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.textSoft, marginBottom: 6 }}>PREVIEW (first {previewMembers.length})</div>
              <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, overflow: "hidden" }}>
                {previewMembers.map((m, i) => (
                  <div key={i} style={{ padding: "8px 12px", borderBottom: i < previewMembers.length - 1 ? `1px solid ${C.border}` : "none", fontSize: 13 }}>
                    <strong>{`${m.firstName} ${m.lastName}`.trim() || "(no name)"}</strong>
                    {m.email ? ` · ${m.email}` : ""}{m.tags && m.tags.length ? ` · ${m.tags.join(", ")}` : ""}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
            <button style={styles.btn("ghost")} onClick={() => setStep("input")}>← Back</button>
            <button style={styles.btn("primary")} onClick={doImport} disabled={mappedCount === 0}>Import {dataRows.length} row{dataRows.length === 1 ? "" : "s"}</button>
          </div>
        </>
      )}

      {step === "done" && result && (
        <>
          <div style={{ textAlign: "center", padding: "20px 0" }}>
            <div style={{ fontSize: 40, marginBottom: 10 }}>✓</div>
            <div style={{ fontSize: 18, fontWeight: 750, color: C.green, marginBottom: 6 }}>Import complete</div>
            <div style={{ fontSize: 14, color: C.textSoft }}>
              Added <strong>{result.added}</strong> member{result.added === 1 ? "" : "s"}.
              {result.skipped > 0 && <> Skipped <strong>{result.skipped}</strong> duplicate{result.skipped === 1 ? "" : "s"} (matching email).</>}
            </div>
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <button style={styles.btn("primary")} onClick={onClose}>Done</button>
          </div>
        </>
      )}
    </Modal>
  );
}

// ── Members tab ──────────────────────────────────────────────────────────────
function Members({ members, dispatch, allTags, onManageTags, tiers = [], perms = {} }) {
  const [showNameTags, setShowNameTags] = useState(false);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [search, setSearch] = useState("");
  const [tagFilter, setTagFilter] = useState("");
  const [showImport, setShowImport] = useState(false);

  const openAdd = () => { setForm({ firstName: "", lastName: "", email: "", phone: "", addrStreet: "", addrCity: "", addrState: "", addrZip: "", tierId: "", status: "active", joinedOn: "", renewsOn: "", tags: [], notes: "", diet: "none", allergies: "", noContact: false }); setModal("add"); };
  const openEdit = (m) => { setForm({ ...m, tags: m.tags || [] }); setModal(m); };
  const close = () => setModal(null);

  const save = () => {
    if (!form.firstName && !form.lastName) return;
    const item = { firstName: form.firstName || "", lastName: form.lastName || "", email: (form.email || "").trim(), phone: form.phone || "", addrStreet: form.addrStreet || "", addrCity: form.addrCity || "", addrState: form.addrState || "", addrZip: form.addrZip || "", tierId: form.tierId || null, status: form.status || "active", joinedOn: form.joinedOn || null, renewsOn: form.renewsOn || null, tags: form.tags || [], notes: form.notes || "", diet: form.diet || "none", allergies: form.allergies || "", noContact: !!form.noContact };
    if (modal === "add") dispatch({ type: "ADD", item }); else dispatch({ type: "UPDATE", id: modal.id, changes: item });
    close();
  };

  const toggleTag = (t) => setForm(f => { const has = (f.tags || []).includes(t); return { ...f, tags: has ? f.tags.filter(x => x !== t) : [...(f.tags || []), t] }; });

  const filtered = members.filter(m => {
    const name = `${m.firstName || ""} ${m.lastName || ""}`.toLowerCase();
    const matchSearch = !search || name.includes(search.toLowerCase()) || (m.email || "").toLowerCase().includes(search.toLowerCase());
    const matchTag = !tagFilter || (m.tags || []).includes(tagFilter);
    return matchSearch && matchTag;
  });

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, gap: 10, flexWrap: "wrap" }}>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", flex: 1 }}>
          <input style={{ ...styles.input, maxWidth: 240 }} placeholder="Search members…" value={search} onChange={e => setSearch(e.target.value)} />
          <select style={{ ...styles.input, maxWidth: 180 }} value={tagFilter} onChange={e => setTagFilter(e.target.value)}>
            <option value="">All groups</option>
            {allTags.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {perms.editMembers && <button style={styles.btn("ghost")} onClick={onManageTags}>Manage Groups</button>}
          {perms.editMembers && <button style={styles.btn("ghost")} onClick={() => setShowImport(true)}>⬆ Import</button>}
          {perms.editMembers && <button style={styles.btn("ghost")} onClick={() => setShowNameTags(true)}>🏷️ Name tags</button>}
          {perms.editMembers && <button style={styles.btn("primary")} onClick={openAdd}>+ Add Member</button>}
        </div>
      </div>

      {showNameTags && <NameTagsModal members={members} tiers={tiers} onClose={() => setShowNameTags(false)} />}

      <div style={styles.card}>
        <Table
          rows={filtered}
          emptyText="No members yet. Add your first member to get started."
          columns={[
            { key: "name", header: "Member", primary: true, render: m => `${m.firstName || ""} ${m.lastName || ""}`.trim() || "—" },
            { key: "tags", header: "Groups", render: m => (m.tags && m.tags.length) ? <span style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>{m.tags.map(t => <span key={t} style={{ ...styles.badge("yes"), fontSize: 11 }}>{t}</span>)}</span> : "—" },
            { key: "renewal", header: "Renewal", render: m => m.renewsOn ? <DueBadge date={m.renewsOn} showOk /> : "—" },
            ...(perms.contact ? [
              { key: "email", header: "Email", render: m => m.email || "—" },
              { key: "phone", header: "Phone", render: m => m.phone || "—" },
              { key: "city", header: "City", render: m => [m.addrCity, m.addrState].filter(Boolean).join(", ") || "—" },
            ] : []),
          ]}
          actions={perms.editMembers ? (m => (
            <>
              <button style={{ ...styles.btn("ghost"), fontSize: 12, padding: "5px 10px" }} onClick={() => openEdit(m)}>Edit</button>
              <button style={{ ...styles.btn("danger"), fontSize: 12, padding: "5px 10px", marginLeft: 6 }} onClick={() => dispatch({ type: "DELETE", id: m.id })}>Remove</button>
            </>
          )) : null}
        />
        {!perms.contact && <div style={{ fontSize: 12, color: C.textSoft, marginTop: 10 }}>Contact details and addresses are hidden for this role.</div>}
      </div>

      {modal && (
        <Modal title={modal === "add" ? "Add Member" : "Edit Member"} onClose={close}>
          <div style={styles.grid2}>
            <Field label="First Name"><input style={styles.input} value={form.firstName || ""} onChange={e => setForm({ ...form, firstName: e.target.value })} /></Field>
            <Field label="Last Name"><input style={styles.input} value={form.lastName || ""} onChange={e => setForm({ ...form, lastName: e.target.value })} /></Field>
          </div>
          <div style={styles.grid2}>
            <Field label="Email"><input style={styles.input} type="email" value={form.email || ""} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="member@example.com" /></Field>
            <Field label="Phone"><input style={styles.input} value={form.phone || ""} onChange={e => setForm({ ...form, phone: e.target.value })} /></Field>
          </div>
          <Field label="Street Address"><input style={styles.input} value={form.addrStreet || ""} onChange={e => setForm({ ...form, addrStreet: e.target.value })} placeholder="123 Main St" /></Field>
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 12, marginBottom: 14 }}>
            <Field label="City"><input style={styles.input} value={form.addrCity || ""} onChange={e => setForm({ ...form, addrCity: e.target.value })} /></Field>
            <Field label="State"><input style={styles.input} value={form.addrState || ""} onChange={e => setForm({ ...form, addrState: e.target.value })} placeholder="ST" /></Field>
            <Field label="ZIP"><input style={styles.input} value={form.addrZip || ""} onChange={e => setForm({ ...form, addrZip: e.target.value })} /></Field>
          </div>
          <div style={{ padding: 12, background: C.bg, borderRadius: 8, border: `1px solid ${C.border}`, marginBottom: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.textSoft, textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 10 }}>Membership</div>
            <div style={styles.grid2}>
              <Field label="Tier">
                <select style={styles.input} value={form.tierId || ""} onChange={e => setForm({ ...form, tierId: e.target.value })}>
                  <option value="">— none —</option>
                  {tiers.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
                </select>
              </Field>
              <Field label="Status">
                <select style={styles.input} value={form.status || "active"} onChange={e => setForm({ ...form, status: e.target.value })}>
                  <option value="active">Active</option>
                  <option value="pending">Pending</option>
                  <option value="lapsed">Lapsed</option>
                </select>
              </Field>
            </div>
            <div style={styles.grid2}>
              <Field label="Joined on"><input style={styles.input} type="date" value={form.joinedOn || ""} onChange={e => setForm({ ...form, joinedOn: e.target.value })} /></Field>
              <Field label="Renews on"><input style={styles.input} type="date" value={form.renewsOn || ""} onChange={e => setForm({ ...form, renewsOn: e.target.value })} /></Field>
            </div>
          </div>
          <div style={{ padding: 12, background: C.bg, borderRadius: 8, border: `1px solid ${C.border}`, marginBottom: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.textSoft, textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 10 }}>Dietary &amp; allergies</div>
            <div style={styles.grid2}>
              <Field label="Dietary preference">
                <select style={styles.input} value={form.diet || "none"} onChange={e => setForm({ ...form, diet: e.target.value })}>
                  {DIET_OPTIONS.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
                </select>
              </Field>
              <Field label="Food allergies"><input style={styles.input} value={form.allergies || ""} onChange={e => setForm({ ...form, allergies: e.target.value })} placeholder="e.g. peanuts, shellfish" /></Field>
            </div>
            <DietDisclaimer style={{ marginTop: 8 }} />
          </div>
          <Field label="Groups / Tags">
            {allTags.length === 0 ? (
              <div style={{ fontSize: 13, color: C.muted }}>No groups defined yet. Use "Manage Groups" to create some (e.g. regions or tiers).</div>
            ) : (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {allTags.map(t => {
                  const on = (form.tags || []).includes(t);
                  return <button key={t} onClick={() => toggleTag(t)} style={{ ...styles.badge(on ? "paid" : "invited"), cursor: "pointer", border: on ? `1.5px solid ${C.green}` : `1.5px solid ${C.border}` }}>{on ? "✓ " : ""}{t}</button>;
                })}
              </div>
            )}
          </Field>
          <Field label="Notes"><textarea style={{ ...styles.input, minHeight: 60, resize: "vertical" }} value={form.notes || ""} onChange={e => setForm({ ...form, notes: e.target.value })} /></Field>
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, marginBottom: 16, cursor: "pointer" }}>
            <input type="checkbox" checked={!!form.noContact} onChange={e => setForm({ ...form, noContact: e.target.checked })} />
            Do not contact (excludes from email invitations)
          </label>
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <button style={styles.btn("ghost")} onClick={close}>Cancel</button>
            <button style={styles.btn("primary")} onClick={save}>Save Member</button>
          </div>
        </Modal>
      )}

      {showImport && (
        <ImportMembers
          onClose={() => setShowImport(false)}
          existingEmails={members.map(m => m.email).filter(Boolean)}
          onImport={(list) => { list.forEach(item => dispatch({ type: "ADD", item })); }}
        />
      )}
    </div>
  );
}

// ── Manage Groups modal ───────────────────────────────────────────────────────
function GroupManager({ tags, onChange, onClose }) {
  const [val, setVal] = useState("");
  const add = () => { const t = val.trim(); if (!t || tags.includes(t)) return; onChange([...tags, t]); setVal(""); };
  return (
    <Modal title="Manage Groups & Tags" onClose={onClose}>
      <p style={{ fontSize: 13, color: C.muted, marginBottom: 14 }}>Create the groups you want to organize members by — regions, membership tiers, chapters, anything. Assign them per member.</p>
      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        <input style={styles.input} value={val} onChange={e => setVal(e.target.value)} onKeyDown={e => e.key === "Enter" && add()} placeholder="e.g. Northeast Region, Gold Tier" />
        <button style={styles.btn("primary")} onClick={add}>Add</button>
      </div>
      {tags.length === 0 ? <div style={styles.empty}>No groups yet.</div> : (
        <div>{tags.map(t => (
          <div key={t} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: `1px solid ${C.border}` }}>
            <span style={{ ...styles.badge("paid") }}>{t}</span>
            <button style={{ ...styles.btn("danger"), fontSize: 12, padding: "4px 10px" }} onClick={() => onChange(tags.filter(x => x !== t))}>Remove</button>
          </div>
        ))}</div>
      )}
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
        <button style={styles.btn("primary")} onClick={onClose}>Done</button>
      </div>
    </Modal>
  );
}

// ── Events tab ────────────────────────────────────────────────────────────────
// ── Provider / supplier directory (caterers, venues, vendors) ────────────────
const PROVIDER_TYPES = [
  { key: "caterer", label: "Caterers" },
  { key: "venue", label: "Venues" },
  { key: "vendor", label: "Vendors" },
  { key: "other", label: "Other" },
];
const providerStars = (n) => { const k = Math.max(0, Math.min(5, Number(n) || 0)); return "★★★★★".slice(0, k) + "☆☆☆☆☆".slice(0, 5 - k); };

// Admin manager for the reusable directory of vetted providers.
function ProviderDirectory({ providers, dispatch, reviews = [], onClose }) {
  const [edit, setEdit] = useState(null); // "add" | provider | null
  const [form, setForm] = useState({});
  const openAdd = () => { setForm({ name: "", type: "caterer", contact: "", rating: 0, preferred: false, notes: "" }); setEdit("add"); };
  const openEdit = (p) => { setForm({ ...p }); setEdit(p); };
  const save = () => {
    if (!form.name) return;
    const item = { name: form.name.trim(), type: form.type || "caterer", contact: form.contact || "", rating: Number(form.rating) || 0, preferred: !!form.preferred, notes: form.notes || "" };
    if (edit === "add") dispatch({ type: "ADD", item }); else dispatch({ type: "UPDATE", id: edit.id, changes: item });
    setEdit(null);
  };
  const remove = (p) => dispatch({ type: "DELETE", id: p.id });

  return (
    <Modal title="Provider directory" onClose={onClose} wide>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
        <div style={{ fontSize: 13, color: C.textSoft, flex: 1, minWidth: 180 }}>Your reusable list of vetted caterers, venues, and vendors. Assign them to events from the event page.</div>
        <button style={styles.btn("primary")} onClick={openAdd}>+ Add</button>
      </div>
      {providers.length === 0 ? (
        <div style={styles.empty}>No providers yet. Add the caterers and vendors you've worked with.</div>
      ) : PROVIDER_TYPES.map(t => {
        const list = providers.filter(p => (p.type || "other") === t.key).sort((a, b) => (b.preferred ? 1 : 0) - (a.preferred ? 1 : 0));
        if (list.length === 0) return null;
        return (
          <div key={t.key} style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 6 }}>{t.label}</div>
            {list.map(p => (
              <div key={p.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, padding: "8px 12px", background: C.bg, borderRadius: 8, marginBottom: 6 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 650, color: C.text }}>{p.name}{p.preferred && <span style={{ ...styles.badge("paid"), marginLeft: 8 }}>Preferred</span>}</div>
                  <div style={{ fontSize: 12, color: C.textSoft }}>{p.rating ? <span style={{ color: "#B8860B" }}>{providerStars(p.rating)}</span> : null}{p.rating && p.contact ? " · " : ""}{p.contact || ""}</div>
                  {(() => { const st = providerStats(p.id, reviews); if (!st.count) return null; const bits = []; if (st.avgCost != null) bits.push(st.minCost === st.maxCost ? `~${money(st.avgCost)}` : `~${money(st.avgCost)} (${money(st.minCost)}–${money(st.maxCost)})`); if (st.avgRating != null) bits.push(`★${st.avgRating.toFixed(1)}`); return <div style={{ fontSize: 12, color: C.green, marginTop: 2, fontWeight: 600 }}>Used {st.count}× · {bits.join(" · ")}</div>; })()}
                </div>
                <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                  <button style={{ ...styles.btn("ghost"), fontSize: 12 }} onClick={() => openEdit(p)}>Edit</button>
                  <button style={{ ...styles.btn("danger"), fontSize: 12 }} onClick={() => remove(p)}>Remove</button>
                </div>
              </div>
            ))}
          </div>
        );
      })}

      {edit && (
        <Modal title={edit === "add" ? "Add provider" : "Edit provider"} onClose={() => setEdit(null)}>
          <Field label="Name"><input style={styles.input} value={form.name || ""} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Heritage Catering Co." /></Field>
          <div style={styles.grid2}>
            <Field label="Type">
              <select style={styles.input} value={form.type || "caterer"} onChange={e => setForm({ ...form, type: e.target.value })}>
                <option value="caterer">Caterer</option>
                <option value="venue">Venue</option>
                <option value="vendor">Vendor</option>
                <option value="other">Other</option>
              </select>
            </Field>
            <Field label="Rating (0–5)"><input style={styles.input} type="number" min="0" max="5" step="1" value={form.rating ?? 0} onChange={e => setForm({ ...form, rating: e.target.value })} /></Field>
          </div>
          <Field label="Contact"><input style={styles.input} value={form.contact || ""} onChange={e => setForm({ ...form, contact: e.target.value })} placeholder="email or phone" /></Field>
          <Field label="Notes"><textarea style={{ ...styles.input, minHeight: 60, resize: "vertical" }} value={form.notes || ""} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="What they did well, pricing, etc." /></Field>
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, color: C.text, margin: "4px 0 14px" }}><input type="checkbox" checked={!!form.preferred} onChange={e => setForm({ ...form, preferred: e.target.checked })} /> Preferred provider</label>
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <button style={styles.btn("ghost")} onClick={() => setEdit(null)}>Cancel</button>
            <button style={styles.btn("primary")} onClick={save}>Save</button>
          </div>
        </Modal>
      )}
    </Modal>
  );
}

// Per-provider cost + rating history across events → budgeting + track record.
function providerStats(providerId, reviews = [], excludeEventId) {
  const rs = reviews.filter(r => String(r.providerId) === String(providerId) && (!excludeEventId || String(r.eventId) !== String(excludeEventId)));
  const costs = rs.map(r => Number(r.cost)).filter(c => c > 0);
  const rated = rs.map(r => Number(r.rating)).filter(n => n > 0);
  const sum = a => a.reduce((x, y) => x + y, 0);
  return {
    count: rs.length,
    avgCost: costs.length ? Math.round(sum(costs) / costs.length) : null,
    minCost: costs.length ? Math.min(...costs) : null,
    maxCost: costs.length ? Math.max(...costs) : null,
    avgRating: rated.length ? sum(rated) / rated.length : null,
  };
}
function providerHint(providerId, reviews, excludeEventId) {
  const st = providerStats(providerId, reviews, excludeEventId);
  if (!st.count) return null;
  const bits = [];
  if (st.avgCost != null) bits.push(st.minCost === st.maxCost ? `~${money(st.avgCost)}` : `~${money(st.avgCost)} (${money(st.minCost)}–${money(st.maxCost)})`);
  if (st.avgRating != null) bits.push(`★${st.avgRating.toFixed(1)}`);
  return `Previously: ${bits.join(" · ")} over ${st.count} event${st.count === 1 ? "" : "s"}`;
}

// Event-level: which directory providers are assigned to this event, plus each
// one's cost and post-event review — building a cost/quality history for budgeting.
function EventProviders({ event, providers, reviews = [], reviewsDispatch, evDispatch, perms = {} }) {
  const [pick, setPick] = useState(false);
  const [reviewP, setReviewP] = useState(null);
  const [rform, setRform] = useState({ cost: "", rating: 0, comment: "" });
  const assigned = event.providers || [];
  const dirById = (id) => providers.find(p => String(p.id) === String(id));
  const resolved = assigned.map(a => dirById(a.providerId)).filter(Boolean);
  const setAssigned = (next) => evDispatch && evDispatch({ type: "UPDATE", id: event.id, changes: { providers: next } });
  const add = (id) => { setAssigned([...assigned, { providerId: String(id) }]); setPick(false); };
  const removeAssigned = (id) => setAssigned(assigned.filter(a => String(a.providerId) !== String(id)));
  const unassigned = providers.filter(p => !assigned.some(a => String(a.providerId) === String(p.id)));
  const reviewFor = (pid) => reviews.find(r => String(r.providerId) === String(pid) && String(r.eventId) === String(event.id));
  const openReview = (p) => { const ex = reviewFor(p.id); setRform(ex ? { cost: ex.cost ?? "", rating: ex.rating || 0, comment: ex.comment || "" } : { cost: "", rating: 0, comment: "" }); setReviewP(p); };
  const saveReview = () => {
    if (!reviewsDispatch || !reviewP) return;
    const ex = reviewFor(reviewP.id);
    const item = { cost: Number(rform.cost) || 0, rating: Number(rform.rating) || 0, comment: rform.comment || "" };
    if (ex) reviewsDispatch({ type: "UPDATE", id: ex.id, changes: item });
    else reviewsDispatch({ type: "ADD", item: { providerId: reviewP.id, eventId: event.id, ...item, reviewedAt: new Date().toISOString() } });
    setReviewP(null);
  };
  const clearReview = () => { const ex = reviewP && reviewFor(reviewP.id); if (ex && reviewsDispatch) reviewsDispatch({ type: "DELETE", id: ex.id }); setReviewP(null); };
  const eventCostTotal = resolved.reduce((s, p) => { const r = reviewFor(p.id); return s + (r ? Number(r.cost) || 0 : 0); }, 0);

  if (resolved.length === 0 && !perms.providers) return null;

  return (
    <div style={styles.card}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
        <div style={{ ...styles.sectionTitle, marginBottom: 0 }}>Suppliers &amp; providers</div>
        {perms.providers && <button style={styles.btn("ghost")} onClick={() => setPick(true)}>+ Assign from directory</button>}
      </div>
      {resolved.length === 0 ? (
        <div style={styles.empty}>No providers assigned yet.</div>
      ) : resolved.map(p => {
        const r = reviewFor(p.id);
        const h = providerHint(p.id, reviews, event.id);
        return (
          <div key={p.id} style={{ padding: "10px 0", borderBottom: `1px solid ${C.border}` }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 650, color: C.text }}>{p.name} <span style={{ fontSize: 12, color: C.muted, fontWeight: 400 }}>· {p.type}</span>{p.preferred && <span style={{ ...styles.badge("paid"), marginLeft: 8 }}>Preferred</span>}</div>
                {p.contact && <div style={{ fontSize: 12, color: C.textSoft }}>{p.contact}</div>}
                {h && <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{h}</div>}
                {r && (Number(r.cost) > 0 || Number(r.rating) > 0 || r.comment) && (
                  <div style={{ fontSize: 12, color: C.green, marginTop: 4, fontWeight: 600 }}>This event: {Number(r.cost) > 0 ? money(r.cost) : "—"}{Number(r.rating) > 0 ? ` · ${providerStars(r.rating)}` : ""}{r.comment ? ` · “${r.comment}”` : ""}</div>
                )}
              </div>
              {perms.providers && (
                <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                  <button style={{ ...styles.btn("ghost"), fontSize: 12 }} onClick={() => openReview(p)}>{r ? "Edit cost/review" : "Log cost & review"}</button>
                  <button title="Remove" style={{ background: "none", border: "none", cursor: "pointer", color: C.muted, fontWeight: 700, fontSize: 16 }} onClick={() => removeAssigned(p.id)}>×</button>
                </div>
              )}
            </div>
          </div>
        );
      })}
      {eventCostTotal > 0 && <div style={{ display: "flex", justifyContent: "space-between", marginTop: 12, fontSize: 14, fontWeight: 700, color: C.text }}><span>Provider cost for this event</span><span>{money(eventCostTotal)}</span></div>}

      {pick && (
        <Modal title="Assign a provider" onClose={() => setPick(false)}>
          {providers.length === 0 ? (
            <div style={styles.empty}>Your directory is empty. Add providers from the Events tab → Providers first.</div>
          ) : unassigned.length === 0 ? (
            <div style={styles.empty}>All directory providers are already assigned.</div>
          ) : unassigned.map(p => {
            const h = providerHint(p.id, reviews, event.id);
            return (
              <button key={p.id} onClick={() => add(p.id)} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, width: "100%", textAlign: "left", padding: "10px 12px", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, marginBottom: 6, cursor: "pointer" }}>
                <span style={{ minWidth: 0 }}>
                  <span style={{ fontSize: 14, fontWeight: 600, color: C.text }}>{p.name} <span style={{ fontSize: 12, color: C.muted }}>· {p.type}</span></span>
                  {h && <span style={{ display: "block", fontSize: 12, color: C.muted }}>{h}</span>}
                </span>
                {p.preferred && <span style={styles.badge("paid")}>Preferred</span>}
              </button>
            );
          })}
        </Modal>
      )}

      {reviewP && (
        <Modal title={`${reviewP.name} — cost & review`} onClose={() => setReviewP(null)}>
          <div style={{ fontSize: 12, color: C.textSoft, marginBottom: 12 }}>What they cost for this event and how they did. This builds their cost and quality history for future budgeting.</div>
          <div style={styles.grid2}>
            <Field label="Cost for this event ($)"><input style={styles.input} type="number" min="0" step="1" value={rform.cost} onChange={e => setRform({ ...rform, cost: e.target.value })} /></Field>
            <Field label="Rating (0–5)"><input style={styles.input} type="number" min="0" max="5" step="1" value={rform.rating} onChange={e => setRform({ ...rform, rating: e.target.value })} /></Field>
          </div>
          <Field label="Review (optional)"><textarea style={{ ...styles.input, minHeight: 60, resize: "vertical" }} value={rform.comment} onChange={e => setRform({ ...rform, comment: e.target.value })} placeholder="How did they do? Anything to remember for next time?" /></Field>
          <div style={{ display: "flex", gap: 10, justifyContent: "space-between", marginTop: 4 }}>
            <div>{reviewFor(reviewP.id) && <button style={styles.btn("danger")} onClick={clearReview}>Delete</button>}</div>
            <div style={{ display: "flex", gap: 10 }}>
              <button style={styles.btn("ghost")} onClick={() => setReviewP(null)}>Cancel</button>
              <button style={styles.btn("primary")} onClick={saveReview}>Save</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Event pre-flight checklist: built-in defaults + admin-editable template ───
const BUILTIN_CHECKLIST = [
  { label: "Venue reserved", required: true, leadDays: 60, sort: 1 },
  { label: "Budget approved", required: true, leadDays: 55, sort: 2 },
  { label: "Program approved", required: true, leadDays: 45, sort: 3 },
  { label: "Caterer hired", required: true, leadDays: 45, sort: 4 },
  { label: "Catering headcount deadline set", required: false, leadDays: 21, sort: 5 },
  { label: "Volunteers / staff assigned", required: false, leadDays: 14, sort: 6 },
];

function effectiveChecklistDefaults(defaults) {
  const src = (defaults && defaults.length) ? defaults : BUILTIN_CHECKLIST;
  return [...src].sort((a, b) => (Number(a.sort) || 0) - (Number(b.sort) || 0));
}

// Build a fresh per-event checklist from the defaults, dating each item off the event date.
function buildChecklist(defaults, eventDate) {
  const base = eventDate ? new Date(eventDate + "T00:00:00") : null;
  return effectiveChecklistDefaults(defaults).map((d, i) => {
    let due = null;
    if (base) { const dd = new Date(base); dd.setDate(dd.getDate() - (Number(d.leadDays) || 0)); due = dd.toISOString().slice(0, 10); }
    return { id: `c${Date.now()}_${i}`, label: d.label, required: !!d.required, due, done: false };
  });
}

// Admin editor for the global default checklist applied to every new event.
function ChecklistDefaultsModal({ defaults, dispatch, onClose }) {
  const [rows, setRows] = useState(() => {
    const src = (defaults && defaults.length) ? defaults.map(d => ({ ...d })) : BUILTIN_CHECKLIST.map(d => ({ ...d }));
    return src.sort((a, b) => (Number(a.sort) || 0) - (Number(b.sort) || 0));
  });
  const setRow = (i, k, v) => setRows(rs => rs.map((r, idx) => idx === i ? { ...r, [k]: v } : r));
  const addRow = () => setRows(rs => [...rs, { label: "", required: false, leadDays: 14, sort: rs.length + 1 }]);
  const removeRow = (i) => setRows(rs => rs.filter((_, idx) => idx !== i));
  const save = () => {
    (defaults || []).forEach(d => dispatch({ type: "DELETE", id: d.id }));
    rows.filter(r => (r.label || "").trim()).forEach((r, i) => dispatch({ type: "ADD", item: { label: r.label.trim(), required: !!r.required, leadDays: Number(r.leadDays) || 0, sort: i + 1 } }));
    onClose();
  };
  return (
    <Modal title="Checklist defaults" onClose={onClose} wide>
      <div style={{ fontSize: 13, color: C.textSoft, marginBottom: 14 }}>These items are added to every new event. Lead time sets each item's due date relative to the event date. Required items must be completed before invitations can go out.</div>
      {rows.map((r, i) => (
        <div key={i} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8 }}>
          <input style={{ ...styles.input, flex: 2, minWidth: 120 }} value={r.label || ""} onChange={e => setRow(i, "label", e.target.value)} placeholder="Task (e.g. Venue reserved)" />
          <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, color: C.text, whiteSpace: "nowrap" }}><input type="checkbox" checked={!!r.required} onChange={e => setRow(i, "required", e.target.checked)} /> Required</label>
          <input style={{ ...styles.input, width: 92 }} type="number" step="1" value={r.leadDays ?? ""} onChange={e => setRow(i, "leadDays", e.target.value)} placeholder="lead days" title="Days before the event this is due" />
          <button title="Remove" style={{ background: "none", border: "none", cursor: "pointer", color: C.danger, fontWeight: 700, fontSize: 18, lineHeight: 1 }} onClick={() => removeRow(i)}>×</button>
        </div>
      ))}
      <button style={styles.btn("ghost")} onClick={addRow}>+ Add default item</button>
      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 16 }}>
        <button style={styles.btn("ghost")} onClick={onClose}>Cancel</button>
        <button style={styles.btn("primary")} onClick={save}>Save defaults</button>
      </div>
    </Modal>
  );
}

function Events({ events, dispatch, invitations, onOpenEvent, checklistDefaults = [], checklistDefaultsDispatch, providers = [], providersDispatch, vendors = [], payments = [], reviews = [], perms = {} }) {
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});

  const openAdd = () => { setForm({ name: "", eventDate: "", location: "", price: "", mealOptions: [], mealDeadline: "", program: [], notes: "", allowGuests: false, guestTypes: [], guestPrice: "", inviteMessage: "" }); setModal("add"); };
  const openEdit = (ev) => { setForm({ ...ev, mealOptions: ev.mealOptions || [], program: ev.program || [], guestTypes: ev.guestTypes || [] }); setModal(ev); };
  const close = () => setModal(null);
  const [mealInput, setMealInput] = useState("");
  const [showDefaults, setShowDefaults] = useState(false);
  const [showProviders, setShowProviders] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);

  const save = () => {
    if (!form.name) return;
    const guestTypes = (form.guestTypes || []).filter(g => (g.label || "").trim() !== "").map(g => ({ label: g.label.trim(), price: Number(g.price) || 0, cap: g.cap === "" || g.cap == null ? null : Number(g.cap) }));
    const program = (form.program || []).filter(p => (p.title || "").trim() !== "" || (p.speaker || "").trim() !== "").map(p => ({ title: (p.title || "").trim(), speaker: (p.speaker || "").trim(), detail: (p.detail || "").trim() }));
    // guest_price is kept in sync as a back-compat bridge (first category's price)
    // until the invitation flow becomes category-aware in a later batch.
    const guestPrice = guestTypes.length ? guestTypes[0].price : (Number(form.guestPrice) || 0);
    const item = { name: form.name, eventDate: form.eventDate || null, location: form.location || "", price: Number(form.price) || 0, mealOptions: form.mealOptions || [], mealDeadline: form.mealDeadline || null, program, notes: form.notes || "", allowGuests: !!form.allowGuests, guestTypes, guestPrice, inviteMessage: form.inviteMessage || "" };
    if (modal === "add") dispatch({ type: "ADD", item: { ...item, checklist: buildChecklist(checklistDefaults, form.eventDate), planApproved: false, published: false } });
    else dispatch({ type: "UPDATE", id: modal.id, changes: item });
    close();
  };

  const addMeal = () => { const m = mealInput.trim(); if (!m || (form.mealOptions || []).includes(m)) return; setForm(f => ({ ...f, mealOptions: [...(f.mealOptions || []), m] })); setMealInput(""); };
  const removeMeal = (m) => setForm(f => ({ ...f, mealOptions: (f.mealOptions || []).filter(x => x !== m) }));

  // Program / speaker outline rows
  const addProgramItem = () => setForm(f => ({ ...f, program: [...(f.program || []), { title: "", speaker: "", detail: "" }] }));
  const setProgramItem = (i, key, val) => setForm(f => ({ ...f, program: (f.program || []).map((p, idx) => idx === i ? { ...p, [key]: val } : p) }));
  const removeProgramItem = (i) => setForm(f => ({ ...f, program: (f.program || []).filter((_, idx) => idx !== i) }));

  // Guest categories (label / price / optional cap)
  const addGuestType = () => setForm(f => ({ ...f, guestTypes: [...(f.guestTypes || []), { label: "", price: "", cap: "" }] }));
  const setGuestType = (i, key, val) => setForm(f => ({ ...f, guestTypes: (f.guestTypes || []).map((g, idx) => idx === i ? { ...g, [key]: val } : g) }));
  const removeGuestType = (i) => setForm(f => ({ ...f, guestTypes: (f.guestTypes || []).filter((_, idx) => idx !== i) }));

  const countFor = (eventId) => invitations.filter(i => String(i.eventId) === String(eventId)).length;
  const comingFor = (ev) => {
    const yes = invitations.filter(i => String(i.eventId) === String(ev.id) && i.rsvp === "yes");
    const heads = yes.length + (ev.allowGuests ? yes.reduce((s, i) => s + (Number(i.guestCount) || 0), 0) : 0);
    return heads;
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div style={{ fontSize: 13, color: C.muted }}>Create events, then invite members and track who's coming.</div>
        {perms.manageEvents && (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button style={styles.btn("ghost")} onClick={() => setShowDefaults(true)}>⚙️ Checklist defaults</button>
            <button style={styles.btn("ghost")} onClick={() => setShowProviders(true)}>📇 Providers</button>
            <button style={styles.btn("primary")} onClick={openAdd}>+ Add Event</button>
          </div>
        )}
        {showDefaults && <ChecklistDefaultsModal defaults={checklistDefaults} dispatch={checklistDefaultsDispatch} onClose={() => setShowDefaults(false)} />}
        {showProviders && <ProviderDirectory providers={providers} dispatch={providersDispatch} reviews={reviews} onClose={() => setShowProviders(false)} />}
        {cancelTarget && (
          <Modal title={`Cancel “${cancelTarget.name}”?`} onClose={() => setCancelTarget(null)}>
            <div style={{ fontSize: 13, color: C.textSoft, marginBottom: 12 }}>Cancelling an event is a guided process. Before it can be finalized, an admin must complete these steps:</div>
            <ul style={{ margin: "0 0 14px", paddingLeft: 20, fontSize: 14, color: C.text }}>
              {buildCancelChecklist(cancelTarget, vendors, payments).map(it => <li key={it.key} style={{ marginBottom: 6 }}>{it.label}</li>)}
            </ul>
            <div style={{ fontSize: 12, color: C.textSoft, marginBottom: 16 }}>Starting cancellation adds this checklist to the event under a red <strong>PENDING CANCELLATION</strong> banner. The event isn't cancelled until every item is checked off.</div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button style={styles.btn("ghost")} onClick={() => setCancelTarget(null)}>Keep event</button>
              <button style={styles.btn("danger")} onClick={() => { const id = cancelTarget.id; dispatch({ type: "UPDATE", id, changes: { cancelPending: true, cancelChecklist: buildCancelChecklist(cancelTarget, vendors, payments) } }); setCancelTarget(null); onOpenEvent(id); }}>Start cancellation</button>
            </div>
          </Modal>
        )}
      </div>

      {events.length === 0 ? (
        <div style={styles.card}><div style={styles.empty}>No events yet. Add one to start inviting members.</div></div>
      ) : (
        <div style={{ display: "grid", gap: 14 }}>
          {events.map(ev => (
            <div key={ev.id} style={styles.card}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 10 }}>
                <div>
                  <div style={{ fontSize: 18, fontWeight: 750, color: C.green }}>{ev.name}{ev.cancelled && <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 10, background: "#FEE2E2", color: "#991B1B", marginLeft: 8, verticalAlign: "middle" }}>Cancelled</span>}{!ev.published && !ev.cancelled && <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 10, background: "#EEF2FF", color: "#3730A3", marginLeft: 8, verticalAlign: "middle" }}>Draft</span>}</div>
                  <div style={{ fontSize: 13, color: C.textSoft, marginTop: 4 }}>
                    {fmtDate(ev.eventDate)}{ev.location ? ` · ${ev.location}` : ""}{Number(ev.price) > 0 ? ` · $${Number(ev.price).toFixed(2)}` : " · Free"}
                  </div>
                  <div style={{ fontSize: 13, color: C.textSoft, marginTop: 6 }}>
                    {countFor(ev.id)} invited · <strong style={{ color: C.green }}>{comingFor(ev)} coming</strong>{ev.allowGuests ? " (incl. guests)" : ""}
                    {ev.mealOptions && ev.mealOptions.length > 0 && <span> · Meals: {ev.mealOptions.join(", ")}</span>}
                    {ev.mealDeadline && <span> · RSVP by {fmtDate(ev.mealDeadline)}</span>}
                  </div>
                  {((ev.program && ev.program.length > 0) || (ev.guestTypes && ev.guestTypes.length > 0)) && (
                    <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>
                      {ev.program && ev.program.length > 0 && <span>📋 {ev.program.length} program item{ev.program.length === 1 ? "" : "s"}</span>}
                      {ev.program && ev.program.length > 0 && ev.guestTypes && ev.guestTypes.length > 0 && <span> · </span>}
                      {ev.guestTypes && ev.guestTypes.length > 0 && <span>🎟️ Guests: {ev.guestTypes.map(g => g.label || "Guest").join(", ")}</span>}
                    </div>
                  )}
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button style={styles.btn("primary")} onClick={() => onOpenEvent(ev.id)}>Manage Invitations</button>
                  {perms.manageEvents && <button style={{ ...styles.btn("ghost"), fontSize: 12 }} onClick={() => openEdit(ev)}>Edit</button>}
                  {perms.manageEvents && !ev.cancelled && (ev.cancelPending
                    ? <button style={{ ...styles.btn("ghost"), fontSize: 12, color: "#B91C1C", borderColor: "#FCA5A5" }} onClick={() => onOpenEvent(ev.id)}>⛔ Pending cancellation</button>
                    : <button style={{ ...styles.btn("ghost"), fontSize: 12 }} onClick={() => setCancelTarget(ev)}>Cancel Event</button>)}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {modal && (
        <Modal title={modal === "add" ? "Add Event" : "Edit Event"} onClose={close}>
          <Field label="Event Name"><input style={styles.input} value={form.name || ""} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Annual Gala" /></Field>
          <div style={styles.grid2}>
            <Field label="Date"><input style={styles.input} type="date" value={form.eventDate || ""} onChange={e => setForm({ ...form, eventDate: e.target.value })} /></Field>
            <Field label="Price to attend ($)"><input style={styles.input} type="number" step="any" value={form.price || ""} onChange={e => setForm({ ...form, price: e.target.value })} placeholder="0 for free" /></Field>
          </div>
          <Field label="Location"><input style={styles.input} value={form.location || ""} onChange={e => setForm({ ...form, location: e.target.value })} /></Field>
          <div style={{ padding: 12, background: C.bg, borderRadius: 8, border: `1px solid ${C.border}`, marginBottom: 14 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, cursor: "pointer", marginBottom: form.allowGuests ? 10 : 0 }}>
              <input type="checkbox" checked={!!form.allowGuests} onChange={e => setForm({ ...form, allowGuests: e.target.checked })} />
              Allow members to bring guests
            </label>
            {form.allowGuests && (
              <div>
                <div style={{ fontSize: 12, color: C.muted, marginBottom: 8 }}>Guest categories — set a price per type of guest. Price 0 = free (a deliberate growth lever). Leave the limit blank for no cap.</div>
                {(form.guestTypes || []).map((g, i) => (
                  <div key={i} style={{ display: "flex", gap: 8, marginBottom: 8, alignItems: "center" }}>
                    <input style={{ ...styles.input, flex: 2 }} value={g.label || ""} onChange={e => setGuestType(i, "label", e.target.value)} placeholder="e.g. Spouse, Colleague, Prospective member" />
                    <input style={{ ...styles.input, flex: 1 }} type="number" step="any" value={g.price ?? ""} onChange={e => setGuestType(i, "price", e.target.value)} placeholder="$ each" />
                    <input style={{ ...styles.input, width: 84 }} type="number" step="1" value={g.cap ?? ""} onChange={e => setGuestType(i, "cap", e.target.value)} placeholder="limit" />
                    <button title="Remove category" style={{ background: "none", border: "none", cursor: "pointer", color: C.danger, fontWeight: 700, fontSize: 18, lineHeight: 1 }} onClick={() => removeGuestType(i)}>×</button>
                  </div>
                ))}
                <button style={styles.btn("ghost")} onClick={addGuestType}>+ Add guest category</button>
                {(form.guestTypes || []).length === 0 && <div style={{ fontSize: 12, color: C.textSoft, marginTop: 6 }}>No categories yet — guests will be free by default.</div>}
              </div>
            )}
          </div>
          <Field label="Meal Options (optional)">
            <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
              <input style={styles.input} value={mealInput} onChange={e => setMealInput(e.target.value)} onKeyDown={e => e.key === "Enter" && (e.preventDefault(), addMeal())} placeholder="e.g. Chicken, Fish, Vegetarian" />
              <button style={styles.btn("ghost")} onClick={addMeal}>Add</button>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {(form.mealOptions || []).map(m => (
                <span key={m} style={{ ...styles.badge("invited"), display: "inline-flex", alignItems: "center", gap: 6 }}>{m}<button onClick={() => removeMeal(m)} style={{ background: "none", border: "none", cursor: "pointer", color: C.danger, fontWeight: 700 }}>×</button></span>
              ))}
            </div>
          </Field>
          <Field label="RSVP / meal-choice deadline (optional)"><input style={styles.input} type="date" value={form.mealDeadline || ""} onChange={e => setForm({ ...form, mealDeadline: e.target.value })} /></Field>
          <Field label="Program / Speakers (optional)">
            <div style={{ fontSize: 12, color: C.muted, marginBottom: 8 }}>The session outline members see on their invitation — talks, speakers, and timing.</div>
            {(form.program || []).map((p, i) => (
              <div key={i} style={{ display: "flex", gap: 8, marginBottom: 8, alignItems: "center" }}>
                <input style={{ ...styles.input, flex: 2 }} value={p.title || ""} onChange={e => setProgramItem(i, "title", e.target.value)} placeholder="Segment (e.g. Keynote)" />
                <input style={{ ...styles.input, flex: 2 }} value={p.speaker || ""} onChange={e => setProgramItem(i, "speaker", e.target.value)} placeholder="Speaker (optional)" />
                <input style={{ ...styles.input, width: 110 }} value={p.detail || ""} onChange={e => setProgramItem(i, "detail", e.target.value)} placeholder="Time / notes" />
                <button title="Remove item" style={{ background: "none", border: "none", cursor: "pointer", color: C.danger, fontWeight: 700, fontSize: 18, lineHeight: 1 }} onClick={() => removeProgramItem(i)}>×</button>
              </div>
            ))}
            <button style={styles.btn("ghost")} onClick={addProgramItem}>+ Add program item</button>
          </Field>
          <Field label="Invitation Message (shown to members on their invitation)">
            <textarea style={{ ...styles.input, minHeight: 70, resize: "vertical" }} value={form.inviteMessage || ""} onChange={e => setForm({ ...form, inviteMessage: e.target.value })} placeholder="e.g. We'd be delighted to have you join us for an evening of celebration and community. We hope you can make it!" />
          </Field>
          <Field label="Notes"><textarea style={{ ...styles.input, minHeight: 50, resize: "vertical" }} value={form.notes || ""} onChange={e => setForm({ ...form, notes: e.target.value })} /></Field>
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <button style={styles.btn("ghost")} onClick={close}>Cancel</button>
            <button style={styles.btn("primary")} onClick={save}>Save Event</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Member-facing invitation (demo) ──────────────────────────────────────────
// Shows what an invited member would receive: the organizer's message, event
// details, RSVP choices, and (for paid events, after they accept) a pay step.
// DEMO: clicking through as the member updates their RSVP/payment in the app.
function MemberInvitationView({ event, member, invitation, onRespond, onPaid, onClose }) {
  const [showPay, setShowPay] = useState(false);
  const rsvp = invitation.rsvp || "invited";
  const paid = !!invitation.paid;
  const isPaidEvent = Number(event.price) > 0;
  const owed = (Number(event.price) || 0) + (event.allowGuests ? (Number(invitation.guestCount) || 0) * (Number(event.guestPrice) || 0) : 0);

  const RsvpButton = ({ value, label, color }) => (
    <button onClick={() => onRespond(value)}
      style={{ flex: 1, padding: "12px 8px", borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: "pointer",
        border: rsvp === value ? `2px solid ${color}` : `1.5px solid ${C.border}`,
        background: rsvp === value ? color : "#FFF", color: rsvp === value ? "#FFF" : C.text }}>
      {label}
    </button>
  );

  return (
    <Modal title="Member Invitation" onClose={onClose} wide>
      <div style={{ background: C.greenTint, border: `1px solid ${C.green}`, borderRadius: 8, padding: "8px 12px", fontSize: 12, color: C.greenDark, marginBottom: 16 }}>
        👁️ <strong>Demo preview</strong> of what {member ? member.firstName : "the member"} receives. You can respond and pay here as them to see the flow.
      </div>

      {/* The invitation "card" the member sees */}
      <div style={{ border: `1px solid ${C.border}`, borderRadius: 12, overflow: "hidden", marginBottom: 18 }}>
        <div style={{ background: C.slate, color: "#FFF", padding: "20px 24px", textAlign: "center" }}>
          <div style={{ fontSize: 13, opacity: 0.8, letterSpacing: "0.05em", textTransform: "uppercase" }}>You're invited</div>
          <div style={{ fontSize: 24, fontWeight: 800, marginTop: 4 }}>{event.name}</div>
          <div style={{ fontSize: 14, opacity: 0.9, marginTop: 6 }}>{fmtDate(event.eventDate)}{event.location ? ` · ${event.location}` : ""}</div>
        </div>
        <div style={{ padding: "22px 24px" }}>
          <div style={{ fontSize: 15, color: C.text, marginBottom: 8 }}>Dear {member ? `${member.firstName} ${member.lastName}`.trim() : "Member"},</div>
          <div style={{ fontSize: 14, color: C.textSoft, lineHeight: 1.6, marginBottom: 18, whiteSpace: "pre-wrap" }}>
            {event.inviteMessage || "We'd be delighted to have you join us. Please let us know if you can attend."}
          </div>

          {isPaidEvent && (
            <div style={{ fontSize: 13, color: C.textSoft, marginBottom: 16, padding: "10px 14px", background: C.bg, borderRadius: 8 }}>
              Attendance: <strong>{money(event.price)}</strong>{event.allowGuests && Number(event.guestPrice) > 0 ? ` · Guests ${money(event.guestPrice)} each` : ""}
            </div>
          )}

          <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 8 }}>Will you join us?</div>
          <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
            <RsvpButton value="yes" label="Plan to attend" color={C.green} />
            <RsvpButton value="maybe" label="Unsure" color="#B8860B" />
            <RsvpButton value="no" label="Unable to attend" color={C.danger} />
          </div>

          {/* Pay step: only for paid events, only after "Plan to attend" */}
          {rsvp === "yes" && isPaidEvent && (
            paid ? (
              <div style={{ textAlign: "center", padding: "14px", background: C.greenTint, borderRadius: 10, color: C.greenDark, fontWeight: 700 }}>
                ✅ Payment received — thank you! We look forward to seeing you.
              </div>
            ) : (
              <div style={{ textAlign: "center", padding: "16px", border: `1.5px dashed ${C.green}`, borderRadius: 10 }}>
                <div style={{ fontSize: 14, color: C.text, marginBottom: 4 }}>Amount due: <strong>{money(owed)}</strong></div>
                <div style={{ fontSize: 12, color: C.muted, marginBottom: 12 }}>Secure your spot by paying online.</div>
                <button style={{ ...styles.btn("primary"), padding: "11px 28px" }} onClick={() => setShowPay(true)}>💳 Pay {money(owed)} online</button>
              </div>
            )
          )}
          {rsvp === "yes" && !isPaidEvent && (
            <div style={{ textAlign: "center", padding: "12px", background: C.greenTint, borderRadius: 10, color: C.greenDark, fontWeight: 650 }}>
              🎉 Thanks for confirming — see you there!
            </div>
          )}
          {rsvp === "maybe" && <div style={{ textAlign: "center", fontSize: 13, color: C.textSoft }}>Thanks — we've noted you're unsure. You can come back and confirm anytime.</div>}
          {rsvp === "no" && <div style={{ textAlign: "center", fontSize: 13, color: C.textSoft }}>Sorry you can't make it — thank you for letting us know.</div>}
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <button style={styles.btn("ghost")} onClick={onClose}>Close preview</button>
      </div>

      {showPay && (
        <PayDemoFlow
          title="Pay for Event"
          recipientName={member ? `${member.firstName} ${member.lastName}`.trim() : ""}
          recipientEmail={member ? member.email : ""}
          lineItem={`${event.name}${invitation.guestCount ? ` (incl. ${invitation.guestCount} guest${invitation.guestCount === 1 ? "" : "s"})` : ""}`}
          amount={owed}
          onPaid={() => { if (onPaid) onPaid(); }}
          onClose={() => setShowPay(false)}
        />
      )}
    </Modal>
  );
}

// ── Event Detail / Invitations (the heart of the app) ──────────────────────────
// ── Collapsible section: a card with an expand/collapse header ───────────────
function Section({ title, badge, action, defaultOpen = true, style, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ ...styles.card, ...(style || {}) }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
        <button onClick={() => setOpen(o => !o)} style={{ display: "flex", alignItems: "center", gap: 10, background: "none", border: "none", padding: 0, cursor: "pointer", flex: 1, minWidth: 0, textAlign: "left" }}>
          <span style={{ fontSize: 12, color: C.muted, transition: "transform .15s", transform: open ? "rotate(90deg)" : "none", display: "inline-block", flexShrink: 0 }}>▶</span>
          <span style={{ ...styles.sectionTitle, marginBottom: 0 }}>{title}</span>
          {(badge !== undefined && badge !== null && badge !== "") ? <span style={styles.badge("invited")}>{badge}</span> : null}
        </button>
        {action ? <div style={{ flexShrink: 0 }}>{action}</div> : null}
      </div>
      {open ? <div style={{ marginTop: 14 }}>{children}</div> : null}
    </div>
  );
}

// ── Per-event member survey: build questions, collect + summarize responses ───
const SURVEY_TYPES = [
  { key: "rating", label: "Rating (1–5)" },
  { key: "choice", label: "Multiple choice" },
  { key: "text", label: "Open text" },
];

// Admin / event-manager builder + results summary (lives on the event page).
function SurveyBuilder({ event, evDispatch, responses = [], perms = {} }) {
  const [qs, setQs] = useState(event.survey || []);
  const [dirty, setDirty] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const update = (next) => { setQs(next); setDirty(true); };
  const addQ = (type) => update([...qs, { id: "q" + Math.random().toString(36).slice(2, 8), type, prompt: "", options: type === "choice" ? ["", ""] : [] }]);
  const setQ = (i, patch) => update(qs.map((q, idx) => idx === i ? { ...q, ...patch } : q));
  const removeQ = (i) => update(qs.filter((_, idx) => idx !== i));
  const move = (i, d) => { const j = i + d; if (j < 0 || j >= qs.length) return; const c = [...qs]; [c[i], c[j]] = [c[j], c[i]]; update(c); };
  const save = () => {
    const clean = qs.filter(q => (q.prompt || "").trim()).map(q => ({ id: q.id, type: q.type, prompt: q.prompt.trim(), options: q.type === "choice" ? (q.options || []).map(o => (o || "").trim()).filter(Boolean) : [] }));
    evDispatch && evDispatch({ type: "UPDATE", id: event.id, changes: { survey: clean } });
    setQs(clean); setDirty(false);
  };
  const toggleOpen = () => evDispatch && evDispatch({ type: "UPDATE", id: event.id, changes: { surveyOpen: !event.surveyOpen } });
  const evResponses = responses.filter(r => String(r.eventId) === String(event.id));
  const saved = event.survey || [];

  return (
    <Section title="📋 Member survey" defaultOpen={false} action={saved.length > 0 && <span style={{ fontSize: 12, fontWeight: 700, padding: "2px 8px", borderRadius: 10, background: event.surveyOpen ? C.greenTint : C.bg, color: event.surveyOpen ? C.green : C.muted }}>{event.surveyOpen ? "Open" : "Draft"}</span>}>
      <div style={{ fontSize: 13, color: C.textSoft, marginBottom: 14 }}>Build a short survey attendees fill out after the event. Open it when you're ready to collect feedback.</div>

      {qs.length === 0 && <div style={styles.empty}>No questions yet. Add one below.</div>}
      {qs.map((q, i) => (
        <div key={q.id} style={{ border: `1px solid ${C.border}`, borderRadius: 10, padding: 12, marginBottom: 10 }}>
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8, flexWrap: "wrap" }}>
            <select style={{ ...styles.input, width: "auto" }} value={q.type} onChange={e => setQ(i, { type: e.target.value, options: e.target.value === "choice" ? (q.options && q.options.length ? q.options : ["", ""]) : [] })}>
              {SURVEY_TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
            <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
              <button style={{ ...styles.btn("ghost"), fontSize: 13, padding: "5px 10px" }} disabled={i === 0} onClick={() => move(i, -1)}>↑</button>
              <button style={{ ...styles.btn("ghost"), fontSize: 13, padding: "5px 10px" }} disabled={i === qs.length - 1} onClick={() => move(i, 1)}>↓</button>
              <button style={{ ...styles.btn("danger"), fontSize: 12 }} onClick={() => removeQ(i)}>Remove</button>
            </div>
          </div>
          <input style={styles.input} value={q.prompt || ""} onChange={e => setQ(i, { prompt: e.target.value })} placeholder="Question (e.g. How would you rate the event?)" />
          {q.type === "choice" && <input style={{ ...styles.input, marginTop: 8 }} value={(q.options || []).join(", ")} onChange={e => setQ(i, { options: e.target.value.split(",").map(s => s.trimStart()) })} placeholder="Options, comma-separated (e.g. Excellent, Good, Fair)" />}
        </div>
      ))}

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 4 }}>
        {SURVEY_TYPES.map(t => <button key={t.key} style={{ ...styles.btn("ghost"), fontSize: 13 }} onClick={() => addQ(t.key)}>+ {t.label}</button>)}
      </div>

      <div style={{ display: "flex", gap: 10, marginTop: 16, flexWrap: "wrap", alignItems: "center" }}>
        {dirty && <button style={styles.btn("primary")} onClick={save}>Save survey</button>}
        {saved.length > 0 && !dirty && <button style={styles.btn(event.surveyOpen ? "ghost" : "primary")} onClick={toggleOpen}>{event.surveyOpen ? "Close survey" : "📣 Open to attendees"}</button>}
        {evResponses.length > 0 && <button style={styles.btn("ghost")} onClick={() => setShowResults(s => !s)}>{showResults ? "Hide" : "View"} results ({evResponses.length})</button>}
      </div>
      {dirty && <div style={{ fontSize: 12, color: C.textSoft, marginTop: 8 }}>Save your changes, then open the survey to attendees.</div>}

      {showResults && (
        <div style={{ marginTop: 16, borderTop: `1px solid ${C.border}`, paddingTop: 14 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 10 }}>{evResponses.length} response{evResponses.length === 1 ? "" : "s"}</div>
          {saved.map(q => {
            if (q.type === "rating") {
              const vals = evResponses.map(r => Number(r.answers && r.answers[q.id])).filter(n => n > 0);
              const avg = vals.length ? (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1) : "—";
              return <div key={q.id} style={{ marginBottom: 10 }}><div style={{ fontSize: 13, color: C.text, fontWeight: 600 }}>{q.prompt}</div><div style={{ fontSize: 13, color: C.green, fontWeight: 700 }}>Average {avg} / 5 <span style={{ color: C.muted, fontWeight: 400 }}>({vals.length})</span></div></div>;
            }
            if (q.type === "choice") {
              const tally = {}; evResponses.forEach(r => { const v = r.answers && r.answers[q.id]; if (v) tally[v] = (tally[v] || 0) + 1; });
              const entries = Object.entries(tally);
              return <div key={q.id} style={{ marginBottom: 10 }}><div style={{ fontSize: 13, color: C.text, fontWeight: 600 }}>{q.prompt}</div>{entries.length ? entries.map(([k, v]) => <div key={k} style={{ fontSize: 13, color: C.textSoft }}>{k}: <strong>{v}</strong></div>) : <div style={{ fontSize: 13, color: C.muted }}>—</div>}</div>;
            }
            const texts = evResponses.map(r => r.answers && r.answers[q.id]).filter(t => (t || "").trim());
            return <div key={q.id} style={{ marginBottom: 10 }}><div style={{ fontSize: 13, color: C.text, fontWeight: 600 }}>{q.prompt}</div>{texts.length ? texts.map((t, ix) => <div key={ix} style={{ fontSize: 13, color: C.textSoft, fontStyle: "italic" }}>“{t}”</div>) : <div style={{ fontSize: 13, color: C.muted }}>—</div>}</div>;
          })}
        </div>
      )}
    </Section>
  );
}

// Member-facing survey card (shown in the portal for events with an open survey).
function MemberSurvey({ event, member, responses = [], dispatch }) {
  const existing = responses.find(r => String(r.eventId) === String(event.id) && String(r.memberId) === String(member.id));
  const [answers, setAnswers] = useState(existing ? (existing.answers || {}) : {});
  const [done, setDone] = useState(!!existing);
  const qs = event.survey || [];
  if (!qs.length) return null;
  const setA = (qid, v) => { setAnswers(a => ({ ...a, [qid]: v })); setDone(false); };
  const submit = () => {
    if (!dispatch) return;
    if (existing) dispatch({ type: "UPDATE", id: existing.id, changes: { answers, submittedAt: new Date().toISOString() } });
    else dispatch({ type: "ADD", item: { eventId: event.id, memberId: member.id, answers, submittedAt: new Date().toISOString() } });
    setDone(true);
  };
  return (
    <div style={styles.card}>
      <div style={{ fontSize: 16, fontWeight: 700, color: C.text, marginBottom: 2 }}>How was {event.name}?</div>
      <div style={{ fontSize: 13, color: C.textSoft, marginBottom: 14 }}>Your feedback helps us plan better events. Rate whatever you like — it’s all optional.</div>
      {qs.map(q => (
        <div key={q.id} style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: C.text, marginBottom: 8 }}>{q.prompt}</div>
          {q.type === "rating" ? (
            <div style={{ display: "flex", gap: 6 }}>
              {[1, 2, 3, 4, 5].map(n => <button key={n} onClick={() => setA(q.id, (Number(answers[q.id]) || 0) === n ? 0 : n)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 28, color: (Number(answers[q.id]) || 0) >= n ? "#F5B301" : C.border, padding: 0, lineHeight: 1 }}>★</button>)}
            </div>
          ) : q.type === "choice" ? (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {(q.options || []).map(o => <button key={o} onClick={() => setA(q.id, o)} style={{ padding: "8px 14px", borderRadius: 20, fontSize: 13, fontWeight: 600, cursor: "pointer", border: answers[q.id] === o ? `2px solid ${C.green}` : `1.5px solid ${C.border}`, background: answers[q.id] === o ? C.greenTint : "#FFF", color: answers[q.id] === o ? C.greenDark : C.text }}>{o}</button>)}
            </div>
          ) : (
            <textarea style={{ ...styles.input, minHeight: 60, resize: "vertical" }} value={answers[q.id] || ""} onChange={e => setA(q.id, e.target.value)} placeholder="Your answer…" />
          )}
        </div>
      ))}
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <button style={styles.btn("primary")} onClick={submit}>{existing ? "Update response" : "Submit feedback"}</button>
        {done && <span style={{ fontSize: 13, color: C.green, fontWeight: 650 }}>✓ Thanks — your feedback was recorded.</span>}
      </div>
    </div>
  );
}

function EventDetail({ event, members, invitations, invDispatch, expenses, expDispatch, vendors, venDispatch, messagesDispatch, evDispatch, providers = [], payments = [], tiers = [], reviews = [], reviewsDispatch, surveyResponses = [], paymentsDispatch, onPreviewManager, perms = {}, onBack }) {
  const [showInvite, setShowInvite] = useState(false);
  const [pickIds, setPickIds] = useState([]);
  const [expModal, setExpModal] = useState(null);
  const [expForm, setExpForm] = useState({});
  const [venModal, setVenModal] = useState(null);
  const [venForm, setVenForm] = useState({});
  const [guestModal, setGuestModal] = useState(null); // invitation whose guests we're detailing
  const [payTarget, setPayTarget] = useState(null); // { kind:'vendor'|'member', ... } for demo pay flow
  const [manageAction, setManageAction] = useState(null); // "cancel" | "delete" | null
  const [showNameTags, setShowNameTags] = useState(false);
  const [cancelNote, setCancelNote] = useState("");
  const [invitePreview, setInvitePreview] = useState(null); // invitation being previewed as the member
  const [compose, setCompose] = useState(null);
  const [newItem, setNewItem] = useState("");
  const [newRequired, setNewRequired] = useState(false);

  const eventInvites = invitations.filter(i => String(i.eventId) === String(event.id));
  const eventExpenses = expenses.filter(x => String(x.eventId) === String(event.id));
  const eventVendors = vendors.filter(v => String(v.eventId) === String(event.id));
  const invitedMemberIds = new Set(eventInvites.map(i => String(i.memberId)));
  const inviteeMembers = eventInvites.map(i => members.find(m => String(m.id) === String(i.memberId))).filter(Boolean);
  const contactableInvitees = inviteeMembers.filter(m => !m.noContact);
  const nameTagMembers = (() => { const yes = eventInvites.filter(i => (i.rsvp || "") === "yes").map(i => members.find(m => String(m.id) === String(i.memberId))).filter(Boolean); return yes.length ? yes : inviteeMembers; })();
  const mgmtPayments = payments.filter(pm => String(pm.eventId) === String(event.id));
  const mgmtUnrefunded = mgmtPayments.filter(pm => !pm.refunded && (Number(pm.amount) > 0));
  const mgmtRefundTotal = mgmtUnrefunded.reduce((sum, pm) => sum + (Number(pm.amount) || 0), 0);
  const canDeleteEvent = eventInvites.length === 0 && eventVendors.length === 0 && mgmtUnrefunded.length === 0;
  const startCancellation = () => evDispatch && evDispatch({ type: "UPDATE", id: event.id, changes: { cancelPending: true, cancelChecklist: buildCancelChecklist(event, vendors, payments) } });
  const toggleCancelItem = (key) => {
    const cur = event.cancelChecklist || [];
    const target = cur.find(it => it.key === key);
    const turningOn = target && !target.done;
    const items = cur.map(it => it.key === key ? { ...it, done: !it.done, doneAt: !it.done ? new Date().toISOString() : null } : it);
    if (evDispatch) evDispatch({ type: "UPDATE", id: event.id, changes: { cancelChecklist: items } });
    if (key === "notify" && turningOn && messagesDispatch) messagesDispatch({ type: "ADD", item: { kind: "cancellation", channel: "email", subject: `Cancelled: ${event.name}`, body: `We're sorry to share that ${event.name} has been cancelled.`, audienceLabel: "All invitees", recipientCount: contactableInvitees.length, eventId: event.id } });
  };
  const abortCancellation = () => evDispatch && evDispatch({ type: "UPDATE", id: event.id, changes: { cancelPending: false, cancelChecklist: [] } });
  const finalizeCancellation = () => {
    const items = event.cancelChecklist || [];
    if (!(items.length && items.every(i => i.done))) return;
    if (evDispatch) evDispatch({ type: "UPDATE", id: event.id, changes: { cancelled: true, cancelledAt: new Date().toISOString(), cancelPending: false } });
  };
  const reinstateEvent = () => evDispatch && evDispatch({ type: "UPDATE", id: event.id, changes: { cancelled: false, cancelledAt: null } });
  const refundPayment = (pm) => paymentsDispatch && paymentsDispatch({ type: "UPDATE", id: pm.id, changes: { refunded: true, refundedAt: new Date().toISOString() } });
  const refundAll = () => mgmtUnrefunded.forEach(refundPayment);
  const deleteEvent = () => { if (evDispatch) evDispatch({ type: "DELETE", id: event.id }); if (onBack) onBack(); };
  const announceEvent = () => evDispatch && evDispatch({ type: "UPDATE", id: event.id, changes: { published: true } });
  const unpublishEvent = () => evDispatch && evDispatch({ type: "UPDATE", id: event.id, changes: { published: false } });
  const announceInvitees = () => setCompose({ kind: "announcement", recipients: inviteeMembers, audienceLabel: `${event.name} invitees`, defaultSubject: event.name, defaultBody: `Hi everyone,\n\nA quick note about ${event.name}${event.eventDate ? " on " + fmtDate(event.eventDate) : ""}. We're looking forward to seeing you there!`, eventId: event.id });

  const checklist = event.checklist || [];
  const requiredItems = checklist.filter(c => c.required);
  const requiredRemaining = requiredItems.filter(c => !c.done);
  const allRequiredDone = requiredRemaining.length === 0;
  const planApproved = !!event.planApproved;
  const invitesUnlocked = checklist.length === 0 ? true : (allRequiredDone && planApproved);
  const setChecklist = (next) => evDispatch && evDispatch({ type: "UPDATE", id: event.id, changes: { checklist: next } });
  const toggleItem = (id) => setChecklist(checklist.map(c => c.id === id ? { ...c, done: !c.done } : c));
  const removeItem = (id) => setChecklist(checklist.filter(c => c.id !== id));
  const addItemQuick = () => { const l = newItem.trim(); if (!l) return; setChecklist([...checklist, { id: `c${Date.now()}`, label: l, required: newRequired, due: null, done: false }]); setNewItem(""); setNewRequired(false); };
  const approvePlan = () => evDispatch && evDispatch({ type: "UPDATE", id: event.id, changes: { planApproved: true, planApprovedAt: new Date().toISOString() } });
  const unapprovePlan = () => evDispatch && evDispatch({ type: "UPDATE", id: event.id, changes: { planApproved: false, planApprovedAt: null } });
  const setManager = (id) => evDispatch && evDispatch({ type: "UPDATE", id: event.id, changes: { managerId: id || null } });
  const uninvited = members.filter(m => !invitedMemberIds.has(String(m.id)));

  const fin = eventFinancials(event, invitations, expenses, vendors);

  const memberById = (id) => members.find(m => String(m.id) === String(id));
  const memberName = (id) => { const m = memberById(id); return m ? `${m.firstName || ""} ${m.lastName || ""}`.trim() : "(removed member)"; };

  const inviteList = (ids) => {
    ids.forEach(mid => {
      invDispatch({ type: "ADD", item: { memberId: mid, eventId: event.id, rsvp: "invited", mealChoice: "", invoiced: false, paid: false, attended: false, invitedAt: new Date().toISOString() } });
    });
  };
  const invite = () => { inviteList(pickIds); setPickIds([]); setShowInvite(false); };
  const inviteAll = () => {
    if (uninvited.length === 0) return;
    if (window.confirm(`Invite all ${uninvited.length} member${uninvited.length === 1 ? "" : "s"} who aren't already invited?`)) {
      inviteList(uninvited.map(m => m.id));
    }
  };

  const setField = (inv, changes) => invDispatch({ type: "UPDATE", id: inv.id, changes });

  const emailInvites = () => {
    const emails = eventInvites.map(i => memberById(i.memberId)).filter(m => m && m.email && !m.noContact).map(m => m.email);
    if (emails.length === 0) { alert("No contactable email addresses among invitees."); return; }
    const subject = encodeURIComponent(`You're invited: ${event.name}`);
    const body = encodeURIComponent(`Hello,\n\nYou're invited to ${event.name} on ${fmtDate(event.eventDate)}${event.location ? ` at ${event.location}` : ""}.\n\nPlease let us know if you can attend${event.mealOptions && event.mealOptions.length ? `, and your meal choice (${event.mealOptions.join(", ")})` : ""}.\n\nThank you,\nWilco Data Solutions`);
    window.location.href = `mailto:?bcc=${emails.join(",")}&subject=${subject}&body=${body}`;
  };

  const sendInvoice = (inv) => {
    const m = memberById(inv.memberId);
    setField(inv, { invoiced: true, invoicedAt: new Date().toISOString() });
    if (m && m.email) {
      const subject = encodeURIComponent(`Invoice: ${event.name}`);
      const body = encodeURIComponent(`Hello ${m.firstName || ""},\n\nHere is your invoice for ${event.name}: ${money(event.price)}.\n\nPlease remit at your convenience.\n\nThank you,\nWilco Data Solutions`);
      window.location.href = `mailto:${m.email}?subject=${subject}&body=${body}`;
    }
  };

  // Expense handlers
  const openExp = (x) => { if (x) { setExpForm({ ...x }); setExpModal(x); } else { setExpForm({ category: "Venue", description: "", amount: "" }); setExpModal("add"); } };
  const saveExp = () => {
    const item = { eventId: event.id, category: expForm.category || "Other", description: expForm.description || "", amount: Number(expForm.amount) || 0 };
    if (expModal === "add") expDispatch({ type: "ADD", item }); else expDispatch({ type: "UPDATE", id: expModal.id, changes: item });
    setExpModal(null);
  };

  // Vendor handlers
  const openVen = (v) => { if (v) { setVenForm({ ...v }); setVenModal(v); } else { setVenForm({ name: "", spaceName: "", fee: "", depositPaid: false, depositAmount: "", paid: false }); setVenModal("add"); } };
  const saveVen = () => {
    const item = { eventId: event.id, name: venForm.name || "", spaceName: venForm.spaceName || "", fee: Number(venForm.fee) || 0, depositPaid: !!venForm.depositPaid, depositAmount: Number(venForm.depositAmount) || 0, paid: !!venForm.paid };
    if (venModal === "add") venDispatch({ type: "ADD", item }); else venDispatch({ type: "UPDATE", id: venModal.id, changes: item });
    setVenModal(null);
  };

  return (
    <div>
      <button style={{ ...styles.btn("ghost"), marginBottom: 14 }} onClick={onBack}>← Back to Events</button>
      {event.cancelled && <div style={{ background: "#FEE2E2", color: "#991B1B", padding: "10px 14px", borderRadius: 10, marginBottom: 14, fontSize: 13, fontWeight: 650 }}>⛔ This event has been cancelled{event.cancelledAt ? ` on ${fmtDate(event.cancelledAt)}` : ""}.</div>}
      {!event.published && !event.cancelled && (
        <div style={{ background: "#EEF2FF", border: "1.5px solid #C7D2FE", color: "#3730A3", padding: "12px 14px", borderRadius: 10, marginBottom: 14, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
          <span style={{ fontSize: 13, fontWeight: 650 }}>📝 DRAFT — not visible to members yet. Safe to change or remove.</span>
          {perms.manageEvents && <button style={styles.btn("primary")} onClick={announceEvent}>📣 Announce event</button>}
        </div>
      )}
      {event.published && !event.cancelled && perms.manageEvents && (
        <div style={{ fontSize: 12, color: C.textSoft, marginBottom: 14 }}>✓ Announced — visible to members. <button style={{ background: "none", border: "none", color: C.green, cursor: "pointer", fontWeight: 600, fontSize: 12, textDecoration: "underline", padding: 0 }} onClick={unpublishEvent}>Revert to draft</button></div>
      )}
      {event.cancelPending && !event.cancelled && (perms.manageEvents ? (() => {
        const items = event.cancelChecklist || [];
        const doneCount = items.filter(i => i.done).length;
        const allDone = items.length > 0 && doneCount === items.length;
        return (
          <div style={{ border: "2px solid #DC2626", background: "#FEF2F2", borderRadius: 12, padding: 16, marginBottom: 16 }}>
            <div style={{ fontSize: 14, fontWeight: 800, color: "#B91C1C", letterSpacing: "0.04em", marginBottom: 4 }}>⛔ PENDING CANCELLATION</div>
            <div style={{ fontSize: 13, color: "#7F1D1D", marginBottom: 12 }}>Complete every step before this event can be cancelled. {doneCount}/{items.length} done.</div>
            {items.map(it => (
              <label key={it.key} style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "9px 0", borderTop: "1px solid #FCA5A5", cursor: "pointer", fontSize: 14, color: "#7F1D1D" }}>
                <input type="checkbox" checked={!!it.done} onChange={() => toggleCancelItem(it.key)} style={{ marginTop: 3, flexShrink: 0 }} />
                <span style={{ textDecoration: it.done ? "line-through" : "none", opacity: it.done ? 0.65 : 1 }}>{it.label}</span>
              </label>
            ))}
            <div style={{ display: "flex", gap: 10, marginTop: 14, flexWrap: "wrap" }}>
              <button style={{ ...styles.btn("danger"), opacity: allDone ? 1 : 0.5, cursor: allDone ? "pointer" : "not-allowed" }} disabled={!allDone} onClick={finalizeCancellation}>Cancel this event</button>
              <button style={styles.btn("ghost")} onClick={abortCancellation}>Abort — keep event</button>
            </div>
          </div>
        );
      })() : (
        <div style={{ border: "2px solid #DC2626", background: "#FEF2F2", borderRadius: 12, padding: 14, marginBottom: 16, fontSize: 13, fontWeight: 650, color: "#B91C1C" }}>⛔ This event is pending cancellation.</div>
      ))}
      {perms.manageEvents && (
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 14, padding: "10px 14px", background: C.bg, borderRadius: 10 }}>
          <span style={{ fontSize: 13, color: C.textSoft }}>Event manager:</span>
          <select style={{ ...styles.input, width: "auto", minWidth: 160 }} value={event.managerId || ""} onChange={e => setManager(e.target.value)}>
            <option value="">— unassigned —</option>
            {members.map(m => <option key={m.id} value={m.id}>{`${m.firstName || ""} ${m.lastName || ""}`.trim()}</option>)}
          </select>
          {event.managerId && onPreviewManager && <button style={styles.btn("ghost")} onClick={() => onPreviewManager(event.id)}>👁 Preview as manager</button>}
        </div>
      )}

      <div style={styles.card}>
        <div style={{ fontSize: 22, fontWeight: 800, color: C.green }}>{event.name}</div>
        <div style={{ fontSize: 14, color: C.textSoft, marginTop: 4 }}>{fmtDate(event.eventDate)}{event.location ? ` · ${event.location}` : ""}{Number(event.price) > 0 ? ` · ${money(event.price)} to attend` : " · Free"}</div>
        <div style={{ display: "flex", gap: 20, marginTop: 14, flexWrap: "wrap" }}>
          <Stat label="Invited" value={eventInvites.length} />
          <Stat label="Coming" value={eventInvites.filter(i => i.rsvp === "yes").length} accent />
          <Stat label="Paid" value={fin.paidMembers} />
          <Stat label="Vendors" value={fin.vendorCount} />
        </div>
      </div>

      {/* FINANCIAL SUMMARY */}
      {perms.financials && (
      <Section title="💰 Event Financials" style={{ background: C.greenTint, border: `1.5px solid ${C.green}` }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 14 }}>
          <Stat label="Collected" value={money(fin.collected)} accent />
          <Stat label="Outstanding" value={money(fin.outstanding)} />
          <Stat label="Expenses" value={money(fin.expenseTotal)} />
          <Stat label="Net (so far)" value={money(fin.net)} accent={fin.net >= 0} />
          <Stat label="Projected Net" value={money(fin.projectedNet)} />
        </div>
        <div style={{ fontSize: 12, color: C.textSoft, marginTop: 12 }}>
          <strong>Collected</strong> = paid attendance + vendor payments. <strong>Outstanding</strong> = invited-but-unpaid attendees ({money(fin.memberOutstanding)}) + unpaid vendor balances ({money(fin.vendorOutstanding)}) — your potential incoming revenue. <strong>Projected Net</strong> assumes everything outstanding comes in.
        </div>
      </Section>
      )}

      {/* PRE-FLIGHT CHECKLIST + APPROVAL GATE */}
      {checklist.length > 0 && (
      <div style={styles.card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
          <div style={{ ...styles.sectionTitle, marginBottom: 0 }}>Pre-flight checklist</div>
          <span style={styles.badge(invitesUnlocked ? "paid" : "invited")}>{requiredItems.length - requiredRemaining.length}/{requiredItems.length} required done</span>
        </div>
        {checklist.map(c => {
          const overdue = c.due && !c.done && new Date(c.due + "T23:59:59") < new Date();
          return (
            <div key={c.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 0", borderBottom: `1px solid ${C.border}` }}>
              <input type="checkbox" checked={!!c.done} disabled={!perms.checkIn} onChange={() => toggleItem(c.id)} style={{ width: 17, height: 17, flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: c.done ? C.muted : C.text, textDecoration: c.done ? "line-through" : "none" }}>{c.label}{c.required && <span style={{ color: C.danger, marginLeft: 6, fontSize: 10, fontWeight: 800, letterSpacing: "0.03em" }}>REQUIRED</span>}</div>
                {c.due && <div style={{ fontSize: 12, color: overdue ? C.danger : C.textSoft }}>Due {fmtDate(c.due)}{overdue ? " · overdue" : ""}</div>}
              </div>
              {perms.manageEvents && <button title="Remove item" style={{ background: "none", border: "none", cursor: "pointer", color: C.muted, fontWeight: 700, fontSize: 16, flexShrink: 0 }} onClick={() => removeItem(c.id)}>×</button>}
            </div>
          );
        })}
        {perms.manageEvents && (
          <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap", alignItems: "center" }}>
            <input style={{ ...styles.input, flex: 2, minWidth: 140 }} value={newItem} onChange={e => setNewItem(e.target.value)} placeholder="Add a checklist item…" onKeyDown={e => e.key === "Enter" && addItemQuick()} />
            <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, color: C.text }}><input type="checkbox" checked={newRequired} onChange={e => setNewRequired(e.target.checked)} /> Required</label>
            <button style={styles.btn("ghost")} onClick={addItemQuick}>Add</button>
          </div>
        )}
        <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${C.border}` }}>
          {planApproved ? (
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: C.green }}>✓ Plan approved{event.planApprovedAt ? ` · ${fmtDate(String(event.planApprovedAt).slice(0, 10))}` : ""}</span>
              {perms.manageEvents && <button style={{ ...styles.btn("ghost"), fontSize: 12 }} onClick={unapprovePlan}>Revoke</button>}
            </div>
          ) : perms.manageEvents ? (
            <div>
              <div style={{ fontSize: 13, color: C.textSoft, marginBottom: 8 }}>{allRequiredDone ? "All required items done — approve the plan to unlock invitations." : "Finish the required items, then approve to unlock invitations."}</div>
              <button style={{ ...styles.btn("primary"), opacity: allRequiredDone ? 1 : 0.5 }} disabled={!allRequiredDone} onClick={approvePlan}>Approve plan</button>
            </div>
          ) : (
            <div style={{ fontSize: 13, color: C.textSoft }}>Awaiting admin approval before invitations can be sent.</div>
          )}
        </div>
      </div>
      )}

      {perms.providers && <EventProviders event={event} providers={providers} reviews={reviews} reviewsDispatch={reviewsDispatch} evDispatch={evDispatch} perms={perms} />}
      {(perms.manageEvents || perms.providers) && <SurveyBuilder event={event} evDispatch={evDispatch} responses={surveyResponses} perms={perms} />}

      {checklist.length > 0 && !invitesUnlocked && (
        <div style={{ background: C.dangerLight, border: `1px solid ${C.danger}`, borderRadius: 10, padding: "10px 14px", marginBottom: 12, fontSize: 13, color: C.text }}>
          🔒 <strong>Invitations locked.</strong> {requiredRemaining.length > 0 ? `${requiredRemaining.length} required item${requiredRemaining.length === 1 ? "" : "s"} still open` : "Plan needs admin approval"}{requiredRemaining.length > 0 && !planApproved ? ", plus admin approval" : ""}.
        </div>
      )}

      <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
        <button style={{ ...styles.btn("primary"), opacity: invitesUnlocked ? 1 : 0.5 }} disabled={!invitesUnlocked} onClick={() => setShowInvite(true)}>+ Invite Members</button>
        {uninvited.length > 0 && <button style={{ ...styles.btn("ghost"), opacity: invitesUnlocked ? 1 : 0.5 }} disabled={!invitesUnlocked} onClick={inviteAll}>Invite All ({uninvited.length})</button>}
        {perms.contact && <button style={styles.btn("ghost")} onClick={emailInvites}>✉ Email All Invitees</button>}
        {messagesDispatch && <button style={styles.btn("ghost")} onClick={announceInvitees}>📣 Message Invitees</button>}
      </div>

      {compose && <ComposeMessage {...compose} messagesDispatch={messagesDispatch} onClose={() => setCompose(null)} />}

      {/* INVITATIONS */}
      {(perms.meals || perms.checkIn) && (() => {
        const attendees = eventInvites.filter(i => (i.rsvp || "") === "yes").map(i => memberById(i.memberId)).filter(Boolean);
        if (!attendees.length) return null;
        const withAllergy = attendees.filter(m => (m.allergies || "").trim());
        const dietCounts = {}; attendees.forEach(m => { const d = m.diet && m.diet !== "none" ? m.diet : null; if (d) dietCounts[d] = (dietCounts[d] || 0) + 1; });
        const dietEntries = Object.entries(dietCounts);
        return (
          <Section title="🍽️ Dietary & allergies" defaultOpen={withAllergy.length > 0}>
            <div style={{ fontSize: 12, color: C.textSoft, marginBottom: 12 }}>Across {attendees.length} confirmed attendee{attendees.length === 1 ? "" : "s"}. Hand this to your caterer.</div>
            {dietEntries.length > 0 ? (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
                {dietEntries.map(([k, n]) => <span key={k} style={{ fontSize: 13, fontWeight: 600, color: C.green, background: C.greenTint, borderRadius: 8, padding: "4px 10px" }}>{dietLabel(k)}: {n}</span>)}
              </div>
            ) : <div style={{ fontSize: 13, color: C.textSoft, marginBottom: 10 }}>No dietary preferences flagged among confirmed attendees.</div>}
            {withAllergy.length > 0 && (
              <div style={{ background: "#FEF2F2", border: "1.5px solid #FCA5A5", borderRadius: 10, padding: 12, marginBottom: 10 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#B91C1C", marginBottom: 6 }}>⚠ Allergies — {withAllergy.length}</div>
                {withAllergy.map(m => <div key={m.id} style={{ fontSize: 13, color: "#7F1D1D", marginBottom: 2 }}><strong>{m.firstName} {m.lastName}:</strong> {m.allergies}</div>)}
              </div>
            )}
            <DietDisclaimer />
          </Section>
        );
      })()}

      <Section title="Invitations & RSVPs" badge={eventInvites.length} action={perms.checkIn && <button style={{ ...styles.btn("ghost"), fontSize: 12 }} onClick={() => setShowNameTags(true)}>🏷️ Name tags</button>}>
        <Table
          rows={eventInvites}
          emptyText="No one invited yet. Use '+ Invite Members'."
          columns={[
            { key: "member", header: "Member", primary: true, render: i => { const m = memberById(i.memberId); const al = m && (m.allergies || "").trim(); const dt = m && m.diet && m.diet !== "none"; return <span>{memberName(i.memberId)}{al ? <span title={m.allergies} style={{ marginLeft: 6, fontSize: 11, fontWeight: 700, padding: "1px 7px", borderRadius: 8, background: "#DC2626", color: "#FFF", whiteSpace: "nowrap" }}>⚠ Allergy</span> : null}{dt ? <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 600, padding: "1px 7px", borderRadius: 8, background: C.greenTint, color: C.green }}>{dietLabel(m.diet)}</span> : null}</span>; } },
            { key: "rsvp", header: "RSVP", render: i => (
              <select value={i.rsvp || "invited"} onChange={e => setField(i, { rsvp: e.target.value })} style={{ ...styles.input, padding: "5px 8px", width: "auto" }}>
                <option value="invited">Invited</option>
                <option value="yes">Plan to attend</option>
                <option value="maybe">Unsure</option>
                <option value="no">Unable to attend</option>
              </select>
            ) },
            { key: "meal", header: "Meal", render: i => (
              (event.mealOptions && event.mealOptions.length > 0) ? (
                <select value={i.mealChoice || ""} onChange={e => setField(i, { mealChoice: e.target.value })} style={{ ...styles.input, padding: "5px 8px", width: "auto" }}>
                  <option value="">—</option>
                  {event.mealOptions.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              ) : "—"
            ) },
            ...(event.allowGuests ? [{ key: "guests", header: "Guests", render: i => (
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <input type="number" min="0" value={i.guestCount || 0} onChange={e => setField(i, { guestCount: Math.max(0, parseInt(e.target.value) || 0) })} style={{ ...styles.input, padding: "5px 8px", width: 60 }} />
                {(event.mealOptions && event.mealOptions.length > 0 && (i.guestCount || 0) > 0) && (
                  <button style={{ ...styles.btn("ghost"), fontSize: 11, padding: "4px 8px" }} onClick={() => setGuestModal(i)}>Details</button>
                )}
              </div>
            ) }] : []),
            ...(perms.money ? [{ key: "payment", header: "Payment", render: i => (
              Number(event.price) > 0 ? (
                i.paid ? <span style={styles.badge("paid")}>Paid</span>
                : i.invoiced ? <span style={styles.badge("invoiced")}>Invoiced</span>
                : <span style={styles.badge("invited")}>—</span>
              ) : <span style={{ color: C.muted, fontSize: 13 }}>Free</span>
            ) }] : []),
          ]}
          actions={i => (
            <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
              <button style={{ ...styles.btn("ghost"), fontSize: 12, padding: "5px 9px" }} onClick={() => setInvitePreview(i)}>✉ Invitation</button>
              {perms.money && Number(event.price) > 0 && !i.invoiced && <button style={{ ...styles.btn("ghost"), fontSize: 12, padding: "5px 9px" }} onClick={() => sendInvoice(i)}>Invoice</button>}
              {perms.money && Number(event.price) > 0 && !i.paid && <button style={{ ...styles.btn("ghost"), fontSize: 12, padding: "5px 9px" }} onClick={() => { const m = memberById(i.memberId); const owed = (Number(event.price) || 0) + (event.allowGuests ? (Number(i.guestCount) || 0) * (Number(event.guestPrice) || 0) : 0); setPayTarget({ kind: "member", id: i.id, name: m ? `${m.firstName || ""} ${m.lastName || ""}`.trim() : "", email: m ? m.email : "", lineItem: `${event.name}${i.guestCount ? ` (incl. ${i.guestCount} guest${i.guestCount === 1 ? "" : "s"})` : ""}`, amount: owed }); }}>💳 Invoice &amp; Charge</button>}
              {perms.money && Number(event.price) > 0 && i.invoiced && !i.paid && <button style={{ ...styles.btn("primary"), fontSize: 12, padding: "5px 9px" }} onClick={() => { setField(i, { paid: true, paidAt: new Date().toISOString() }); recordPayment(paymentsDispatch, { amount: (Number(event.price) || 0) + (event.allowGuests ? (Number(i.guestCount) || 0) * (Number(event.guestPrice) || 0) : 0), kind: "attendance", eventId: event.id, memberId: i.memberId, sourceType: "invitation", sourceId: i.id, label: event.name }); }}>Mark Paid</button>}
              {perms.manageEvents && <button style={{ ...styles.btn("danger"), fontSize: 12, padding: "5px 9px" }} onClick={() => invDispatch({ type: "DELETE", id: i.id })}>✕</button>}
            </div>
          )}
        />
      </Section>

      {/* VENDOR SPACE */}
      {perms.vendors && (
      <Section title="🏪 Vendor Space" defaultOpen={false} action={<button style={styles.btn("primary")} onClick={() => openVen(null)}>+ Add Vendor</button>}>
        <Table
          rows={eventVendors}
          emptyText="No vendor spaces sold yet."
          columns={[
            { key: "name", header: "Vendor", primary: true, render: v => v.name || "—" },
            { key: "space", header: "Space", render: v => v.spaceName || "—" },
            { key: "fee", header: "Fee", align: "right", render: v => money(v.fee) },
            { key: "status", header: "Status", render: v => (
              v.paid ? <span style={styles.badge("paid")}>Paid in full</span>
              : v.depositPaid ? <span style={styles.badge("invoiced")}>Deposit paid ({money(v.depositAmount)})</span>
              : <span style={styles.badge("invited")}>Reserved — unpaid</span>
            ) },
          ]}
          actions={v => (
            <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
              {!v.paid && <button style={{ ...styles.btn("ghost"), fontSize: 12, padding: "5px 9px" }} onClick={() => setPayTarget({ kind: "vendor", id: v.id, name: v.name, email: "", lineItem: `Vendor space — ${v.spaceName || event.name}`, amount: Number(v.fee) || 0 })}>💳 Invoice &amp; Charge</button>}
              {!v.paid && <button style={{ ...styles.btn("primary"), fontSize: 12, padding: "5px 9px" }} onClick={() => { venDispatch({ type: "UPDATE", id: v.id, changes: { paid: true } }); recordPayment(paymentsDispatch, { amount: Number(v.fee) || 0, kind: "vendor", eventId: event.id, sourceType: "vendor", sourceId: v.id, label: `Vendor — ${v.name || v.spaceName || event.name}` }); }}>Mark Paid</button>}
              <button style={{ ...styles.btn("ghost"), fontSize: 12, padding: "5px 9px" }} onClick={() => openVen(v)}>Edit</button>
              <button style={{ ...styles.btn("danger"), fontSize: 12, padding: "5px 9px" }} onClick={() => venDispatch({ type: "DELETE", id: v.id })}>✕</button>
            </div>
          )}
        />
      </Section>
      )}

      {/* EXPENSES */}
      {perms.financials && (
      <Section title="🧾 Event Expenses" defaultOpen={false} action={<button style={styles.btn("primary")} onClick={() => openExp(null)}>+ Add Expense</button>}>
        <Table
          rows={eventExpenses}
          emptyText="No expenses logged yet."
          columns={[
            { key: "category", header: "Category", primary: true, render: x => x.category || "Other" },
            { key: "description", header: "Description", render: x => x.description || "—" },
            { key: "amount", header: "Amount", align: "right", render: x => money(x.amount) },
          ]}
          actions={x => (
            <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
              <button style={{ ...styles.btn("ghost"), fontSize: 12, padding: "5px 9px" }} onClick={() => openExp(x)}>Edit</button>
              <button style={{ ...styles.btn("danger"), fontSize: 12, padding: "5px 9px" }} onClick={() => expDispatch({ type: "DELETE", id: x.id })}>✕</button>
            </div>
          )}
        />
        {eventExpenses.length > 0 && <div style={{ textAlign: "right", marginTop: 10, fontWeight: 700, color: C.text }}>Total expenses: {money(fin.expenseTotal)}</div>}
      </Section>
      )}

      {perms.manageEvents && (
      <Section title="⚙️ Manage event" defaultOpen={false}>
        {event.cancelled ? (
          <div style={{ marginBottom: 16, padding: "10px 12px", background: "#FEF3C7", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
            <span style={{ fontSize: 13, color: "#92400E", fontWeight: 650 }}>This event is cancelled{event.cancelledAt ? ` (${fmtDate(event.cancelledAt)})` : ""}.</span>
            <button style={{ ...styles.btn("ghost"), fontSize: 12 }} onClick={reinstateEvent}>Reinstate</button>
          </div>
        ) : event.cancelPending ? (
          <div style={{ marginBottom: 16, padding: "10px 12px", background: "#FEF2F2", borderRadius: 8, border: "1px solid #FCA5A5" }}>
            <span style={{ fontSize: 13, color: "#B91C1C", fontWeight: 650 }}>Cancellation in progress — complete the checklist at the top of this event to finalize.</span>
          </div>
        ) : (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 14, fontWeight: 650, color: C.text, marginBottom: 4 }}>Cancel event</div>
            <div style={{ fontSize: 13, color: C.textSoft, marginBottom: 10 }}>Starts a guided cancellation: an admin completes a short checklist (notify invitees, refund deposits, and so on) before it's finalized.</div>
            <button style={styles.btn("ghost")} onClick={startCancellation}>Start cancellation</button>
          </div>
        )}
        <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 650, color: C.text, marginBottom: 4 }}>Delete event</div>
          <div style={{ fontSize: 13, color: C.textSoft, marginBottom: 10 }}>Permanently removes this event. Available only once it has no invitations, vendors, or unrefunded payments — so nothing owed is lost track of.</div>
          {!canDeleteEvent && (
            <div style={{ background: C.bg, borderRadius: 8, padding: "12px 14px", marginBottom: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 650, color: C.text, marginBottom: 8 }}>Clear these first:</div>
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: C.textSoft }}>
                {eventInvites.length > 0 && <li>{eventInvites.length} invitation{eventInvites.length === 1 ? "" : "s"} — remove in “Invitations &amp; RSVPs” above.</li>}
                {eventVendors.length > 0 && <li>{eventVendors.length} vendor{eventVendors.length === 1 ? "" : "s"} — remove in “Vendor Space” above.</li>}
                {mgmtUnrefunded.length > 0 && <li>{money(mgmtRefundTotal)} across {mgmtUnrefunded.length} payment{mgmtUnrefunded.length === 1 ? "" : "s"} still to refund.</li>}
              </ul>
              {mgmtUnrefunded.length > 0 && (
                <div style={{ marginTop: 12 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: C.text }}>Payments to refund</span>
                    <button style={{ ...styles.btn("ghost"), fontSize: 12 }} onClick={refundAll}>Refund all</button>
                  </div>
                  {mgmtUnrefunded.map(pm => (
                    <div key={pm.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, padding: "7px 0", borderTop: `1px solid ${C.border}` }}>
                      <span style={{ fontSize: 13, color: C.text }}>{pm.label || pm.kind} — {money(pm.amount)}</span>
                      <button style={{ ...styles.btn("ghost"), fontSize: 12 }} onClick={() => refundPayment(pm)}>Mark refunded</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {canDeleteEvent && event.cancelled && <div style={{ fontSize: 12, color: C.green, marginBottom: 10 }}>✓ Nothing outstanding — this event can be safely deleted.</div>}
          <button style={{ ...styles.btn("danger"), opacity: canDeleteEvent ? 1 : 0.5, cursor: canDeleteEvent ? "pointer" : "not-allowed" }} disabled={!canDeleteEvent} onClick={() => setManageAction("delete")}>Delete event</button>
        </div>
      </Section>
      )}

      {manageAction === "delete" && (
        <Modal title="Delete this event?" onClose={() => setManageAction(null)}>
          <div style={{ fontSize: 13, color: C.textSoft, marginBottom: 16 }}>This permanently removes “{event.name}” and can't be undone.</div>
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <button style={styles.btn("ghost")} onClick={() => setManageAction(null)}>Keep event</button>
            <button style={styles.btn("danger")} onClick={deleteEvent}>Delete permanently</button>
          </div>
        </Modal>
      )}

      {showNameTags && <NameTagsModal members={nameTagMembers} tiers={tiers} label="All attendees" onClose={() => setShowNameTags(false)} />}

      {/* Invite picker modal */}
      {showInvite && (
        <Modal title="Invite Members" onClose={() => setShowInvite(false)} wide>
          {uninvited.length === 0 ? <div style={styles.empty}>Everyone is already invited.</div> : (
            <>
              <div style={{ maxHeight: 320, overflowY: "auto", marginBottom: 14 }}>
                {uninvited.map(m => {
                  const on = pickIds.includes(m.id);
                  return (
                    <label key={m.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 4px", borderBottom: `1px solid ${C.border}`, cursor: "pointer" }}>
                      <input type="checkbox" checked={on} onChange={() => setPickIds(p => on ? p.filter(x => x !== m.id) : [...p, m.id])} />
                      <span>{`${m.firstName || ""} ${m.lastName || ""}`.trim()}</span>
                      {m.tags && m.tags.length > 0 && <span style={{ fontSize: 11, color: C.muted }}>· {m.tags.join(", ")}</span>}
                    </label>
                  );
                })}
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <button style={styles.btn("ghost")} onClick={() => setPickIds(uninvited.map(m => m.id))}>Select all</button>
                <button style={styles.btn("primary")} onClick={invite} disabled={pickIds.length === 0}>Invite {pickIds.length || ""}</button>
              </div>
            </>
          )}
        </Modal>
      )}

      {/* Expense modal */}
      {expModal && (
        <Modal title={expModal === "add" ? "Add Expense" : "Edit Expense"} onClose={() => setExpModal(null)}>
          <Field label="Category">
            <select style={styles.input} value={expForm.category || "Venue"} onChange={e => setExpForm({ ...expForm, category: e.target.value })}>
              {EXPENSE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Description"><input style={styles.input} value={expForm.description || ""} onChange={e => setExpForm({ ...expForm, description: e.target.value })} placeholder="e.g. Main hall rental" /></Field>
          <Field label="Amount ($)"><input style={styles.input} type="number" step="any" value={expForm.amount || ""} onChange={e => setExpForm({ ...expForm, amount: e.target.value })} /></Field>
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <button style={styles.btn("ghost")} onClick={() => setExpModal(null)}>Cancel</button>
            <button style={styles.btn("primary")} onClick={saveExp}>Save Expense</button>
          </div>
        </Modal>
      )}

      {/* Vendor modal */}
      {venModal && (
        <Modal title={venModal === "add" ? "Add Vendor" : "Edit Vendor"} onClose={() => setVenModal(null)}>
          <div style={styles.grid2}>
            <Field label="Vendor Name"><input style={styles.input} value={venForm.name || ""} onChange={e => setVenForm({ ...venForm, name: e.target.value })} /></Field>
            <Field label="Space / Booth"><input style={styles.input} value={venForm.spaceName || ""} onChange={e => setVenForm({ ...venForm, spaceName: e.target.value })} placeholder="e.g. Booth 4" /></Field>
          </div>
          <Field label="Total Fee ($)"><input style={styles.input} type="number" step="any" value={venForm.fee || ""} onChange={e => setVenForm({ ...venForm, fee: e.target.value })} /></Field>
          <div style={{ padding: 12, background: C.bg, borderRadius: 8, border: `1px solid ${C.border}`, marginBottom: 14 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, marginBottom: 10, cursor: "pointer" }}>
              <input type="checkbox" checked={!!venForm.depositPaid} onChange={e => setVenForm({ ...venForm, depositPaid: e.target.checked })} />
              Deposit paid to reserve the space
            </label>
            {venForm.depositPaid && (
              <Field label="Deposit Amount ($)"><input style={styles.input} type="number" step="any" value={venForm.depositAmount || ""} onChange={e => setVenForm({ ...venForm, depositAmount: e.target.value })} placeholder="Amount paid to reserve" /></Field>
            )}
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, cursor: "pointer" }}>
              <input type="checkbox" checked={!!venForm.paid} onChange={e => setVenForm({ ...venForm, paid: e.target.checked })} />
              Paid in full
            </label>
          </div>
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <button style={styles.btn("ghost")} onClick={() => setVenModal(null)}>Cancel</button>
            <button style={styles.btn("primary")} onClick={saveVen}>Save Vendor</button>
          </div>
        </Modal>
      )}

      {/* Per-guest details modal (optional names + meals) */}
      {guestModal && (() => {
        const inv = eventInvites.find(x => x.id === guestModal.id) || guestModal;
        const n = Number(inv.guestCount) || 0;
        const details = Array.isArray(inv.guestDetails) ? inv.guestDetails : [];
        const rows = Array.from({ length: n }, (_, idx) => details[idx] || { name: "", meal: "" });
        const setGuest = (idx, key, val) => {
          const next = rows.map((g, j) => j === idx ? { ...g, [key]: val } : g);
          invDispatch({ type: "UPDATE", id: inv.id, changes: { guestDetails: next } });
        };
        return (
          <Modal title={`Guests for ${memberName(inv.memberId)}`} onClose={() => setGuestModal(null)}>
            <p style={{ fontSize: 13, color: C.muted, marginBottom: 14 }}>Optional — add each guest's name and meal choice. You can leave these blank and just use the guest count.</p>
            {rows.map((g, idx) => (
              <div key={idx} style={{ display: "flex", gap: 8, marginBottom: 10, alignItems: "center" }}>
                <span style={{ fontSize: 13, color: C.textSoft, width: 60 }}>Guest {idx + 1}</span>
                <input style={{ ...styles.input, flex: 1 }} value={g.name || ""} onChange={e => setGuest(idx, "name", e.target.value)} placeholder="Name (optional)" />
                {event.mealOptions && event.mealOptions.length > 0 && (
                  <select style={{ ...styles.input, width: 130 }} value={g.meal || ""} onChange={e => setGuest(idx, "meal", e.target.value)}>
                    <option value="">Meal —</option>
                    {event.mealOptions.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                )}
              </div>
            ))}
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 14 }}>
              <button style={styles.btn("primary")} onClick={() => setGuestModal(null)}>Done</button>
            </div>
          </Modal>
        );
      })()}

      {/* Demo invoice & online payment flow (vendors + members) */}
      {payTarget && (
        <PayDemoFlow
          title="Invoice & Online Payment"
          recipientName={payTarget.name}
          recipientEmail={payTarget.email}
          lineItem={payTarget.lineItem}
          amount={payTarget.amount}
          onPaid={() => {
            if (payTarget.kind === "vendor") { venDispatch({ type: "UPDATE", id: payTarget.id, changes: { paid: true } }); recordPayment(paymentsDispatch, { amount: payTarget.amount, kind: "vendor", eventId: event.id, sourceType: "vendor", sourceId: payTarget.id, label: payTarget.lineItem }); }
            else { invDispatch({ type: "UPDATE", id: payTarget.id, changes: { invoiced: true, paid: true, paidAt: new Date().toISOString() } }); recordPayment(paymentsDispatch, { amount: payTarget.amount, kind: "attendance", eventId: event.id, memberId: (invitations.find(x => String(x.id) === String(payTarget.id)) || {}).memberId, sourceType: "invitation", sourceId: payTarget.id, label: payTarget.lineItem }); }
          }}
          onClose={() => setPayTarget(null)}
        />
      )}

      {/* Member invitation preview (demo: respond + pay as the member) */}
      {invitePreview && (() => {
        const inv = eventInvites.find(x => x.id === invitePreview.id) || invitePreview;
        const m = memberById(inv.memberId);
        return (
          <MemberInvitationView
            event={event}
            member={m}
            invitation={inv}
            onRespond={(val) => setField(inv, { rsvp: val })}
            onPaid={() => setField(inv, { invoiced: true, paid: true, paidAt: new Date().toISOString() })}
            onClose={() => setInvitePreview(null)}
          />
        );
      })()}
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div>
      <div style={{ fontSize: 26, fontWeight: 800, color: accent ? C.green : C.text }}>{value}</div>
      <div style={{ fontSize: 12, color: C.textSoft, textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</div>
    </div>
  );
}

// ── Lists / Check-in tab ───────────────────────────────────────────────────────
function Lists({ events, members, invitations, invDispatch, prospects = [], prospectsDispatch, membersDispatch, paymentsDispatch, perms = {} }) {
  const [eventId, setEventId] = useState("");
  const [payTarget, setPayTarget] = useState(null); // invitation being charged at the door
  const selected = events.find(e => String(e.id) === String(eventId));
  const memberById = (id) => members.find(m => String(m.id) === String(id));
  const memberName = (id) => { const m = memberById(id); return m ? `${m.firstName || ""} ${m.lastName || ""}`.trim() : "(removed)"; };

  const evInv = selected ? invitations.filter(i => String(i.eventId) === String(selected.id)) : [];
  const coming = evInv.filter(i => i.rsvp === "yes")
    .sort((a, b) => memberName(a.memberId).localeCompare(memberName(b.memberId)));
  const unpaidComing = coming.filter(i => Number(selected?.price) > 0 && !i.paid);
  const owedForInv = (i) => (Number(selected?.price) || 0) + (selected?.allowGuests ? (Number(i.guestCount) || 0) * (Number(selected?.guestPrice) || 0) : 0);
  const checkedIn = coming.filter(i => i.attended).length;
  const paidActivities = Number(selected?.price) > 0;
  const guestsOn = !!selected?.allowGuests;
  const guestHeads = guestsOn ? coming.reduce((s, i) => s + (Number(i.guestCount) || 0), 0) : 0;
  const totalHeads = coming.length + guestHeads;

  // Meal tally — members' meals + any per-guest meals
  const mealTally = {};
  coming.forEach(i => {
    if (i.mealChoice) mealTally[i.mealChoice] = (mealTally[i.mealChoice] || 0) + 1;
    if (guestsOn && Array.isArray(i.guestDetails)) {
      i.guestDetails.forEach(g => { if (g && g.meal) mealTally[g.meal] = (mealTally[g.meal] || 0) + 1; });
    }
  });

  const toggleAttend = (i) => invDispatch({ type: "UPDATE", id: i.id, changes: { attended: !i.attended } });

  // Growth loop: guests brought by members, grouped by inviter, convertible to members.
  const eventProspects = selected ? prospects.filter(p => String(p.sourceEventId) === String(selected.id)) : [];
  const prospectsByInviter = {};
  eventProspects.forEach(p => { const k = String(p.invitedByMemberId || "unknown"); (prospectsByInviter[k] = prospectsByInviter[k] || []).push(p); });
  const convertProspect = async (p) => {
    const parts = (p.name || "").trim().split(/\s+/);
    const firstName = parts.shift() || (p.name || "Guest");
    const lastName = parts.join(" ");
    const isEmail = (p.contact || "").includes("@");
    const item = { firstName, lastName, email: isEmail ? p.contact : "", phone: isEmail ? "" : (p.contact || ""), status: "active", tags: ["Converted from guest"] };
    const created = membersDispatch ? await membersDispatch({ type: "ADD", item }) : null;
    if (prospectsDispatch) prospectsDispatch({ type: "UPDATE", id: p.id, changes: { status: "joined", convertedMemberId: created ? created.id : null } });
  };

  // Which section to isolate when printing: "list" or "catering".
  const [printMode, setPrintMode] = useState("list");
  const printSection = (mode) => { setPrintMode(mode); setTimeout(() => window.print(), 50); };

  // Total head count per meal (members + guests) for catering.
  const cateringTotal = Object.values(mealTally).reduce((s, n) => s + n, 0);
  const noMealCount = totalHeads - cateringTotal; // coming without a recorded meal choice

  return (
    <div className={`print-mode-${printMode}`}>
      <div className="no-print" style={styles.card}>
        <Field label="Choose an event to generate its attendee list">
          <select style={styles.input} value={eventId} onChange={e => setEventId(e.target.value)}>
            <option value="">— select an event —</option>
            {events.map(e => <option key={e.id} value={e.id}>{e.name} ({fmtDate(e.eventDate)})</option>)}
          </select>
        </Field>
      </div>

      {selected && (
        <>
          <div className="section-list">
          {/* Print header — only shows on the printed page */}
          <div className="print-only print-header">
            <h1 style={{ margin: 0, fontSize: 20 }}>{selected.name} — Attendee List</h1>
            <div style={{ fontSize: 13, color: "#444" }}>{fmtDate(selected.eventDate)}{selected.location ? ` · ${selected.location}` : ""}</div>
          </div>

          {/* CHECK-IN + ATTENDEE LIST */}
          <div style={styles.card}>
            <div className="no-print" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10, marginBottom: 6 }}>
              <div style={{ ...styles.sectionTitle, marginBottom: 0 }}>
                Coming to {selected.name} — {coming.length} member{coming.length === 1 ? "" : "s"}{guestsOn && guestHeads > 0 ? ` + ${guestHeads} guest${guestHeads === 1 ? "" : "s"} = ${totalHeads}` : ""} · <span style={{ color: C.green }}>{checkedIn} checked in</span>
              </div>
              <button style={styles.btn("ghost")} onClick={() => printSection("list")}>🖨 Print List</button>
            </div>
            {Object.keys(mealTally).length > 0 && (
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
                {Object.entries(mealTally).map(([meal, n]) => (
                  <span key={meal} style={styles.badge("yes")}>{meal}: {n}</span>
                ))}
              </div>
            )}

            {coming.length === 0 ? <div style={styles.empty}>No confirmed attendees yet.</div> : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }} className="attendee-table">
                  <thead>
                    <tr style={{ borderBottom: `2px solid ${C.border}` }}>
                      <th style={{ width: 70, textAlign: "center", padding: "10px 12px", color: C.textSoft, fontWeight: 650, fontSize: 12, textTransform: "uppercase" }}>Check-in</th>
                      <th style={{ textAlign: "left", padding: "10px 12px", color: C.textSoft, fontWeight: 650, fontSize: 12, textTransform: "uppercase" }}>Attendee</th>
                      {guestsOn && <th style={{ textAlign: "center", padding: "10px 12px", color: C.textSoft, fontWeight: 650, fontSize: 12, textTransform: "uppercase" }}>Guests</th>}
                      <th style={{ textAlign: "left", padding: "10px 12px", color: C.textSoft, fontWeight: 650, fontSize: 12, textTransform: "uppercase" }}>Meal</th>
                      {paidActivities && perms.paidStatus && <th style={{ textAlign: "left", padding: "10px 12px", color: C.textSoft, fontWeight: 650, fontSize: 12, textTransform: "uppercase" }}>Paid</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {coming.map(i => (
                      <tr key={i.id} style={{ borderBottom: `1px solid ${C.border}`, background: i.attended ? C.greenTint : "transparent" }}>
                        <td style={{ textAlign: "center", padding: "10px 12px" }}>
                          {/* On screen: a tap target. On print: an empty checkbox to tick by hand. */}
                          <input type="checkbox" className="no-print checkin-box" checked={!!i.attended} onChange={() => toggleAttend(i)} style={{ width: 20, height: 20, cursor: "pointer" }} />
                          <span className="print-only print-box">☐</span>
                        </td>
                        <td style={{ padding: "10px 12px", fontWeight: 650 }}>{memberName(i.memberId)}</td>
                        {guestsOn && <td style={{ padding: "10px 12px", textAlign: "center" }}>{Number(i.guestCount) || 0}</td>}
                        <td style={{ padding: "10px 12px" }}>{i.mealChoice || "—"}</td>
                        {paidActivities && perms.paidStatus && <td style={{ padding: "10px 12px" }}>{i.paid ? <span style={styles.badge("paid")}>Paid</span> : <span style={styles.badge("invoiced")}>Unpaid</span>}</td>}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* RSVP'd BUT UNPAID */}
          {paidActivities && perms.collectPayment && (
            <div style={styles.card}>
              <div style={styles.sectionTitle}>⚠️ Coming but not yet paid — {unpaidComing.length}</div>
              {unpaidComing.length === 0 ? (
                <div style={styles.empty}>Everyone coming has paid. 🎉</div>
              ) : (
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }} className="attendee-table">
                  <thead>
                    <tr style={{ borderBottom: `2px solid ${C.border}` }}>
                      <th style={{ textAlign: "left", padding: "10px 12px", color: C.textSoft, fontWeight: 650, fontSize: 12, textTransform: "uppercase" }}>Member</th>
                      <th style={{ textAlign: "left", padding: "10px 12px", color: C.textSoft, fontWeight: 650, fontSize: 12, textTransform: "uppercase" }}>Email</th>
                      <th style={{ textAlign: "right", padding: "10px 12px", color: C.textSoft, fontWeight: 650, fontSize: 12, textTransform: "uppercase" }}>Owes</th>
                      <th className="no-print" style={{ width: 1 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {unpaidComing.map(i => { const m = memberById(i.memberId); return (
                      <tr key={i.id} style={{ borderBottom: `1px solid ${C.border}` }}>
                        <td style={{ padding: "10px 12px", fontWeight: 650 }}>{memberName(i.memberId)}{selected.allowGuests && i.guestCount ? <span style={{ color: C.textSoft, fontWeight: 400 }}> +{i.guestCount}</span> : ""}</td>
                        <td style={{ padding: "10px 12px", color: C.textSoft }}>{m?.email || "—"}</td>
                        <td style={{ padding: "10px 12px", textAlign: "right" }}>{money(owedForInv(i))}</td>
                        <td className="no-print" style={{ padding: "8px 12px", textAlign: "right", whiteSpace: "nowrap" }}>
                          <button style={{ ...styles.btn("primary"), fontSize: 12, padding: "6px 12px" }} onClick={() => setPayTarget(i)}>💳 Collect Payment</button>
                        </td>
                      </tr>
                    ); })}
                  </tbody>
                  {perms.money && (
                  <tfoot>
                    <tr><td colSpan={2} style={{ padding: "10px 12px", fontWeight: 700, textAlign: "right" }}>Total outstanding:</td><td style={{ padding: "10px 12px", fontWeight: 700, textAlign: "right", color: C.green }}>{money(unpaidComing.reduce((s, i) => s + owedForInv(i), 0))}</td><td className="no-print"></td></tr>
                  </tfoot>
                  )}
                </table>
              )}
            </div>
          )}
          </div>{/* end section-list */}

          {/* GUESTS & PROSPECTS — the growth loop */}
          {perms.checkIn && eventProspects.length > 0 && (
            <div className="no-print" style={styles.card}>
              <div style={styles.sectionTitle}>🌱 Guests &amp; Prospects — {eventProspects.length}</div>
              <div style={{ fontSize: 13, color: C.textSoft, marginTop: -8, marginBottom: 14 }}>Guests brought by members. Convert a prospect into a member when they join at the door.</div>
              {Object.entries(prospectsByInviter).map(([mid, list]) => (
                <div key={mid} style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 6 }}>Invited by {memberName(mid)}</div>
                  {list.map(p => (
                    <div key={p.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, padding: "8px 12px", background: C.bg, borderRadius: 8, marginBottom: 6 }}>
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 650, color: C.text }}>{p.name}{p.guestType ? <span style={{ color: C.textSoft, fontWeight: 400 }}> · {p.guestType}</span> : ""}</div>
                        <div style={{ fontSize: 12, color: C.textSoft }}>{perms.contact && p.contact ? p.contact : ""}{p.meal ? `${perms.contact && p.contact ? " · " : ""}${p.meal}` : ""}</div>
                      </div>
                      {p.status === "joined"
                        ? <span style={styles.badge("paid")}>Joined ✓</span>
                        : <button style={{ ...styles.btn("primary"), fontSize: 12, padding: "6px 12px", whiteSpace: "nowrap" }} onClick={() => convertProspect(p)}>+ Convert to member</button>}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}

          {/* CATERING SUMMARY (separate printable view) */}
          {(event => {
            const ev = selected;
            const hasMeals = ev.mealOptions && ev.mealOptions.length > 0;
            return (
              <div className="section-catering">
                <div className="print-only print-header">
                  <h1 style={{ margin: 0, fontSize: 20 }}>{ev.name} — Catering Summary</h1>
                  <div style={{ fontSize: 13, color: "#444" }}>{fmtDate(ev.eventDate)}{ev.location ? ` · ${ev.location}` : ""}</div>
                </div>
                <div style={styles.card}>
                  <div className="no-print" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10, marginBottom: 14 }}>
                    <div style={{ ...styles.sectionTitle, marginBottom: 0 }}>🍽️ Catering Summary</div>
                    <button style={styles.btn("ghost")} onClick={() => printSection("catering")}>🖨 Print Catering Summary</button>
                  </div>
                  <div style={{ fontSize: 15, marginBottom: 14 }}>
                    Total expected: <strong>{totalHeads}</strong> {totalHeads === 1 ? "person" : "people"}
                    {guestsOn && guestHeads > 0 ? ` (${coming.length} member${coming.length === 1 ? "" : "s"} + ${guestHeads} guest${guestHeads === 1 ? "" : "s"})` : ""}
                  </div>
                  {hasMeals ? (
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14, maxWidth: 420 }} className="attendee-table">
                      <thead>
                        <tr style={{ borderBottom: `2px solid ${C.border}` }}>
                          <th style={{ textAlign: "left", padding: "10px 12px", color: C.textSoft, fontWeight: 650, fontSize: 12, textTransform: "uppercase" }}>Meal</th>
                          <th style={{ textAlign: "right", padding: "10px 12px", color: C.textSoft, fontWeight: 650, fontSize: 12, textTransform: "uppercase" }}>Count</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ev.mealOptions.map(meal => (
                          <tr key={meal} style={{ borderBottom: `1px solid ${C.border}` }}>
                            <td style={{ padding: "10px 12px", fontWeight: 650 }}>{meal}</td>
                            <td style={{ padding: "10px 12px", textAlign: "right", fontWeight: 700 }}>{mealTally[meal] || 0}</td>
                          </tr>
                        ))}
                        {noMealCount > 0 && (
                          <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                            <td style={{ padding: "10px 12px", color: C.textSoft, fontStyle: "italic" }}>No meal selected</td>
                            <td style={{ padding: "10px 12px", textAlign: "right", color: C.textSoft }}>{noMealCount}</td>
                          </tr>
                        )}
                      </tbody>
                      <tfoot>
                        <tr style={{ borderTop: `2px solid ${C.border}` }}>
                          <td style={{ padding: "10px 12px", fontWeight: 750 }}>Total meals</td>
                          <td style={{ padding: "10px 12px", textAlign: "right", fontWeight: 750, color: C.green }}>{totalHeads}</td>
                        </tr>
                      </tfoot>
                    </table>
                  ) : (
                    <div style={styles.empty}>This event has no meal options set. Total headcount is {totalHeads}.</div>
                  )}
                </div>
              </div>
            );
          })()}
        </>
      )}

      {/* Collect payment at the door (demo pay flow) */}
      {payTarget && selected && (() => {
        const m = memberById(payTarget.memberId);
        return (
          <PayDemoFlow
            title="Collect Payment"
            recipientName={memberName(payTarget.memberId)}
            recipientEmail={m ? m.email : ""}
            lineItem={`${selected.name}${payTarget.guestCount ? ` (incl. ${payTarget.guestCount} guest${payTarget.guestCount === 1 ? "" : "s"})` : ""}`}
            amount={owedForInv(payTarget)}
            onPaid={() => { invDispatch({ type: "UPDATE", id: payTarget.id, changes: { invoiced: true, paid: true, paidAt: new Date().toISOString() } }); recordPayment(paymentsDispatch, { amount: owedForInv(payTarget), kind: "attendance", eventId: selected.id, memberId: payTarget.memberId, sourceType: "invitation", sourceId: payTarget.id, label: selected.name }); }}
            onClose={() => setPayTarget(null)}
          />
        );
      })()}
    </div>
  );
}
// ── Login ──────────────────────────────────────────────────────────────────────
function LoginGate() {
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const GUEST_EMAIL = "guest@wilcodatasolutions.com";
  const GUEST_PASSWORD = "WilcoDemo2026";

  const submit = async () => {
    setError(""); setBusy(true);
    const { error } = await supabase.auth.signInWithPassword({ email: email.trim(), password: pw });
    setBusy(false);
    if (error) { setError(error.message || "Sign in failed."); setPw(""); }
  };
  const guest = async () => {
    setError(""); setBusy(true);
    const { error } = await supabase.auth.signInWithPassword({ email: GUEST_EMAIL, password: GUEST_PASSWORD });
    setBusy(false);
    if (error) setError("Guest demo is temporarily unavailable. Please try again shortly.");
  };

  return (
    <div style={{ ...styles.app, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 16, padding: 36, maxWidth: 400, width: "100%", boxShadow: "0 20px 60px rgba(35,49,66,0.15)" }}>
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <img src="/logo.svg" alt="Wilco Data Solutions" style={{ width: "100%", maxWidth: 320, marginBottom: 8 }} />
          <div style={{ fontSize: 15, fontWeight: 700, color: C.green }}>Membership &amp; Events</div>
          <div style={{ fontSize: 13, color: C.muted, marginTop: 4 }}>Sign in to continue</div>
        </div>
        <button style={{ ...styles.btn("primary"), width: "100%", padding: 13, fontSize: 15, marginBottom: 8, opacity: busy ? 0.6 : 1 }} disabled={busy} onClick={guest}>👋 Try as guest</button>
        <div style={{ fontSize: 12, color: C.textSoft, textAlign: "center", marginBottom: 18 }}>Explore the full demo — no sign-up required.</div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "4px 0 16px" }}>
          <div style={{ flex: 1, height: 1, background: C.border }} /><span style={{ fontSize: 11, color: C.textSoft }}>or sign in</span><div style={{ flex: 1, height: 1, background: C.border }} />
        </div>
        <Field label="Email"><input style={styles.input} type="email" value={email} onChange={e => { setEmail(e.target.value); setError(""); }} onKeyDown={e => e.key === "Enter" && submit()} /></Field>
        <Field label="Password"><input style={styles.input} type="password" value={pw} onChange={e => { setPw(e.target.value); setError(""); }} onKeyDown={e => e.key === "Enter" && submit()} /></Field>
        {error && <div style={{ fontSize: 13, color: C.danger, marginBottom: 12 }}>{error}</div>}
        <button style={{ ...styles.btn("ghost"), width: "100%", padding: 12, fontSize: 15, opacity: busy ? 0.6 : 1 }} disabled={busy} onClick={submit}>{busy ? "Signing in…" : "Sign In"}</button>
        <div style={{ textAlign: "center", marginTop: 18 }}>
          <a href="https://wilcodatasolutions.com" style={{ fontSize: 13, color: C.textSoft, textDecoration: "none" }}>← Back to wilcodatasolutions.com</a>
        </div>
      </div>
    </div>
  );
}
// ── Revenue over time: reads the payments ledger, slices by period ───────────
const REV_KINDS = [
  { key: "dues", label: "Dues", color: "#2E9E5B" },
  { key: "attendance", label: "Event tickets", color: "#3B82C4" },
  { key: "guest", label: "Guest fees", color: "#16A0A0" },
  { key: "vendor", label: "Vendor fees", color: "#D08A1E" },
  { key: "other", label: "Other", color: "#8A8F98" },
];
function RevenueOverTime({ payments = [] }) {
  const [period, setPeriod] = useState("month");
  const now = new Date();
  const buckets = [];
  if (period === "month") {
    for (let i = 11; i >= 0; i--) { const d = new Date(now.getFullYear(), now.getMonth() - i, 1); buckets.push({ key: `m${d.getFullYear()}-${d.getMonth()}`, label: d.toLocaleString("en-US", { month: "short" }), y: d.getFullYear(), m: d.getMonth(), q: null }); }
  } else if (period === "quarter") {
    for (let i = 7; i >= 0; i--) { const d = new Date(now.getFullYear(), now.getMonth() - i * 3, 1); const q = Math.floor(d.getMonth() / 3); buckets.push({ key: `q${d.getFullYear()}-${q}`, label: `Q${q + 1} '${String(d.getFullYear()).slice(2)}`, y: d.getFullYear(), q, m: null }); }
  } else {
    for (let i = 3; i >= 0; i--) { const y = now.getFullYear() - i; buckets.push({ key: `y${y}`, label: `${y}`, y, q: null, m: null }); }
  }
  const inBucket = (dt, b) => {
    if (period === "month") return dt.getFullYear() === b.y && dt.getMonth() === b.m;
    if (period === "quarter") return dt.getFullYear() === b.y && Math.floor(dt.getMonth() / 3) === b.q;
    return dt.getFullYear() === b.y;
  };
  buckets.forEach(b => { b.byKind = {}; b.total = 0; });
  payments.forEach(p => {
    if (p.refunded) return;
    const dt = p.paidAt ? new Date(p.paidAt) : null; if (!dt || isNaN(dt)) return;
    const b = buckets.find(x => inBucket(dt, x)); if (!b) return;
    const k = p.kind || "other"; const amt = Number(p.amount) || 0;
    b.byKind[k] = (b.byKind[k] || 0) + amt; b.total += amt;
  });
  const maxTotal = Math.max(1, ...buckets.map(b => b.total));
  const rangeTotal = buckets.reduce((s, b) => s + b.total, 0);
  const rangeByKind = {};
  buckets.forEach(b => Object.entries(b.byKind).forEach(([k, v]) => { rangeByKind[k] = (rangeByKind[k] || 0) + v; }));
  const H = 160;

  return (
    <div style={styles.card}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10, marginBottom: 4 }}>
        <div style={{ ...styles.sectionTitle, marginBottom: 0 }}>Revenue over time</div>
        <div style={{ display: "flex", gap: 6 }}>
          {["month", "quarter", "year"].map(pk => (
            <button key={pk} onClick={() => setPeriod(pk)} style={{ ...styles.btn(period === pk ? "primary" : "ghost"), fontSize: 12, padding: "6px 12px", textTransform: "capitalize" }}>{pk}</button>
          ))}
        </div>
      </div>
      <div style={{ fontSize: 13, color: C.textSoft, marginBottom: 18 }}>{money(rangeTotal)} collected across this range — dues and events combined.</div>

      <div style={{ display: "flex", alignItems: "flex-end", gap: 8, height: H }}>
        {buckets.map(b => {
          const barH = b.total > 0 ? Math.max(4, Math.round((b.total / maxTotal) * (H - 26))) : 2;
          return (
            <div key={b.key} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 6, height: "100%", justifyContent: "flex-end", minWidth: 0 }}>
              <div style={{ fontSize: 10, color: C.textSoft, fontWeight: 650, whiteSpace: "nowrap" }}>{b.total > 0 ? `$${Math.round(b.total)}` : ""}</div>
              <div style={{ width: "78%", maxWidth: 40, height: barH, display: "flex", flexDirection: "column-reverse", borderRadius: "4px 4px 0 0", overflow: "hidden", background: b.total > 0 ? "transparent" : C.border }}>
                {REV_KINDS.map(k => { const v = b.byKind[k.key] || 0; if (!v || b.total <= 0) return null; return <div key={k.key} style={{ height: Math.max(1, Math.round((v / b.total) * barH)), background: k.color }} />; })}
              </div>
              <div style={{ fontSize: 10, color: C.muted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "100%" }}>{b.label}</div>
            </div>
          );
        })}
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 14, marginTop: 18, borderTop: `1px solid ${C.border}`, paddingTop: 14 }}>
        {REV_KINDS.filter(k => rangeByKind[k.key]).map(k => (
          <div key={k.key} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: C.text }}>
            <span style={{ width: 10, height: 10, borderRadius: 2, background: k.color, display: "inline-block" }} />
            <span>{k.label} — <strong>{money(rangeByKind[k.key])}</strong></span>
          </div>
        ))}
        {rangeTotal === 0 && <div style={styles.empty}>No payments recorded yet. Collected payments will appear here over time.</div>}
      </div>
    </div>
  );
}

// ── Financials overview tab ────────────────────────────────────────────────────
function Financials({ events, invitations, expenses, vendors, payments = [] }) {
  const rows = events.map(ev => ({ ev, fin: eventFinancials(ev, invitations, expenses, vendors) }));
  const totals = rows.reduce((t, { fin }) => ({
    collected: t.collected + fin.collected,
    outstanding: t.outstanding + fin.outstanding,
    expenses: t.expenses + fin.expenseTotal,
    net: t.net + fin.net,
  }), { collected: 0, outstanding: 0, expenses: 0, net: 0 });

  return (
    <div>
      <div style={{ ...styles.card, background: C.slate, color: "#FFF" }}>
        <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 14 }}>All Events — Financial Overview</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 14 }}>
          <div><div style={{ fontSize: 26, fontWeight: 800 }}>{money(totals.collected)}</div><div style={{ fontSize: 12, opacity: 0.8, textTransform: "uppercase", letterSpacing: "0.05em" }}>Collected</div></div>
          <div><div style={{ fontSize: 26, fontWeight: 800 }}>{money(totals.outstanding)}</div><div style={{ fontSize: 12, opacity: 0.8, textTransform: "uppercase", letterSpacing: "0.05em" }}>Outstanding</div></div>
          <div><div style={{ fontSize: 26, fontWeight: 800 }}>{money(totals.expenses)}</div><div style={{ fontSize: 12, opacity: 0.8, textTransform: "uppercase", letterSpacing: "0.05em" }}>Expenses</div></div>
          <div><div style={{ fontSize: 26, fontWeight: 800, color: totals.net >= 0 ? "#9FE0AC" : "#FFB4AE" }}>{money(totals.net)}</div><div style={{ fontSize: 12, opacity: 0.8, textTransform: "uppercase", letterSpacing: "0.05em" }}>Net</div></div>
        </div>
      </div>

      <RevenueOverTime payments={payments} />

      <div style={styles.card}>
        <div style={styles.sectionTitle}>By Event</div>
        <Table
          rows={rows.map(r => ({ id: r.ev.id, ...r }))}
          emptyText="No events yet."
          columns={[
            { key: "name", header: "Event", primary: true, render: r => r.ev.name },
            { key: "date", header: "Date", render: r => fmtDate(r.ev.eventDate) },
            { key: "collected", header: "Collected", align: "right", render: r => money(r.fin.collected) },
            { key: "outstanding", header: "Outstanding", align: "right", render: r => <span style={{ color: C.textSoft }}>{money(r.fin.outstanding)}</span> },
            { key: "expenses", header: "Expenses", align: "right", render: r => money(r.fin.expenseTotal) },
            { key: "net", header: "Net", align: "right", render: r => <span style={{ fontWeight: 700, color: r.fin.net >= 0 ? C.green : C.danger }}>{money(r.fin.net)}</span> },
            { key: "projected", header: "Projected", align: "right", render: r => <span style={{ color: C.textSoft }}>{money(r.fin.projectedNet)}</span> },
          ]}
        />
        <div style={{ fontSize: 12, color: C.textSoft, marginTop: 12 }}>
          "Outstanding" is potential incoming revenue — invited attendees who haven't paid plus unpaid vendor balances. "Projected" net assumes it all comes in.
        </div>
      </div>
    </div>
  );
}

// ── Overview / Dashboard tab ───────────────────────────────────────────────────
function Dashboard({ events, members, invitations, expenses, vendors, tiers = [], layout = [], layoutDispatch, perms = {}, onOpenEvent, onGoToEvents }) {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const parseDay = (d) => (d ? new Date(d + (String(d).length === 10 ? "T00:00:00" : "")) : null);

  const upcoming = events
    .filter(e => { const d = parseDay(e.eventDate); return d && d >= today; })
    .sort((a, b) => parseDay(a.eventDate) - parseDay(b.eventDate));
  const nextEvent = upcoming[0] || null;

  const snapshot = (ev) => {
    const evInv = invitations.filter(i => String(i.eventId) === String(ev.id));
    return {
      invited: evInv.length,
      coming: evInv.filter(i => i.rsvp === "yes").length,
      paid: evInv.filter(i => i.paid).length,
    };
  };

  // All-events financial totals (admin only) — mirrors the Financials tab.
  const fin = perms.financials
    ? events.reduce((t, ev) => {
        const f = eventFinancials(ev, invitations, expenses, vendors);
        return { collected: t.collected + f.collected, outstanding: t.outstanding + f.outstanding, net: t.net + f.net };
      }, { collected: 0, outstanding: 0, net: 0 })
    : null;

  // ── Membership analytics (derived from members + tiers; no schema needed) ──
  const soon = new Date(today); soon.setDate(soon.getDate() + 30);
  let activeCount = 0, dueCount = 0, overdueCount = 0, lapsedCount = 0;
  members.forEach(m => {
    const st = m.status || "active";
    if (st === "lapsed") { lapsedCount++; return; }
    const r = parseDay(m.renewsOn);
    if (r && r < today) overdueCount++;
    else if (r && r <= soon) dueCount++;
    else activeCount++;
  });
  const annualDues = (m) => {
    const t = tiers.find(x => String(x.id) === String(m.tierId));
    if (!t) return 0;
    const pr = Number(t.price) || 0;
    return t.period === "month" ? pr * 12 : t.period === "once" ? 0 : pr;
  };
  const duesRunRate = members.filter(m => (m.status || "active") !== "lapsed").reduce((sum, m) => sum + annualDues(m), 0);
  const tierRows = [...tiers].sort((a, b) => (Number(a.sort) || 0) - (Number(b.sort) || 0)).map(t => ({ label: t.label, count: members.filter(m => String(m.tierId) === String(t.id)).length }));
  const noTierCount = members.filter(m => !m.tierId).length;
  if (noTierCount > 0) tierRows.push({ label: "No tier", count: noTierCount });
  const tierMax = Math.max(1, ...tierRows.map(r => r.count));
  const growthMonths = [];
  for (let gi = 5; gi >= 0; gi--) { const d = new Date(today.getFullYear(), today.getMonth() - gi, 1); growthMonths.push({ y: d.getFullYear(), mo: d.getMonth(), label: d.toLocaleString("en-US", { month: "short" }), count: 0 }); }
  members.forEach(m => { const j = parseDay(m.joinedOn); if (!j) return; const gm = growthMonths.find(x => x.y === j.getFullYear() && x.mo === j.getMonth()); if (gm) gm.count++; });
  const growthMax = Math.max(1, ...growthMonths.map(x => x.count));

  const tile = (label, value, accent) => (
    <div style={{ ...styles.card, marginBottom: 0 }}>
      <div style={{ fontSize: 28, fontWeight: 800, color: accent ? C.green : C.text }}>{value}</div>
      <div style={{ fontSize: 12, color: C.textSoft, textTransform: "uppercase", letterSpacing: "0.05em", marginTop: 2 }}>{label}</div>
    </div>
  );

  const [dashEditing, setDashEditing] = useState(false);

  const WIDGETS = [
    { key: "headline", label: "Headline stats", adminOnly: false, node: (
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12, marginBottom: 16 }}>
        {tile("Members", members.length)}
        {tile("Upcoming events", upcoming.length, true)}
        {tile("Total events", events.length)}
      </div>
    ) },
    { key: "membership", label: "Membership health", adminOnly: false, node: (
      <div style={styles.card}>
        <div style={styles.sectionTitle}>Membership health</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))", gap: 12, marginBottom: 18 }}>
          <Stat label="Active" value={activeCount} accent />
          <Stat label="Due soon" value={dueCount} />
          <Stat label="Overdue" value={overdueCount} />
          <Stat label="Lapsed" value={lapsedCount} />
          {perms.financials && <Stat label="Annual dues" value={money(duesRunRate)} />}
        </div>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 10 }}>Members by tier</div>
        {tierRows.length === 0 ? <div style={styles.empty}>No tiers yet.</div> : tierRows.map((r, i) => (
          <div key={i} style={{ marginBottom: 10 }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 4 }}><span style={{ color: C.text }}>{r.label}</span><span style={{ color: C.textSoft }}>{r.count}</span></div>
            <div style={{ height: 8, background: C.bg, borderRadius: 4, overflow: "hidden" }}><div style={{ width: `${Math.round((r.count / tierMax) * 100)}%`, height: "100%", background: C.green }} /></div>
          </div>
        ))}
        <div style={{ fontSize: 13, fontWeight: 700, color: C.text, margin: "20px 0 12px" }}>New members — last 6 months</div>
        <div style={{ display: "flex", alignItems: "flex-end", gap: 10, height: 130 }}>
          {growthMonths.map((mo, i) => (
            <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 6, height: "100%", justifyContent: "flex-end" }}>
              <div style={{ fontSize: 12, color: C.textSoft, fontWeight: 650 }}>{mo.count || ""}</div>
              <div style={{ width: "72%", maxWidth: 46, background: mo.count ? C.green : C.border, borderRadius: "5px 5px 0 0", height: `${mo.count ? Math.max(6, Math.round((mo.count / growthMax) * 96)) : 3}px` }} />
              <div style={{ fontSize: 11, color: C.muted }}>{mo.label}</div>
            </div>
          ))}
        </div>
      </div>
    ) },
    { key: "financials", label: "Financial summary", adminOnly: true, node: (perms.financials && fin ? (
      <div style={{ ...styles.card, background: C.slate, color: "#FFF" }}>
        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 14, opacity: 0.92 }}>All Events — Financial Summary</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 14 }}>
          <div><div style={{ fontSize: 26, fontWeight: 800 }}>{money(fin.collected)}</div><div style={{ fontSize: 12, opacity: 0.8, textTransform: "uppercase", letterSpacing: "0.05em" }}>Collected</div></div>
          <div><div style={{ fontSize: 26, fontWeight: 800 }}>{money(fin.outstanding)}</div><div style={{ fontSize: 12, opacity: 0.8, textTransform: "uppercase", letterSpacing: "0.05em" }}>Outstanding</div></div>
          <div><div style={{ fontSize: 26, fontWeight: 800, color: fin.net >= 0 ? "#9FE0AC" : "#FFB4AE" }}>{money(fin.net)}</div><div style={{ fontSize: 12, opacity: 0.8, textTransform: "uppercase", letterSpacing: "0.05em" }}>Net</div></div>
        </div>
        <div style={{ fontSize: 12, opacity: 0.7, marginTop: 12 }}>Net = collected − expenses. The Financials tab has the per-event breakdown.</div>
      </div>
    ) : null) },
    { key: "nextEvent", label: "Next event", adminOnly: false, node: (
      <div style={styles.card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
          <div style={styles.sectionTitle}>Next event</div>
          <button style={{ ...styles.btn("ghost"), fontSize: 13, padding: "6px 12px" }} onClick={onGoToEvents}>View all events</button>
        </div>
        {nextEvent ? (() => {
          const s = snapshot(nextEvent);
          return (
            <div onClick={() => onOpenEvent(nextEvent.id)} style={{ cursor: "pointer", border: `1px solid ${C.border}`, borderRadius: 12, padding: 16 }}>
              <div style={{ fontSize: 17, fontWeight: 700, color: C.text }}>{nextEvent.name}</div>
              <div style={{ fontSize: 13, color: C.textSoft, marginBottom: 14 }}>{fmtDate(nextEvent.eventDate)}</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(90px, 1fr))", gap: 14 }}>
                <Stat label="Invited" value={s.invited} />
                <Stat label="Coming" value={s.coming} accent />
                {perms.paidStatus && <Stat label="Paid" value={s.paid} />}
              </div>
            </div>
          );
        })() : (
          <div style={styles.empty}>No upcoming events. <span style={{ color: C.green, cursor: "pointer", fontWeight: 600 }} onClick={onGoToEvents}>Create one →</span></div>
        )}
      </div>
    ) },
    { key: "upcoming", label: "Upcoming events", adminOnly: false, node: (upcoming.length > 1 ? (
      <div style={styles.card}>
        <div style={styles.sectionTitle}>Upcoming</div>
        {upcoming.slice(1, 5).map(ev => {
          const s = snapshot(ev);
          return (
            <div key={ev.id} onClick={() => onOpenEvent(ev.id)} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, padding: "11px 0", borderBottom: `1px solid ${C.border}`, cursor: "pointer" }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 600, color: C.text }}>{ev.name}</div>
                <div style={{ fontSize: 12, color: C.textSoft }}>{fmtDate(ev.eventDate)}</div>
              </div>
              <div style={{ fontSize: 13, color: C.textSoft, whiteSpace: "nowrap" }}>{s.coming} coming · {s.invited} invited</div>
            </div>
          );
        })}
      </div>
    ) : null) },
  ];

  const layoutFor = (key) => layout.find(l => String(l.widgetKey) === key);
  const ordered = WIDGETS
    .filter(w => !w.adminOnly || perms.financials)
    .map((w, i) => { const cfg = layoutFor(w.key); return { ...w, visible: cfg ? cfg.visible !== false : true, sort: cfg ? (Number(cfg.sort) || 0) : i, _id: cfg ? cfg.id : null }; })
    .sort((a, b) => a.sort - b.sort);
  const ensureRows = () => { if (!layoutDispatch) return; ordered.forEach((w, i) => { if (!w._id) layoutDispatch({ type: "ADD", item: { widgetKey: w.key, visible: w.visible, sort: i } }); }); };
  const toggleVis = (w) => { if (!layoutDispatch) return; if (w._id) layoutDispatch({ type: "UPDATE", id: w._id, changes: { visible: !w.visible } }); else layoutDispatch({ type: "ADD", item: { widgetKey: w.key, visible: !w.visible, sort: w.sort } }); };
  const moveW = (idx, dir) => { if (!layoutDispatch) return; const j = idx + dir; if (j < 0 || j >= ordered.length) return; const a = ordered[idx], b = ordered[j]; const put = (w, sort) => w._id ? layoutDispatch({ type: "UPDATE", id: w._id, changes: { sort } }) : layoutDispatch({ type: "ADD", item: { widgetKey: w.key, visible: w.visible, sort } }); put(a, b.sort); put(b, a.sort); };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 16 }}>
        <div style={{ ...styles.sectionTitle, marginBottom: 0 }}>Overview</div>
        {perms.manageEvents && layoutDispatch && (
          <button style={styles.btn(dashEditing ? "primary" : "ghost")} onClick={() => { if (!dashEditing) ensureRows(); setDashEditing(e => !e); }}>{dashEditing ? "✓ Done" : "⚙️ Edit dashboard"}</button>
        )}
      </div>

      {dashEditing && <div style={{ fontSize: 13, color: C.textSoft, marginBottom: 14, background: C.bg, borderRadius: 8, padding: "10px 12px" }}>Show or hide widgets and reorder them. This layout is shared across the demo — per-user dashboards come with individual logins.</div>}

      {ordered.map((w, i) => {
        if (!dashEditing && (!w.visible || !w.node)) return null;
        return (
          <div key={w.key} style={dashEditing ? { border: `1px dashed ${C.border}`, borderRadius: 12, padding: 10, marginBottom: 12, opacity: w.visible ? 1 : 0.55 } : undefined}>
            {dashEditing && (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 10 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{w.label}{w.visible ? "" : " · hidden"}</span>
                <div style={{ display: "flex", gap: 6 }}>
                  <button style={{ ...styles.btn("ghost"), fontSize: 13, padding: "5px 10px", opacity: i === 0 ? 0.4 : 1 }} disabled={i === 0} onClick={() => moveW(i, -1)}>↑</button>
                  <button style={{ ...styles.btn("ghost"), fontSize: 13, padding: "5px 10px", opacity: i === ordered.length - 1 ? 0.4 : 1 }} disabled={i === ordered.length - 1} onClick={() => moveW(i, 1)}>↓</button>
                  <button style={{ ...styles.btn(w.visible ? "ghost" : "primary"), fontSize: 12, padding: "5px 10px" }} onClick={() => toggleVis(w)}>{w.visible ? "Hide" : "Show"}</button>
                </div>
              </div>
            )}
            {w.node}
          </div>
        );
      })}
    </div>
  );
}

// ── Shell ────────────────────────────────────────────────────────────────────
// ── Team & access: a demo of multi-user auth (roles, invites, sign-in) ───────
const TEAM_ROLES = [
  { key: "admin", label: "Admin", blurb: "Full access — financials, contacts, settings." },
  { key: "manager", label: "Event Manager", blurb: "Runs assigned events — checklist, RSVPs, door. No financials." },
  { key: "staff", label: "Staff", blurb: "Check-in, meals, and door payments." },
  { key: "viewer", label: "Viewer", blurb: "Read-only names and attendance summaries." },
];
const teamPermsFor = (roleKey) => (roleKey === "manager" ? MANAGER_PERMS : can(roleKey));
const TEAM_CAPS = [
  { label: "View dashboard & events", key: null },
  { label: "Check in attendees & meals", key: "checkIn" },
  { label: "Collect payments", key: "collectPayment" },
  { label: "Manage events & approvals", key: "manageEvents" },
  { label: "See financials & revenue", key: "financials" },
  { label: "View member contact info", key: "contact" },
  { label: "Add / edit members", key: "editMembers" },
  { label: "Manage vendors", key: "vendors" },
];

function Team({ team = [], dispatch, perms = {} }) {
  const [invite, setInvite] = useState(null);
  const [resent, setResent] = useState({});
  const initials = (name) => (name || "?").trim().split(/\s+/).map(w => w[0]).slice(0, 2).join("").toUpperCase() || "?";
  const setRole = (u, role) => dispatch && dispatch({ type: "UPDATE", id: u.id, changes: { role } });
  const toggleStatus = (u) => dispatch && dispatch({ type: "UPDATE", id: u.id, changes: { status: u.status === "disabled" ? "active" : "disabled" } });
  const remove = (u) => dispatch && dispatch({ type: "DELETE", id: u.id });
  const openInvite = () => setInvite({ name: "", email: "", role: "staff" });
  const sendInvite = () => {
    if (!invite.email) return;
    dispatch && dispatch({ type: "ADD", item: { name: (invite.name || "").trim() || invite.email.split("@")[0], email: invite.email.trim(), role: invite.role, status: "invited", lastActive: null } });
    setInvite(null);
  };
  const rank = { active: 0, invited: 1, disabled: 2 };
  const sorted = [...team].sort((a, b) => (rank[a.status] ?? 0) - (rank[b.status] ?? 0));

  const statusPill = (st) => {
    const map = { active: { bg: C.greenTint, fg: C.green, t: "Active" }, invited: { bg: "#FEF3C7", fg: "#B45309", t: "Invited" }, disabled: { bg: C.bg, fg: C.muted, t: "Disabled" } };
    const s = map[st] || map.active;
    return <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 10, background: s.bg, color: s.fg, marginLeft: 6 }}>{s.t}</span>;
  };

  return (
    <div>
      <div style={{ ...styles.card, background: C.greenTint, border: `1.5px solid ${C.green}` }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 6 }}>🔐 Team &amp; access</div>
        <div style={{ fontSize: 13, color: C.textSoft, lineHeight: 1.5 }}>In the live product, each teammate has their own login, and their role decides what they can see and do — the same tiers you can preview with the "View as" switcher at the top. Here you'd invite people, set roles, and revoke access. <em>(Demo preview — no real accounts or emails are created.)</em></div>
      </div>

      <div style={styles.card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 6 }}>
          <div style={{ ...styles.sectionTitle, marginBottom: 0 }}>Team members</div>
          {perms.editMembers && <button style={styles.btn("primary")} onClick={openInvite}>+ Invite teammate</button>}
        </div>
        {sorted.length === 0 ? (
          <div style={styles.empty}>No teammates yet. Invite someone to get started.</div>
        ) : sorted.map(u => (
          <div key={u.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 0", borderBottom: `1px solid ${C.border}`, flexWrap: "wrap", opacity: u.status === "disabled" ? 0.6 : 1 }}>
            <div style={{ width: 38, height: 38, borderRadius: "50%", background: C.greenTint, color: C.green, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 14, flexShrink: 0 }}>{initials(u.name)}</div>
            <div style={{ flex: 1, minWidth: 150 }}>
              <div style={{ fontSize: 14, fontWeight: 650, color: C.text }}>{u.name || u.email}{statusPill(u.status)}</div>
              <div style={{ fontSize: 12, color: C.textSoft }}>{u.email}{u.status === "active" && u.lastActive ? ` · last active ${fmtDate(u.lastActive)}` : u.status === "invited" ? (resent[u.id] ? " · invitation re-sent ✓" : " · invitation pending") : u.status === "disabled" ? " · access disabled" : ""}</div>
            </div>
            <select value={u.role} onChange={e => setRole(u, e.target.value)} disabled={!perms.editMembers} style={{ ...styles.input, width: "auto", minWidth: 130 }}>
              {TEAM_ROLES.map(r => <option key={r.key} value={r.key}>{r.label}</option>)}
            </select>
            {perms.editMembers && (
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {u.status === "invited" && <button style={{ ...styles.btn("ghost"), fontSize: 12 }} onClick={() => setResent(r => ({ ...r, [u.id]: true }))}>Resend</button>}
                <button style={{ ...styles.btn("ghost"), fontSize: 12 }} onClick={() => toggleStatus(u)}>{u.status === "disabled" ? "Enable" : "Disable"}</button>
                <button style={{ ...styles.btn("danger"), fontSize: 12 }} onClick={() => remove(u)}>Remove</button>
              </div>
            )}
          </div>
        ))}
        <div style={{ fontSize: 12, color: C.textSoft, marginTop: 12 }}>Changing a role here would instantly change what that person sees on their next screen — no separate setup.</div>
      </div>

      <Section title="Roles &amp; permissions" defaultOpen={true}>
        <div style={{ fontSize: 13, color: C.textSoft, marginBottom: 14 }}>What each role can do. These mirror the access tiers already enforced across the app.</div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 480 }}>
            <thead>
              <tr>
                <th style={{ textAlign: "left", padding: "8px 10px", color: C.textSoft, fontWeight: 600, fontSize: 12 }}>Capability</th>
                {TEAM_ROLES.map(r => <th key={r.key} style={{ padding: "8px 6px", color: C.text, fontWeight: 700, fontSize: 12, textAlign: "center" }}>{r.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {TEAM_CAPS.map((row, i) => (
                <tr key={i} style={{ borderTop: `1px solid ${C.border}` }}>
                  <td style={{ padding: "9px 10px", color: C.text }}>{row.label}</td>
                  {TEAM_ROLES.map(r => { const ok = row.key === null ? true : !!teamPermsFor(r.key)[row.key]; return <td key={r.key} style={{ padding: "9px 6px", textAlign: "center", color: ok ? C.green : C.border, fontWeight: 700, fontSize: 15 }}>{ok ? "✓" : "·"}</td>; })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 16 }}>
          {TEAM_ROLES.map(r => (
            <div key={r.key} style={{ fontSize: 12, color: C.textSoft }}><strong style={{ color: C.text }}>{r.label}:</strong> {r.blurb}</div>
          ))}
        </div>
      </Section>

      <Section title="How teammates sign in" defaultOpen={false}>
        <div style={{ maxWidth: 340, margin: "0 auto", border: `1px solid ${C.border}`, borderRadius: 12, padding: 20 }}>
          <div style={{ textAlign: "center", fontWeight: 800, fontSize: 16, color: C.text, marginBottom: 4 }}>Wilco Members</div>
          <div style={{ textAlign: "center", fontSize: 12, color: C.textSoft, marginBottom: 16 }}>Sign in to your account</div>
          <Field label="Email"><input style={styles.input} value="marcus.johnson@example.com" readOnly /></Field>
          <Field label="Password"><input style={styles.input} type="password" value="passworddemo" readOnly /></Field>
          <button style={{ ...styles.btn("primary"), width: "100%", marginTop: 8, opacity: 0.7 }} disabled>Sign in</button>
          <div style={{ fontSize: 11, color: C.muted, textAlign: "center", marginTop: 12 }}>Preview only — each teammate signs in with their own email and password, and their role controls what they see once inside.</div>
        </div>
      </Section>

      {invite && (
        <Modal title="Invite teammate" onClose={() => setInvite(null)}>
          <div style={styles.grid2}>
            <Field label="Name"><input style={styles.input} value={invite.name} onChange={e => setInvite({ ...invite, name: e.target.value })} placeholder="Full name" /></Field>
            <Field label="Role">
              <select style={styles.input} value={invite.role} onChange={e => setInvite({ ...invite, role: e.target.value })}>
                {TEAM_ROLES.map(r => <option key={r.key} value={r.key}>{r.label}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Email"><input style={styles.input} value={invite.email} onChange={e => setInvite({ ...invite, email: e.target.value })} placeholder="name@example.com" /></Field>
          <div style={{ fontSize: 12, color: C.textSoft, margin: "2px 0 14px" }}>In the live product this sends an email invite. In the demo it just adds a pending teammate.</div>
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <button style={styles.btn("ghost")} onClick={() => setInvite(null)}>Cancel</button>
            <button style={styles.btn("primary")} onClick={sendInvite}>Send invite</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

const TABS = [
  { id: "dashboard", label: "📊 Dashboard" },
  { id: "members", label: "👥 Members" },
  { id: "membership", label: "💳 Membership" },
  { id: "events", label: "📅 Events" },
  { id: "lists", label: "📋 Lists & Check-in" },
  { id: "financials", label: "💰 Financials" },
  { id: "messages", label: "✉️ Messages" },
  { id: "team", label: "🔐 Team" },
  { id: "newsletter", label: "📰 Newsletter" },
];

// ── Member self-service portal (demo: "View as member") ──────────────────────
// Simulated member sign-in: choose a member and see their own home — status,
// next event, RSVP + meal choice, program, bring-a-guest, and event history.
// DEMO: this is a UI simulation over the shared seed data (no separate member
// auth). A production build would authenticate each member and scope rows by RLS.
function DietDisclaimer({ style }) {
  return (
    <div style={{ fontSize: 11.5, color: C.textSoft, display: "flex", gap: 5, alignItems: "flex-start", ...(style || {}) }}>
      <span style={{ color: "#DC2626", fontWeight: 800, fontSize: 14, lineHeight: 1.1 }}>*</span>
      <span><em>{DIET_DISCLAIMER}</em></span>
    </div>
  );
}

// Member's own dietary/allergy card — collapses to a compact summary once reviewed.
function MemberDietCard({ member, dispatch }) {
  const reviewed = !!member.dietReviewed;
  const [editing, setEditing] = useState(!reviewed);
  const [diet, setDiet] = useState(member.diet || "none");
  const [allergies, setAllergies] = useState(member.allergies || "");
  const save = () => { dispatch && dispatch({ type: "UPDATE", id: member.id, changes: { diet, allergies, dietReviewed: true } }); setEditing(false); };
  const markNone = () => { dispatch && dispatch({ type: "UPDATE", id: member.id, changes: { dietReviewed: true } }); setEditing(false); };
  const startEdit = () => { setDiet(member.diet || "none"); setAllergies(member.allergies || ""); setEditing(true); };

  if (reviewed && !editing) {
    const pref = member.diet && member.diet !== "none" ? dietLabel(member.diet) : "No dietary preference";
    const al = (member.allergies || "").trim() ? `allergies: ${member.allergies}` : "no allergies";
    return (
      <div style={{ ...styles.card, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 650, color: C.text }}><span style={{ color: C.green, fontWeight: 800 }}>✓</span> Dietary &amp; allergies</div>
          <div style={{ fontSize: 13, color: C.textSoft }}>{pref} · {al}</div>
        </div>
        <button style={{ ...styles.btn("ghost"), fontSize: 13 }} onClick={startEdit}>Edit</button>
      </div>
    );
  }
  return (
    <div style={styles.card}>
      <div style={styles.sectionTitle}>Dietary &amp; allergies</div>
      <div style={{ fontSize: 13, color: C.textSoft, marginBottom: 14 }}>We&#39;ll share this with catering for events you attend.</div>
      <div style={styles.grid2}>
        <Field label="Dietary preference">
          <select style={styles.input} value={diet} onChange={e => setDiet(e.target.value)}>
            {DIET_OPTIONS.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
          </select>
        </Field>
        <Field label="Food allergies"><input style={styles.input} value={allergies} onChange={e => setAllergies(e.target.value)} placeholder="e.g. peanuts, shellfish" /></Field>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <button style={styles.btn("primary")} onClick={save}>Save</button>
        <button style={styles.btn("ghost")} onClick={markNone}>No dietary needs</button>
        {reviewed && <button style={styles.btn("ghost")} onClick={() => setEditing(false)}>Cancel</button>}
      </div>
      <DietDisclaimer style={{ marginTop: 12 }} />
    </div>
  );
}

// The published announcement shown at the top of the member portal (and in the editor preview).
function AnnouncementBar({ announcement, onDismiss, preview }) {
  if (!announcement) return preview ? <div style={styles.card}><div style={styles.empty}>Nothing to preview yet.</div></div> : null;
  const { title, bodyHtml, pdfUrl } = announcement;
  if (!title && !(bodyHtml || "").trim() && !pdfUrl) return preview ? <div style={styles.card}><div style={styles.empty}>Nothing to preview yet.</div></div> : null;
  return (
    <div style={{ ...styles.card, borderLeft: `4px solid ${C.green}`, background: "#FBFDFB" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
        <div style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: C.green }}>📰 {title || "Newsletter"}</div>
        {onDismiss && <button title="Dismiss" style={{ background: "none", border: "none", cursor: "pointer", color: C.muted, fontSize: 18, fontWeight: 700, lineHeight: 1 }} onClick={onDismiss}>×</button>}
      </div>
      {(bodyHtml || "").trim() ? <div style={{ fontSize: 14, color: C.text, marginTop: 8, lineHeight: 1.5 }} dangerouslySetInnerHTML={{ __html: bodyHtml }} /> : null}
      {pdfUrl ? <a href={pdfUrl} target="_blank" rel="noopener noreferrer" style={{ ...styles.btn("primary"), display: "inline-block", marginTop: 12, textDecoration: "none" }}>📄 Read the full newsletter</a> : null}
    </div>
  );
}

// Admin newsletter editor: lightweight rich text + optional PDF link, publish to the member portal.
function Newsletter({ announcements = [], dispatch }) {
  const current = announcements.slice().sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))[0] || null;
  const [title, setTitle] = useState(current ? current.title || "" : "");
  const [pdfUrl, setPdfUrl] = useState(current ? current.pdfUrl || "" : "");
  const [published, setPublished] = useState(current ? !!current.published : false);
  const [body, setBody] = useState(current ? current.bodyHtml || "" : "");
  const [savedFlag, setSavedFlag] = useState(false);
  const edRef = useRef(null);
  const curId = current ? current.id : null;
  useEffect(() => { if (edRef.current) edRef.current.innerHTML = (current && current.bodyHtml) || ""; setBody((current && current.bodyHtml) || ""); }, [curId]);
  const exec = (cmd, val) => { document.execCommand(cmd, false, val); if (edRef.current) { edRef.current.focus(); setBody(edRef.current.innerHTML); } };
  const addLink = () => { const url = window.prompt("Link URL (https://…):"); if (url) exec("createLink", url); };
  const persist = (pub) => {
    const bodyHtml = edRef.current ? edRef.current.innerHTML : body;
    const item = { title: title.trim(), bodyHtml, pdfUrl: pdfUrl.trim(), published: pub != null ? pub : published };
    if (current) dispatch && dispatch({ type: "UPDATE", id: current.id, changes: item });
    else dispatch && dispatch({ type: "ADD", item });
    if (pub != null) setPublished(pub);
    setSavedFlag(true);
  };
  const tbtn = { ...styles.btn("ghost"), fontSize: 14, padding: "6px 11px", minWidth: 34 };
  return (
    <div>
      <div style={styles.sectionTitle}>📰 Newsletter</div>
      <div style={{ fontSize: 13, color: C.textSoft, marginBottom: 16 }}>Post an announcement to the top of every member&#39;s portal. Write a short note, link a full PDF newsletter, or both.</div>
      <div style={styles.card}>
        <Field label="Headline"><input style={styles.input} value={title} onChange={e => { setTitle(e.target.value); setSavedFlag(false); }} placeholder="e.g. March Newsletter — Gala tickets on sale" /></Field>
        <Field label="Message">
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 6 }}>
            <button style={{ ...tbtn, fontWeight: 800 }} onMouseDown={e => e.preventDefault()} onClick={() => exec("bold")}>B</button>
            <button style={{ ...tbtn, fontStyle: "italic" }} onMouseDown={e => e.preventDefault()} onClick={() => exec("italic")}>i</button>
            <button style={tbtn} onMouseDown={e => e.preventDefault()} onClick={() => exec("formatBlock", "H3")}>H</button>
            <button style={tbtn} onMouseDown={e => e.preventDefault()} onClick={() => exec("insertUnorderedList")}>• List</button>
            <button style={tbtn} onMouseDown={e => e.preventDefault()} onClick={addLink}>🔗 Link</button>
          </div>
          <div ref={edRef} contentEditable suppressContentEditableWarning onInput={e => { setBody(e.currentTarget.innerHTML); setSavedFlag(false); }} style={{ ...styles.input, minHeight: 150, textAlign: "left", overflowY: "auto", lineHeight: 1.5 }} />
        </Field>
        <Field label="Full newsletter PDF link (optional)"><input style={styles.input} value={pdfUrl} onChange={e => { setPdfUrl(e.target.value); setSavedFlag(false); }} placeholder="https://…/newsletter-march.pdf" /></Field>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginTop: 4 }}>
          <button style={styles.btn("ghost")} onClick={() => persist()}>Save draft</button>
          {published
            ? <button style={styles.btn("ghost")} onClick={() => persist(false)}>Unpublish</button>
            : <button style={styles.btn("primary")} onClick={() => persist(true)}>📣 Publish to members</button>}
          <span style={{ fontSize: 12, fontWeight: 700, padding: "3px 10px", borderRadius: 10, background: published ? C.greenTint : C.bg, color: published ? C.green : C.muted }}>{published ? "Live" : "Draft"}</span>
          {savedFlag && <span style={{ fontSize: 13, color: C.green, fontWeight: 650 }}>✓ Saved</span>}
        </div>
        <div style={{ fontSize: 11, color: C.muted, marginTop: 10 }}>Formatting note: keep it simple (bold, italic, headings, lists, links). For a fully designed newsletter, attach it as a PDF above.</div>
      </div>
      <div style={{ ...styles.sectionTitle, marginTop: 8 }}>Preview — how members see it</div>
      <AnnouncementBar announcement={{ title, bodyHtml: body, pdfUrl }} preview />
    </div>
  );
}

function MemberPortal({ member, members, events, invitations, tiers, invDispatch, prospectsDispatch, membersDispatch, announcement, surveyResponses = [], surveyResponsesDispatch, onPickMember, onExit }) {
  const [guestInv, setGuestInv] = useState(null); // invitation we're adding a guest to
  const [gName, setGName] = useState("");
  const [gType, setGType] = useState("");
  const [gMeal, setGMeal] = useState("");
  const [gContact, setGContact] = useState("");
  const [payGuest, setPayGuest] = useState(null); // demo pay step after adding a paid guest

  const tier = tiers.find(t => String(t.id) === String(member.tierId));
  const status = member.status || "active";
  const statusKind = status === "active" ? "yes" : status === "lapsed" ? "no" : "maybe";

  const parseD = (d) => d ? new Date(d + "T00:00:00") : null;
  const today = new Date(); today.setHours(0, 0, 0, 0);

  const mine = invitations
    .filter(i => String(i.memberId) === String(member.id))
    .map(i => ({ inv: i, ev: events.find(e => String(e.id) === String(i.eventId)) }))
    .filter(x => x.ev && x.ev.published);

  const upcoming = mine
    .filter(x => { const d = parseD(x.ev.eventDate); return !d || d >= today; })
    .sort((a, b) => (parseD(a.ev.eventDate) || 0) - (parseD(b.ev.eventDate) || 0));
  const hero = upcoming[0];
  const rest = upcoming.slice(1);
  const history = mine
    .filter(x => x.inv.attended || (parseD(x.ev.eventDate) && parseD(x.ev.eventDate) < today))
    .sort((a, b) => (parseD(b.ev.eventDate) || 0) - (parseD(a.ev.eventDate) || 0));
  const surveyableList = mine.filter(x => x.ev.surveyOpen && (x.ev.survey || []).length && (x.inv.attended || x.inv.rsvp === "yes"));

  const setInv = (inv, changes) => invDispatch({ type: "UPDATE", id: inv.id, changes });

  const openGuest = (inv) => {
    setGuestInv(inv); setGName(""); setGMeal(""); setGContact("");
    const ev = events.find(e => String(e.id) === String(inv.eventId));
    const gts = (ev && ev.guestTypes) || [];
    setGType(gts[0] ? gts[0].label : "");
  };
  const confirmGuest = () => {
    const inv = guestInv; if (!inv) return;
    const ev = events.find(e => String(e.id) === String(inv.eventId));
    const gts = (ev && ev.guestTypes) || [];
    const cat = gts.find(g => g.label === gType);
    const entry = { name: gName.trim() || "Guest", meal: gMeal || "", guestType: gType || "", price: cat ? Number(cat.price) || 0 : 0 };
    const details = [...(inv.guestDetails || []), entry];
    setInv(inv, { guestCount: details.length, guestDetails: details });
    if (prospectsDispatch) prospectsDispatch({ type: "ADD", item: { name: entry.name, contact: gContact.trim(), sourceEventId: ev.id, invitedByMemberId: member.id, guestType: entry.guestType, meal: entry.meal, status: "new" } });
    setGuestInv(null);
    if (entry.price > 0) setPayGuest({ amount: entry.price, label: `${ev.name} — guest (${entry.guestType})` });
  };

  const refNews = useRef(null), refMem = useRef(null), refDiet = useRef(null), refEv = useRef(null), refFb = useRef(null);
  const refMap = { newsletter: refNews, membership: refMem, dietary: refDiet, events: refEv, feedback: refFb };
  const [isWide, setIsWide] = useState(typeof window !== "undefined" ? window.innerWidth >= 860 : true);
  const [dismissed, setDismissed] = useState(false);
  useEffect(() => { const onR = () => setIsWide(window.innerWidth >= 860); window.addEventListener("resize", onR); return () => window.removeEventListener("resize", onR); }, []);
  const goTo = (k) => { const r = refMap[k]; if (r && r.current) r.current.scrollIntoView({ behavior: "smooth", block: "start" }); };
  const showNews = !!announcement && !dismissed;
  const navItems = [showNews && { key: "newsletter", label: "📰 News" }, { key: "membership", label: "💳 Membership" }, { key: "dietary", label: "🍽️ Dietary" }, { key: "events", label: "📅 Events" }, surveyableList.length && { key: "feedback", label: "📝 Feedback" }].filter(Boolean);
  const sm = { scrollMarginTop: 78 };

  const duesAlert = (() => {
    const di = member.renewsOn ? dueInfo(member.renewsOn) : null;
    if (!di || di.tone === "ok") return null;
    const st = di.tone === "overdue" ? { bg: "#DC2626", fg: "#FFF", border: "#B91C1C" } : { bg: "#FEF3C7", fg: "#92400E", border: "#FCD34D" };
    const msg = di.tone === "overdue"
      ? `Your membership expired ${Math.abs(di.days)} day${Math.abs(di.days) === 1 ? "" : "s"} ago. Renew to stay active.`
      : di.days === 0 ? "Your membership renewal is due today."
      : `Your membership renews in ${di.days} day${di.days === 1 ? "" : "s"} — ${fmtDate(member.renewsOn)}.`;
    return (
      <div style={{ background: st.bg, color: st.fg, border: `1.5px solid ${st.border}`, borderRadius: 12, padding: "12px 16px", marginBottom: 16, fontSize: 14, fontWeight: 650, display: "flex", alignItems: "center", gap: 10 }}>
        <span style={{ fontSize: 18, flexShrink: 0 }}>{di.tone === "overdue" ? "⚠️" : "🔔"}</span>
        <span>{msg}</span>
      </div>
    );
  })();

  const nav = (
    <nav style={isWide
      ? { position: "sticky", top: 16, width: 172, flexShrink: 0, display: "flex", flexDirection: "column", gap: 4 }
      : { position: "sticky", top: 0, zIndex: 5, display: "flex", gap: 8, overflowX: "auto", padding: "8px 0 10px", background: C.bg, marginBottom: 8, WebkitOverflowScrolling: "touch" }}>
      {navItems.map(it => (
        <button key={it.key} onClick={() => goTo(it.key)} style={isWide
          ? { textAlign: "left", padding: "8px 12px", borderRadius: 8, border: "none", background: "none", cursor: "pointer", fontSize: 14, fontWeight: 600, color: C.textSoft }
          : { flexShrink: 0, padding: "7px 14px", borderRadius: 20, border: `1px solid ${C.border}`, background: "#FFF", cursor: "pointer", fontSize: 13, fontWeight: 600, color: C.text, whiteSpace: "nowrap" }}>{it.label}</button>
      ))}
    </nav>
  );

  const sections = (
    <>
      {showNews && <div ref={refNews} style={sm}><AnnouncementBar announcement={announcement} onDismiss={() => setDismissed(true)} /></div>}

      <div ref={refMem} style={sm}>
        <div style={styles.card}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 10 }}>
            <div>
              <div style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: "0.05em", color: C.muted, marginBottom: 4 }}>Membership</div>
              <div style={{ fontSize: 20, fontWeight: 750, color: C.green }}>
                {tier ? tier.label : "—"}
                {tier && Number(tier.price) > 0 ? <span style={{ fontSize: 14, color: C.textSoft, fontWeight: 600 }}> · {money(tier.price)}/{tier.period === "month" ? "mo" : tier.period === "once" ? "once" : "yr"}</span> : null}
              </div>
            </div>
            <span style={styles.badge(statusKind)}>{status.charAt(0).toUpperCase() + status.slice(1)}</span>
          </div>
          <div style={{ display: "flex", gap: 24, marginTop: 14, flexWrap: "wrap" }}>
            {member.joinedOn && <div><div style={{ fontSize: 12, color: C.muted }}>Member since</div><div style={{ fontSize: 14, fontWeight: 650, color: C.text }}>{fmtDate(member.joinedOn)}</div></div>}
            {member.renewsOn && <div><div style={{ fontSize: 12, color: C.muted }}>{status === "lapsed" ? "Expired" : "Renews"}</div><div style={{ fontSize: 14, fontWeight: 650, color: status === "lapsed" ? C.danger : C.text }}>{fmtDate(member.renewsOn)}</div><div style={{ marginTop: 4 }}><DueBadge date={member.renewsOn} /></div></div>}
          </div>
          {tier && (tier.benefits || []).length > 0 && (
            <div style={{ marginTop: 14, display: "flex", flexWrap: "wrap", gap: 6 }}>
              {(tier.benefits || []).map((b, i) => <span key={i} style={styles.badge("invited")}>{b}</span>)}
            </div>
          )}
        </div>
      </div>

      <div ref={refDiet} style={sm}><MemberDietCard member={member} dispatch={membersDispatch} /></div>

      <div ref={refEv} style={sm}>
        {hero ? (
          <MemberEventCard entry={hero} onRespond={(val) => setInv(hero.inv, { rsvp: val })} onMeal={(m) => setInv(hero.inv, { mealChoice: m })} onAddGuest={() => openGuest(hero.inv)} hero />
        ) : (
          <div style={styles.card}><div style={styles.empty}>You have no upcoming events right now. We&#39;ll let you know when something&#39;s on the calendar.</div></div>
        )}
        {rest.length > 0 && (
          <>
            <div style={{ ...styles.sectionTitle, marginTop: 24 }}>More upcoming events</div>
            {rest.map(x => <MemberEventCard key={x.inv.id} entry={x} onRespond={(val) => setInv(x.inv, { rsvp: val })} onMeal={(m) => setInv(x.inv, { mealChoice: m })} onAddGuest={() => openGuest(x.inv)} />)}
          </>
        )}
        <div style={{ ...styles.sectionTitle, marginTop: 24 }}>Your event history</div>
        {history.length === 0 ? (
          <div style={styles.card}><div style={styles.empty}>No past events yet.</div></div>
        ) : (
          <div style={styles.card}>
            {history.map((x, i) => (
              <div key={x.inv.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: i < history.length - 1 ? `1px solid ${C.border}` : "none" }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 650, color: C.text }}>{x.ev.name}</div>
                  <div style={{ fontSize: 12, color: C.textSoft }}>{fmtDate(x.ev.eventDate)}{x.ev.location ? ` · ${x.ev.location}` : ""}</div>
                </div>
                {x.inv.attended ? <span style={styles.badge("attended")}>Attended</span> : <span style={styles.badge(x.inv.rsvp === "no" ? "no" : "invited")}>{x.inv.rsvp === "no" ? "Didn't attend" : "No record"}</span>}
              </div>
            ))}
          </div>
        )}
      </div>

      {surveyableList.length > 0 && (
        <div ref={refFb} style={sm}>
          <div style={{ ...styles.sectionTitle, marginTop: 24 }}>Event feedback</div>
          {surveyableList.map(x => <MemberSurvey key={x.ev.id} event={x.ev} member={member} responses={surveyResponses} dispatch={surveyResponsesDispatch} />)}
        </div>
      )}
    </>
  );

  return (
    <>
      {/* Member-mode banner + picker */}
      <div style={{ background: C.slate, color: "#FFF", padding: "10px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
        <div style={{ fontSize: 13, opacity: 0.9 }}>👤 <strong>Member portal</strong> — demo preview of what a signed-in member sees.</div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span className="hide-mobile" style={{ fontSize: 12, opacity: 0.75 }}>Viewing as:</span>
          <select value={member.id} onChange={e => onPickMember(e.target.value)} style={{ ...styles.input, width: "auto", padding: "6px 10px" }}>
            {members.map(m => <option key={m.id} value={m.id}>{`${m.firstName} ${m.lastName}`.trim()}</option>)}
          </select>
          <button style={{ ...styles.btn("ghost"), color: "#FFF", borderColor: "rgba(255,255,255,0.4)", padding: "6px 12px" }} onClick={onExit}>← Exit</button>
        </div>
      </div>

      <main style={styles.main}>
        <div style={{ marginBottom: 18 }}>
          <div style={{ fontSize: 24, fontWeight: 800, color: C.text }}>Welcome, {member.firstName || "Member"}.</div>
          <div style={{ fontSize: 14, color: C.textSoft, marginTop: 2 }}>Here&#39;s your membership at a glance.</div>
        </div>

        {duesAlert}

        {isWide ? (
          <div style={{ display: "flex", gap: 24, alignItems: "flex-start" }}>
            {nav}
            <div style={{ flex: 1, minWidth: 0 }}>{sections}</div>
          </div>
        ) : (
          <>{nav}{sections}</>
        )}
      </main>

      {/* Bring-a-guest modal */}
      {guestInv && (() => {
        const ev = events.find(e => String(e.id) === String(guestInv.eventId));
        const gts = (ev && ev.guestTypes) || [];
        const cat = gts.find(g => g.label === gType);
        return (
          <Modal title="Bring a guest" onClose={() => setGuestInv(null)}>
            <div style={{ fontSize: 13, color: C.textSoft, marginBottom: 14 }}>Invite a guest to <strong>{ev.name}</strong>. {gts.length > 0 ? "Choose a guest type below." : "Guests are welcome at no charge."}</div>
            <Field label="Guest name"><input style={styles.input} value={gName} onChange={e => setGName(e.target.value)} placeholder="Full name" /></Field>
            <Field label="Guest email or phone (so we can follow up)"><input style={styles.input} value={gContact} onChange={e => setGContact(e.target.value)} placeholder="name@example.com or 555-0100" /></Field>
            {gts.length > 0 && (
              <Field label="Guest type">
                <select style={styles.input} value={gType} onChange={e => setGType(e.target.value)}>
                  {gts.map((g, i) => <option key={i} value={g.label}>{g.label}{Number(g.price) > 0 ? ` — ${money(g.price)}` : " — free"}</option>)}
                </select>
              </Field>
            )}
            {ev.mealOptions && ev.mealOptions.length > 0 && (
              <Field label="Meal choice (optional)">
                <select style={styles.input} value={gMeal} onChange={e => setGMeal(e.target.value)}>
                  <option value="">Select…</option>
                  {ev.mealOptions.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </Field>
            )}
            {cat && Number(cat.price) > 0 && <div style={{ fontSize: 13, color: C.text, background: C.bg, borderRadius: 8, padding: "10px 12px", marginBottom: 14 }}>Guest fee: <strong>{money(cat.price)}</strong></div>}
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button style={styles.btn("ghost")} onClick={() => setGuestInv(null)}>Cancel</button>
              <button style={styles.btn("primary")} onClick={confirmGuest}>Add guest</button>
            </div>
          </Modal>
        );
      })()}

      {payGuest && (
        <PayDemoFlow title="Guest fee" recipientName={`${member.firstName} ${member.lastName}`.trim()} recipientEmail={member.email} lineItem={payGuest.label} amount={payGuest.amount} onPaid={() => setPayGuest(null)} onClose={() => setPayGuest(null)} />
      )}
    </>
  );
}

// A single event card inside the member portal: details, RSVP, meal, guests, program.
function MemberEventCard({ entry, onRespond, onMeal, onAddGuest, hero }) {
  const { inv, ev } = entry;
  const rsvp = inv.rsvp || "invited";
  const deadlinePassed = ev.mealDeadline ? new Date(ev.mealDeadline + "T23:59:59") < new Date() : false;
  const RsvpBtn = ({ value, label, color }) => (
    <button onClick={() => onRespond(value)} style={{ flex: 1, padding: "10px 8px", borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: "pointer", border: rsvp === value ? `2px solid ${color}` : `1.5px solid ${C.border}`, background: rsvp === value ? color : "#FFF", color: rsvp === value ? "#FFF" : C.text }}>{label}</button>
  );
  return (
    <div style={{ ...styles.card, ...(hero ? { border: `2px solid ${C.green}` } : {}) }}>
      {hero && <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em", color: C.green, fontWeight: 700, marginBottom: 6 }}>Your next event</div>}
      <div style={{ fontSize: 19, fontWeight: 750, color: C.text }}>{ev.name}</div>
      <div style={{ fontSize: 13, color: C.textSoft, marginTop: 3 }}>{fmtDate(ev.eventDate)}{ev.location ? ` · ${ev.location}` : ""}{Number(ev.price) > 0 ? ` · ${money(ev.price)}` : " · Free"}</div>
      {ev.eventDate && (
        <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
          <button style={{ ...styles.btn("ghost"), fontSize: 12 }} onClick={() => downloadICS(ev)}>📅 Add to calendar</button>
          <a href={googleCalUrl(ev)} target="_blank" rel="noopener noreferrer" style={{ ...styles.btn("ghost"), fontSize: 12, textDecoration: "none", display: "inline-flex", alignItems: "center" }}>Google</a>
        </div>
      )}

      <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginTop: 16, marginBottom: 6 }}>Will you attend?</div>
      <div style={{ display: "flex", gap: 8 }}>
        <RsvpBtn value="yes" label="Plan to attend" color={C.green} />
        <RsvpBtn value="maybe" label="Unsure" color="#B8860B" />
        <RsvpBtn value="no" label="Can't make it" color={C.danger} />
      </div>

      {rsvp === "yes" && (
        <>
          {ev.mealOptions && ev.mealOptions.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 6 }}>Meal choice{ev.mealDeadline ? <span style={{ fontWeight: 500, color: deadlinePassed ? C.danger : C.textSoft }}> · {deadlinePassed ? "deadline passed" : `choose by ${fmtDate(ev.mealDeadline)}`}</span> : null}</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {ev.mealOptions.map(m => (
                  <button key={m} disabled={deadlinePassed} onClick={() => onMeal(m)} style={{ padding: "8px 14px", borderRadius: 20, fontSize: 13, fontWeight: 650, cursor: deadlinePassed ? "not-allowed" : "pointer", border: inv.mealChoice === m ? `2px solid ${C.green}` : `1.5px solid ${C.border}`, background: inv.mealChoice === m ? C.greenTint : "#FFF", color: inv.mealChoice === m ? C.greenDark : C.text, opacity: deadlinePassed && inv.mealChoice !== m ? 0.5 : 1 }}>{m}</button>
                ))}
              </div>
            </div>
          )}

          {ev.allowGuests && (
            <div style={{ marginTop: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 6 }}>Guests</div>
              {(inv.guestDetails || []).length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
                  {(inv.guestDetails || []).map((g, i) => <span key={i} style={styles.badge("invited")}>{g.name}{g.guestType ? ` · ${g.guestType}` : ""}{g.meal ? ` · ${g.meal}` : ""}</span>)}
                </div>
              )}
              <button style={styles.btn("ghost")} onClick={onAddGuest}>+ Bring a guest</button>
            </div>
          )}
        </>
      )}

      {(ev.program || []).length > 0 && (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 8 }}>Program</div>
          <div style={{ display: "grid", gap: 6 }}>
            {(ev.program || []).map((p, i) => (
              <div key={i} style={{ display: "flex", gap: 10, fontSize: 13, color: C.text, padding: "6px 0", borderBottom: i < (ev.program.length - 1) ? `1px solid ${C.border}` : "none" }}>
                {p.detail ? <div style={{ width: 70, color: C.muted, flexShrink: 0 }}>{p.detail}</div> : null}
                <div><span style={{ fontWeight: 650 }}>{p.title}</span>{p.speaker ? <span style={{ color: C.textSoft }}> · {p.speaker}</span> : null}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Membership: tier/dues catalog + renewals ("who's due") ───────────────────
// Admin surface: manage membership tiers, and work the renewal pipeline —
// members bucketed into overdue / due-soon / lapsed, each renewable in a tap
// (advancing their date by the tier's period, and collecting dues if priced).
function Membership({ members, membersDispatch, tiers, tiersDispatch, messagesDispatch, paymentsDispatch, perms = {} }) {
  const [tierModal, setTierModal] = useState(null); // "add" | tier | null
  const [tform, setTform] = useState({});
  const [benefitInput, setBenefitInput] = useState("");
  const [payDues, setPayDues] = useState(null); // { member, tier, amount }
  const [compose, setCompose] = useState(null);

  const tierById = (id) => tiers.find(t => String(t.id) === String(id));
  const periodLabel = (p) => p === "month" ? "mo" : p === "once" ? "one-time" : "yr";
  const memberName = (m) => `${m.firstName || ""} ${m.lastName || ""}`.trim() || "(no name)";

  const openAddTier = () => { setTform({ label: "", price: "", period: "year", benefits: [], sort: tiers.length + 1 }); setBenefitInput(""); setTierModal("add"); };
  const openEditTier = (t) => { setTform({ ...t, benefits: t.benefits || [] }); setBenefitInput(""); setTierModal(t); };
  const closeTier = () => setTierModal(null);
  const addBenefit = () => { const b = benefitInput.trim(); if (!b || (tform.benefits || []).includes(b)) return; setTform(f => ({ ...f, benefits: [...(f.benefits || []), b] })); setBenefitInput(""); };
  const removeBenefit = (b) => setTform(f => ({ ...f, benefits: (f.benefits || []).filter(x => x !== b) }));
  const saveTier = () => {
    if (!tform.label) return;
    const item = { label: tform.label.trim(), price: Number(tform.price) || 0, period: tform.period || "year", benefits: tform.benefits || [], sort: Number(tform.sort) || 0 };
    if (tierModal === "add") tiersDispatch({ type: "ADD", item }); else tiersDispatch({ type: "UPDATE", id: tierModal.id, changes: item });
    closeTier();
  };
  const deleteTier = (t) => tiersDispatch({ type: "DELETE", id: t.id });
  const sortedTiers = [...tiers].sort((a, b) => (Number(a.sort) || 0) - (Number(b.sort) || 0));

  // Renewals — bucket members by standing.
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const parseD = (d) => d ? new Date(d + "T00:00:00") : null;
  const soonCutoff = new Date(today); soonCutoff.setDate(soonCutoff.getDate() + 30);
  const bucketOf = (m) => {
    if ((m.status || "active") === "lapsed") return "lapsed";
    const r = parseD(m.renewsOn);
    if (!r) return "active";
    if (r < today) return "overdue";
    if (r <= soonCutoff) return "due";
    return "active";
  };
  const buckets = { overdue: [], due: [], lapsed: [], active: [] };
  members.forEach(m => buckets[bucketOf(m)].push(m));

  const advance = (fromDate, period) => {
    if (period === "once") return null;
    const cur = parseD(fromDate);
    const base = cur && cur > today ? cur : new Date(today);
    const d = new Date(base);
    if (period === "month") d.setMonth(d.getMonth() + 1); else d.setFullYear(d.getFullYear() + 1);
    return d.toISOString().slice(0, 10);
  };
  const markRenewed = (m) => {
    const t = tierById(m.tierId);
    const next = advance(m.renewsOn, t ? t.period : "year");
    membersDispatch({ type: "UPDATE", id: m.id, changes: { status: "active", renewsOn: next } });
  };
  const renewMember = (m) => {
    const t = tierById(m.tierId);
    if (t && Number(t.price) > 0) setPayDues({ member: m, tier: t, amount: Number(t.price) });
    else markRenewed(m);
  };
  const remindBucket = (b) => setCompose({
    kind: "reminder",
    recipients: buckets[b.key],
    audienceLabel: b.key === "due" ? "Due-soon members" : b.key === "overdue" ? "Overdue members" : "Lapsed members",
    defaultSubject: "Your membership renewal",
    defaultBody: "Hi there,\n\nThis is a friendly reminder that your membership is up for renewal. You can renew online or at our next event — we'd love to keep you with us.\n\nThank you for being part of our community!",
  });

  const RenewRow = ({ m, kind }) => {
    const t = tierById(m.tierId);
    return (
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, padding: "8px 12px", background: C.bg, borderRadius: 8, marginBottom: 6 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 650, color: C.text }}>{memberName(m)}</div>
          <div style={{ fontSize: 12, color: C.textSoft }}>{t ? t.label : "No tier"}{m.renewsOn ? ` · ${kind === "overdue" || kind === "lapsed" ? "was due" : "renews"} ${fmtDate(m.renewsOn)}` : ""}</div>
        </div>
        <button style={{ ...styles.btn("primary"), fontSize: 12, padding: "6px 12px", whiteSpace: "nowrap" }} onClick={() => renewMember(m)}>{t && Number(t.price) > 0 ? `Renew · ${money(t.price)}` : "Mark renewed"}</button>
      </div>
    );
  };

  const bucketMeta = [
    { key: "overdue", title: "⚠️ Overdue", color: C.danger },
    { key: "due", title: "⏳ Due soon (30 days)", color: "#8A6D2E" },
    { key: "lapsed", title: "❌ Lapsed", color: C.danger },
  ];

  return (
    <div>
      {/* TIER CATALOG */}
      <div style={styles.card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10, marginBottom: 12 }}>
          <div style={{ ...styles.sectionTitle, marginBottom: 0 }}>Membership tiers</div>
          <button style={styles.btn("primary")} onClick={openAddTier}>+ Add Tier</button>
        </div>
        {sortedTiers.length === 0 ? (
          <div style={styles.empty}>No tiers yet. Add your membership levels to get started.</div>
        ) : (
          <div style={{ display: "grid", gap: 10 }}>
            {sortedTiers.map(t => {
              const count = members.filter(m => String(m.tierId) === String(t.id)).length;
              return (
                <div key={t.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, border: `1px solid ${C.border}`, borderRadius: 10, padding: "12px 14px" }}>
                  <div>
                    <div style={{ fontSize: 16, fontWeight: 750, color: C.green }}>{t.label} <span style={{ fontSize: 13, color: C.textSoft, fontWeight: 600 }}>· {money(t.price)}/{periodLabel(t.period)}</span></div>
                    <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{count} member{count === 1 ? "" : "s"}</div>
                    {(t.benefits || []).length > 0 && <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>{(t.benefits || []).map((b, i) => <span key={i} style={styles.badge("invited")}>{b}</span>)}</div>}
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button style={{ ...styles.btn("ghost"), fontSize: 12 }} onClick={() => openEditTier(t)}>Edit</button>
                    <button style={{ ...styles.btn("danger"), fontSize: 12 }} onClick={() => deleteTier(t)}>Remove</button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* RENEWALS / WHO'S DUE */}
      <div style={styles.card}>
        <div style={styles.sectionTitle}>Renewals</div>
        {bucketMeta.every(b => buckets[b.key].length === 0) ? (
          <div style={styles.empty}>Everyone's current — no renewals due. 🎉</div>
        ) : bucketMeta.map(b => buckets[b.key].length > 0 && (
          <div key={b.key} style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: b.color }}>{b.title} — {buckets[b.key].length}</div>
              {messagesDispatch && <button style={{ ...styles.btn("ghost"), fontSize: 12, padding: "5px 10px" }} onClick={() => remindBucket(b)}>🔔 Remind all</button>}
            </div>
            {buckets[b.key].map(m => <RenewRow key={m.id} m={m} kind={b.key} />)}
          </div>
        ))}
        <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>{buckets.active.length} member{buckets.active.length === 1 ? "" : "s"} current and not due soon.</div>
      </div>

      {/* Tier add/edit modal */}
      {tierModal && (
        <Modal title={tierModal === "add" ? "Add Tier" : "Edit Tier"} onClose={closeTier}>
          <Field label="Tier name"><input style={styles.input} value={tform.label || ""} onChange={e => setTform({ ...tform, label: e.target.value })} placeholder="e.g. Individual" /></Field>
          <div style={styles.grid2}>
            <Field label="Price ($)"><input style={styles.input} type="number" step="any" value={tform.price ?? ""} onChange={e => setTform({ ...tform, price: e.target.value })} placeholder="0" /></Field>
            <Field label="Billing period">
              <select style={styles.input} value={tform.period || "year"} onChange={e => setTform({ ...tform, period: e.target.value })}>
                <option value="year">Per year</option>
                <option value="month">Per month</option>
                <option value="once">One-time (lifetime)</option>
              </select>
            </Field>
          </div>
          <Field label="Benefits">
            <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
              <input style={styles.input} value={benefitInput} onChange={e => setBenefitInput(e.target.value)} onKeyDown={e => e.key === "Enter" && (e.preventDefault(), addBenefit())} placeholder="e.g. Event discounts" />
              <button style={styles.btn("ghost")} onClick={addBenefit}>Add</button>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {(tform.benefits || []).map(b => <span key={b} style={{ ...styles.badge("invited"), display: "inline-flex", alignItems: "center", gap: 6 }}>{b}<button onClick={() => removeBenefit(b)} style={{ background: "none", border: "none", cursor: "pointer", color: C.danger, fontWeight: 700 }}>×</button></span>)}
            </div>
          </Field>
          <Field label="Display order"><input style={styles.input} type="number" step="1" value={tform.sort ?? ""} onChange={e => setTform({ ...tform, sort: e.target.value })} placeholder="1" /></Field>
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <button style={styles.btn("ghost")} onClick={closeTier}>Cancel</button>
            <button style={styles.btn("primary")} onClick={saveTier}>Save Tier</button>
          </div>
        </Modal>
      )}

      {payDues && (
        <PayDemoFlow title="Collect Dues" recipientName={memberName(payDues.member)} recipientEmail={payDues.member.email || ""} lineItem={`${payDues.tier.label} membership dues`} amount={payDues.amount} onPaid={() => { markRenewed(payDues.member); recordPayment(paymentsDispatch, { amount: payDues.amount, kind: "dues", memberId: payDues.member.id, sourceType: "member", sourceId: payDues.member.id, label: `${payDues.tier.label} membership dues` }); }} onClose={() => setPayDues(null)} />
      )}

      {compose && <ComposeMessage {...compose} messagesDispatch={messagesDispatch} onClose={() => setCompose(null)} />}
    </div>
  );
}

// ── Communications: simulated compose/send + message history ─────────────────
const MSG_KINDS = {
  reminder: { label: "Renewal reminder", icon: "🔔" },
  announcement: { label: "Announcement", icon: "📣" },
  receipt: { label: "Receipt", icon: "🧾" },
};

// Shared composer. Simulates sending and logs one row to mbr_messages.
function ComposeMessage({ kind = "announcement", recipients = [], audienceLabel = "", defaultSubject = "", defaultBody = "", eventId = null, messagesDispatch, onClose }) {
  const [channel, setChannel] = useState("email");
  const [subject, setSubject] = useState(defaultSubject);
  const [body, setBody] = useState(defaultBody);
  const [sent, setSent] = useState(false);

  const hasContact = (r) => channel === "email" ? !!(r.email && String(r.email).trim()) : !!(r.phone && String(r.phone).trim());
  const eligible = recipients.filter(r => !r.no_contact && !r.noContact && hasContact(r));
  const excluded = recipients.length - eligible.length;
  const meta = MSG_KINDS[kind] || MSG_KINDS.announcement;

  const send = () => {
    if (messagesDispatch) messagesDispatch({ type: "ADD", item: { kind, channel, subject: channel === "email" ? subject : "", body, audienceLabel, recipientCount: eligible.length, eventId: eventId || null } });
    setSent(true);
  };

  return (
    <Modal title={sent ? "Sent" : `${meta.icon} ${meta.label}`} onClose={onClose}>
      {sent ? (
        <div style={{ textAlign: "center", padding: "10px 0" }}>
          <div style={{ fontSize: 40, marginBottom: 8, color: C.green }}>✓</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: C.green }}>Sent to {eligible.length} recipient{eligible.length === 1 ? "" : "s"}</div>
          <div style={{ fontSize: 13, color: C.textSoft, marginTop: 4 }}>via {channel === "email" ? "email" : "SMS"} · logged in Messages</div>
          <button style={{ ...styles.btn("primary"), marginTop: 18 }} onClick={onClose}>Done</button>
        </div>
      ) : (
        <>
          <div style={{ fontSize: 13, color: C.textSoft, background: C.bg, borderRadius: 8, padding: "10px 12px", marginBottom: 14 }}>
            <strong>{audienceLabel || "Recipients"}</strong> · {eligible.length} will receive this{excluded > 0 ? ` · ${excluded} skipped (no contact / opted out)` : ""}
          </div>
          <Field label="Channel">
            <div style={{ display: "flex", gap: 8 }}>
              {["email", "sms"].map(ch => (
                <button key={ch} onClick={() => setChannel(ch)} style={{ flex: 1, padding: "9px", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: "pointer", border: channel === ch ? `2px solid ${C.green}` : `1.5px solid ${C.border}`, background: channel === ch ? C.greenTint : "#FFF", color: channel === ch ? C.greenDark : C.text }}>{ch === "email" ? "✉️ Email" : "💬 SMS"}</button>
              ))}
            </div>
          </Field>
          {channel === "email" && <Field label="Subject"><input style={styles.input} value={subject} onChange={e => setSubject(e.target.value)} placeholder="Subject line" /></Field>}
          <Field label="Message"><textarea style={{ ...styles.input, minHeight: 120, resize: "vertical" }} value={body} onChange={e => setBody(e.target.value)} placeholder="Write your message…" /></Field>
          <div style={{ fontSize: 12, color: C.muted, marginBottom: 14 }}>Demo — messages are logged to show the workflow, not actually delivered.</div>
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <button style={styles.btn("ghost")} onClick={onClose}>Cancel</button>
            <button style={{ ...styles.btn("primary"), opacity: eligible.length === 0 ? 0.5 : 1 }} onClick={send} disabled={eligible.length === 0}>Send to {eligible.length}</button>
          </div>
        </>
      )}
    </Modal>
  );
}

// Messages tab: history + start a new announcement to all members.
function Communications({ messages, messagesDispatch, members, events, perms = {} }) {
  const [compose, setCompose] = useState(null);
  const sorted = [...messages].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
  const eventName = (id) => { const e = events.find(x => String(x.id) === String(id)); return e ? e.name : null; };
  const openAnnouncement = () => setCompose({ kind: "announcement", recipients: members, audienceLabel: "All members", defaultSubject: "", defaultBody: "" });

  return (
    <div>
      <div style={styles.card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
          <div>
            <div style={{ ...styles.sectionTitle, marginBottom: 2 }}>Messages</div>
            <div style={{ fontSize: 13, color: C.textSoft }}>Renewal reminders, announcements, and receipts sent to members.</div>
          </div>
          <button style={styles.btn("primary")} onClick={openAnnouncement}>📣 New announcement</button>
        </div>
      </div>

      <div style={styles.card}>
        {sorted.length === 0 ? (
          <div style={styles.empty}>No messages sent yet. Send a reminder from the Membership tab, announce an event, or start an announcement above.</div>
        ) : sorted.map(m => {
          const meta = MSG_KINDS[m.kind] || MSG_KINDS.announcement;
          const en = m.eventId ? eventName(m.eventId) : null;
          return (
            <div key={m.id} style={{ display: "flex", gap: 12, padding: "12px 0", borderBottom: `1px solid ${C.border}` }}>
              <div style={{ fontSize: 22 }}>{meta.icon}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{m.subject || meta.label}</span>
                  <span style={{ fontSize: 12, color: C.muted }}>{fmtDate(String(m.created_at || "").slice(0, 10))}</span>
                </div>
                {m.body ? <div style={{ fontSize: 13, color: C.textSoft, marginTop: 2 }}>{m.body.length > 140 ? m.body.slice(0, 140) + "…" : m.body}</div> : null}
                <div style={{ fontSize: 12, color: C.muted, marginTop: 6 }}>{meta.label} · {m.channel === "sms" ? "SMS" : "Email"} · {m.recipientCount || 0} recipient{(m.recipientCount || 0) === 1 ? "" : "s"}{m.audienceLabel ? ` · ${m.audienceLabel}` : ""}{en ? ` · ${en}` : ""}</div>
              </div>
            </div>
          );
        })}
      </div>

      {compose && <ComposeMessage {...compose} messagesDispatch={messagesDispatch} onClose={() => setCompose(null)} />}
    </div>
  );
}

// ── Self-guided product tour (mirrors the catering demo's tour styling) ───────
const TOUR_STEPS = [
  { tab: "dashboard", title: "Welcome 👋", body: "This is a live membership manager — members, events, money, and the member's own portal, all connected. Take a two-minute tour, or tap “Check it out” any time to explore, then resume where you left off." },
  { tab: "dashboard", title: "Your dashboard", body: "A customizable overview: membership health, revenue, and what's coming up. Admins can show, hide, and reorder widgets so the front page shows what matters to them." },
  { tab: "members", title: "Members & groups", body: "The full roster with tiers, status, and groups (region, chapter, category). Renewals due soon glow amber and turn red when overdue — and you can print name tags or export in a click." },
  { tab: "membership", title: "Tiers & renewals", body: "Manage dues tiers and see exactly who's due, overdue, or lapsed — then collect payment right from the renewal list." },
  { tab: "events", title: "Events, drafts & announcing", body: "Plan events start to finish. New events stay hidden as drafts until you announce them — so a plan that falls through never reaches your members." },
  { tab: "events", openEvent: true, title: "Pre-flight checklist & approval", body: "Every event carries a checklist with due dates, and invitations stay locked until the plan is approved — no half-baked event goes out the door." },
  { tab: "events", openEvent: true, title: "Suppliers & cost history", body: "Assign caterers, venues, and vendors from a directory that remembers what each one charged and how they rated — real numbers to budget against, and a running supplier cost for the event." },
  { tab: "events", openEvent: true, title: "Dietary & allergies", body: "Members set dietary needs once on their profile; here they roll up into a caterer-ready summary, with allergies flagged in red on the attendee list." },
  { tab: "events", openEvent: true, title: "Post-event surveys", body: "Build a quick feedback survey per event — overall plus per-area ratings — and watch the averages and comments roll in." },
  { tab: "events", openEvent: true, title: "Guarded cancellation", body: "Cancelling isn't one careless click: it opens a checklist (notify invitees, refund deposits) and only finalizes once every step is done." },
  { tab: "lists", title: "Check-in & name tags", body: "Run the door: check attendees in, track meals, and print name tags — allergies and dietary tags come along automatically." },
  { tab: "financials", title: "Money over time", body: "A real payments ledger behind the scenes: revenue by month, quarter, or year, split across dues and events — the money picture a spreadsheet never gave you." },
  { tab: "messages", title: "Communications", body: "Message members and event invitees, with a log of everything that went out — reminders, announcements, and cancellation notices." },
  { tab: "newsletter", title: "Newsletter", body: "Write a rich-text announcement (or attach a PDF) and publish it straight to the top of every member's portal." },
  { tab: "team", title: "Team & roles", body: "Invite teammates and set roles — admin, event manager, staff, viewer — each seeing exactly what their job needs. This is the multi-user foundation the whole app is built for." },
  { member: true, title: "The member's view", body: "This is what a signed-in member sees: your newsletter up top, a jump-nav to their info, membership and renewal status, RSVPs with add-to-calendar, dietary self-service, and event feedback." },
  { tab: "dashboard", title: "That's the tour 🎉", body: "Explore anything freely now — every number is live and every screen works. This is a working demo of software built around how your organization actually runs. Thanks for looking." },
];

function TourCard({ step, index, total, onBack, onNext, onCheck, onClose, pos, onDrag }) {
  const last = index === total - 1;
  const cardRef = useRef(null);
  const drag = useRef(null);
  const onMove = (e) => {
    const d = drag.current; if (!d || !cardRef.current) return;
    const w = cardRef.current.offsetWidth, h = cardRef.current.offsetHeight;
    let l = d.l + (e.clientX - d.sx), t = d.t + (e.clientY - d.sy);
    l = Math.max(6, Math.min(window.innerWidth - w - 6, l));
    t = Math.max(6, Math.min(window.innerHeight - h - 6, t));
    onDrag && onDrag({ left: l, top: t });
  };
  const onUp = () => { drag.current = null; window.removeEventListener("pointermove", onMove); window.removeEventListener("pointerup", onUp); };
  const onDown = (e) => {
    if (!cardRef.current) return;
    const r = cardRef.current.getBoundingClientRect();
    drag.current = { sx: e.clientX, sy: e.clientY, l: r.left, t: r.top };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };
  const base = { position: "fixed", zIndex: 900, width: "min(460px, calc(100vw - 24px))" };
  const wrapStyle = pos ? { ...base, left: pos.left, top: pos.top } : { ...base, left: "50%", bottom: 14, transform: "translateX(-50%)" };
  return (
    <div ref={cardRef} style={wrapStyle}>
      <div style={{ background: "#FFFFFF", border: `1px solid ${C.border}`, borderRadius: 14, boxShadow: "0 12px 40px rgba(20,24,30,0.28)", overflow: "hidden" }}>
        <div style={{ height: 4, background: C.bg }}><div style={{ height: "100%", width: `${(index + 1) / total * 100}%`, background: C.green, transition: "width .2s" }} /></div>
        <div onPointerDown={onDown} style={{ padding: "14px 18px 0", cursor: "move", touchAction: "none" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: C.greenDark, userSelect: "none" }}>∷ Tour · {index + 1} of {total}</span>
            <button onPointerDown={e => e.stopPropagation()} onClick={onClose} aria-label="Close tour" style={{ background: "none", border: "none", color: C.muted, fontSize: 18, cursor: "pointer", lineHeight: 1 }}>×</button>
          </div>
        </div>
        <div style={{ padding: "0 18px 16px" }}>
          <div style={{ fontSize: 17, fontWeight: 800, color: C.text, marginBottom: 5 }}>{step.title}</div>
          <div style={{ fontSize: 14, color: C.textSoft, lineHeight: 1.5 }}>{step.body}</div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 16, flexWrap: "wrap" }}>
            {index > 0 && <button style={{ ...styles.btn("ghost"), padding: "8px 12px" }} onClick={onBack}>◂ Back</button>}
            <button style={{ ...styles.btn("ghost"), padding: "8px 12px", borderColor: "#B98A3E", color: "#7A5A1E" }} onClick={onCheck}>Check it out</button>
            <div style={{ flex: 1 }} />
            <button style={{ ...styles.btn("primary"), padding: "8px 16px" }} onClick={last ? onClose : onNext}>{last ? "Done" : "Next ▸"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function TourPill({ index, total, onResume }) {
  return (
    <button onClick={onResume}
      style={{
        position: "fixed", right: 14, bottom: 16, zIndex: 900, cursor: "pointer",
        background: "#F5C24B", color: "#4A3A12", border: "1px solid #E3A81F", borderRadius: 999,
        padding: "10px 16px", fontWeight: 800, fontSize: 13, boxShadow: "0 6px 20px rgba(227,168,31,0.5)",
        animation: "tourPulse 1.8s ease-in-out infinite",
      }}>
      <style>{`@keyframes tourPulse { 0%,100% { box-shadow: 0 6px 20px rgba(227,168,31,0.45); } 50% { box-shadow: 0 6px 26px rgba(227,168,31,0.85); } }`}</style>
      ▸ Resume tour · {index + 1}/{total}
    </button>
  );
}

export default function App() {
  const [session, setSession] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [tab, setTab] = useState("dashboard");
  const [openEventId, setOpenEventId] = useState(null);
  const [showGroups, setShowGroups] = useState(false);
  const [tags, setTags] = useState([]); // member groups (demo-level; persist later)
  const [role, setRole] = useState("admin"); // demo role preview: admin | staff | viewer
  const [memberViewId, setMemberViewId] = useState(null); // demo "view as member" — a member id or null
  const [managerViewEventId, setManagerViewEventId] = useState(null); // demo "view as event manager" — an event id or null
  const perms = can(role);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => { setSession(data.session); setAuthChecked(true); });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => setSession(s));
    return () => sub.subscription.unsubscribe();
  }, []);

  const ready = !!session;
  const [members, membersDispatch] = useTable("mbr_members", {}, ready);
  const [events, eventsDispatch] = useTable("mbr_events", {}, ready);
  const [invitations, invDispatch] = useTable("mbr_invitations", {}, ready);
  const [expenses, expDispatch] = useTable("mbr_event_expenses", {}, ready);
  const [vendors, venDispatch] = useTable("mbr_vendors", {}, ready);
  const [tiers, tiersDispatch] = useTable("mbr_tiers", {}, ready);
  const [prospects, prospectsDispatch] = useTable("mbr_prospects", {}, ready);
  const [messages, messagesDispatch] = useTable("mbr_messages", {}, ready);
  const [checklistDefaults, checklistDefaultsDispatch] = useTable("mbr_checklist_defaults", {}, ready);
  const [providers, providersDispatch] = useTable("mbr_providers", {}, ready);
  const [providerReviews, providerReviewsDispatch] = useTable("mbr_provider_reviews", {}, ready);
  const [surveyResponses, surveyResponsesDispatch] = useTable("mbr_survey_responses", {}, ready);
  const [announcements, announcementsDispatch] = useTable("mbr_announcements", {}, ready);
  const currentAnnouncement = (announcements || []).filter(a => a.published).sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))[0] || null;
  const [dashLayout, dashLayoutDispatch] = useTable("mbr_dashboard", {}, ready);
  const [payments, paymentsDispatch] = useTable("mbr_payments", {}, ready);
  const [team, teamDispatch] = useTable("mbr_team", {}, ready);

  // Derive the set of tags actually in use, merged with user-defined ones.
  const allTags = useMemo(() => {
    const used = new Set(tags);
    members.forEach(m => (m.tags || []).forEach(t => used.add(t)));
    return Array.from(used).sort();
  }, [tags, members]);

  const lock = async () => { await supabase.auth.signOut(); setOpenEventId(null); };

  // Guided product tour state — hooks must run before the auth gate below (React #310)
  const [tourOpen, setTourOpen] = useState(false);
  const [tourMin, setTourMin] = useState(false);
  const [tourStep, setTourStep] = useState(0);
  const [tourPos, setTourPos] = useState(null);
  useEffect(() => { try { if (!localStorage.getItem("mbr_tour_done")) setTourOpen(true); } catch (e) { setTourOpen(true); } }, []);
  if (!authChecked) return <div style={{ ...styles.app, display: "flex", alignItems: "center", justifyContent: "center" }}><div style={{ color: C.muted }}>Loading…</div></div>;
  if (!session) return <LoginGate />;

  const openEvent = events.find(e => String(e.id) === String(openEventId));
  const viewMember = memberViewId ? members.find(m => String(m.id) === String(memberViewId)) : null;
  const managerViewEvent = managerViewEventId ? events.find(e => String(e.id) === String(managerViewEventId)) : null;
  const managerViewManagerName = (() => { if (!managerViewEvent || !managerViewEvent.managerId) return ""; const m = members.find(x => String(x.id) === String(managerViewEvent.managerId)); return m ? `${m.firstName || ""} ${m.lastName || ""}`.trim() : ""; })();
  const tourEventId = (() => { const g = events.find(e => e.name === "Annual Awards Gala"); return g ? g.id : (events[0] && events[0].id) || null; })();
  const goStep = (n) => {
    if (n < 0 || n >= TOUR_STEPS.length) return;
    setTourStep(n);
    const st = TOUR_STEPS[n];
    setManagerViewEventId(null);
    if (st.member) { setOpenEventId(null); if (members[0]) setMemberViewId(members[0].id); return; }
    setMemberViewId(null);
    if (st.openEvent && tourEventId) { setTab("events"); setOpenEventId(tourEventId); }
    else { setOpenEventId(null); if (st.tab) setTab(st.tab); }
  };
  const startTour = () => { setTourMin(false); setTourOpen(true); setTourPos(null); goStep(0); };
  const endTour = () => { setTourOpen(false); setTourMin(false); try { localStorage.setItem("mbr_tour_done", "1"); } catch (e) {} };
  const resumeTour = () => { setTourMin(false); goStep(tourStep); };

  return (
    <div style={styles.app}>
      <header style={{ background: C.slate, color: "#FFF", padding: "0 24px", display: "flex", alignItems: "center", justifyContent: "space-between", height: 68, boxShadow: "0 2px 8px rgba(35,49,66,0.25)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <a href="https://wilcodatasolutions.com" title="Back to wilcodatasolutions.com" style={{ background: "#F9F8F6", borderRadius: 10, padding: "5px 12px", display: "flex", alignItems: "center", gap: 8, textDecoration: "none" }}>
            <img src="/logo-mark.svg" alt="" style={{ height: 30 }} onError={e => { e.target.style.display = "none"; }} />
            <span style={{ fontWeight: 800, color: C.slate, fontSize: 16 }}>Wilco <span style={{ color: C.green }}>Data Solutions</span></span>
          </a>
          <a href="https://wilcodatasolutions.com" className="hide-mobile" style={{ fontSize: 13, color: "#FFF", opacity: 0.85, textDecoration: "none", display: "flex", alignItems: "center", gap: 4 }}>← Back to site</a>
          <span className="hide-mobile" style={{ fontSize: 14, opacity: 0.85 }}>Membership &amp; Events</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {!viewMember && (
            <div style={{ display: "flex", alignItems: "center", gap: 6, background: "rgba(255,255,255,0.12)", borderRadius: 9, padding: "4px 6px" }}>
              <span className="hide-mobile" style={{ fontSize: 11, opacity: 0.7, paddingLeft: 4 }}>View as:</span>
              {Object.entries(ROLES).map(([key, r]) => (
                <button key={key} onClick={() => setRole(key)} title={r.blurb}
                  style={{ background: role === key ? "#FFF" : "transparent", color: role === key ? C.slate : "#FFF", border: "none", borderRadius: 6, padding: "5px 10px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                  {r.label}
                </button>
              ))}
            </div>
          )}
          {!viewMember && members.length > 0 && (
            <button title="Preview the member self-service view" style={{ background: "rgba(255,255,255,0.15)", color: "#FFF", border: "none", borderRadius: 8, padding: "7px 12px", fontWeight: 650, fontSize: 12, cursor: "pointer" }} onClick={() => setMemberViewId(members[0].id)}>👤 Member view</button>
          )}
          <button title="Take a guided tour of the demo" style={{ background: "#F5C24B", color: "#4A3A12", border: "none", borderRadius: 999, padding: "7px 14px", fontWeight: 800, fontSize: 13, cursor: "pointer" }} onClick={startTour}>Take a tour</button>
          <button style={{ background: "rgba(255,255,255,0.15)", color: "#FFF", border: "none", borderRadius: 8, padding: "7px 14px", fontWeight: 600, fontSize: 13, cursor: "pointer" }} onClick={lock}>Sign Out</button>
        </div>
      </header>

      {viewMember ? (
        <MemberPortal member={viewMember} members={members} events={events} invitations={invitations} tiers={tiers} invDispatch={invDispatch} prospectsDispatch={prospectsDispatch} membersDispatch={membersDispatch} announcement={currentAnnouncement} surveyResponses={surveyResponses} surveyResponsesDispatch={surveyResponsesDispatch} onPickMember={setMemberViewId} onExit={() => setMemberViewId(null)} />
      ) : managerViewEvent ? (
        <>
          <div style={{ background: C.slate, color: "#FFF", padding: "10px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
            <div style={{ fontSize: 13, opacity: 0.92 }}>🗂️ <strong>Event Manager view</strong> — {managerViewManagerName ? `${managerViewManagerName} · ` : ""}{managerViewEvent.name}. Scoped to this event; no financials or approvals.</div>
            <button style={{ ...styles.btn("ghost"), color: "#FFF", borderColor: "rgba(255,255,255,0.4)", padding: "6px 12px" }} onClick={() => setManagerViewEventId(null)}>← Exit</button>
          </div>
          <main style={styles.main}>
            <EventDetail event={managerViewEvent} members={members} invitations={invitations} invDispatch={invDispatch} expenses={expenses} expDispatch={expDispatch} vendors={vendors} venDispatch={venDispatch} messagesDispatch={messagesDispatch} evDispatch={eventsDispatch} providers={providers} payments={payments} tiers={tiers} reviews={providerReviews} reviewsDispatch={providerReviewsDispatch} surveyResponses={surveyResponses} paymentsDispatch={paymentsDispatch} perms={MANAGER_PERMS} onBack={() => setManagerViewEventId(null)} />
          </main>
        </>
      ) : (
        <>
      {!openEvent && (
        <nav style={{ background: C.surface, borderBottom: `1px solid ${C.border}`, padding: "0 16px", display: "flex", gap: 4, overflowX: "auto" }}>
          {TABS.filter(t => (t.id !== "financials" || perms.financials) && (t.id !== "membership" || perms.editMembers) && (t.id !== "messages" || perms.editMembers)).map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} style={{ background: "none", border: "none", padding: "16px 16px", fontSize: 14, fontWeight: 650, cursor: "pointer", color: tab === t.id ? C.green : C.textSoft, borderBottom: tab === t.id ? `3px solid ${C.green}` : "3px solid transparent", whiteSpace: "nowrap" }}>{t.label}</button>
          ))}
        </nav>
      )}

      {/* Demo role banner */}
      <div style={{ background: C.greenTint, borderBottom: `1px solid ${C.border}`, padding: "8px 20px", fontSize: 13, color: C.greenDark, textAlign: "center" }}>
        👁️ Previewing as <strong>{ROLES[role].label}</strong> — {ROLES[role].blurb} <span style={{ color: C.textSoft }}>(Demo preview of role-based access.)</span>
      </div>

      <main style={styles.main}>
        {openEvent ? (
          <EventDetail event={openEvent} members={members} invitations={invitations} invDispatch={invDispatch} expenses={expenses} expDispatch={expDispatch} vendors={vendors} venDispatch={venDispatch} messagesDispatch={messagesDispatch} evDispatch={eventsDispatch} providers={providers} payments={payments} tiers={tiers} reviews={providerReviews} reviewsDispatch={providerReviewsDispatch} surveyResponses={surveyResponses} paymentsDispatch={paymentsDispatch} onPreviewManager={setManagerViewEventId} perms={perms} onBack={() => setOpenEventId(null)} />
        ) : (
          <>
            {tab === "dashboard" && <Dashboard events={events} members={members} invitations={invitations} expenses={expenses} vendors={vendors} tiers={tiers} layout={dashLayout} layoutDispatch={dashLayoutDispatch} perms={perms} onOpenEvent={setOpenEventId} onGoToEvents={() => setTab("events")} />}
            {tab === "members" && <Members members={members} dispatch={membersDispatch} allTags={allTags} onManageTags={() => setShowGroups(true)} tiers={tiers} perms={perms} />}
            {tab === "membership" && perms.editMembers && <Membership members={members} membersDispatch={membersDispatch} tiers={tiers} tiersDispatch={tiersDispatch} messagesDispatch={messagesDispatch} paymentsDispatch={paymentsDispatch} perms={perms} />}
            {tab === "events" && <Events events={events} dispatch={eventsDispatch} invitations={invitations} onOpenEvent={setOpenEventId} checklistDefaults={checklistDefaults} checklistDefaultsDispatch={checklistDefaultsDispatch} providers={providers} providersDispatch={providersDispatch} vendors={vendors} payments={payments} reviews={providerReviews} perms={perms} />}
            {tab === "lists" && <Lists events={events} members={members} invitations={invitations} invDispatch={invDispatch} prospects={prospects} prospectsDispatch={prospectsDispatch} membersDispatch={membersDispatch} paymentsDispatch={paymentsDispatch} perms={perms} />}
            {tab === "financials" && perms.financials && <Financials events={events} invitations={invitations} expenses={expenses} vendors={vendors} payments={payments} />}
            {tab === "financials" && !perms.financials && <div style={styles.card}><div style={styles.empty}>Financials aren't available for this role.</div></div>}
            {tab === "messages" && perms.editMembers && <Communications messages={messages} messagesDispatch={messagesDispatch} members={members} events={events} perms={perms} />}
            {tab === "team" && perms.editMembers && <Team team={team} dispatch={teamDispatch} perms={perms} />}
            {tab === "newsletter" && perms.editMembers && <Newsletter announcements={announcements} dispatch={announcementsDispatch} />}
            {tab === "newsletter" && !perms.editMembers && <div style={styles.card}><div style={styles.empty}>The newsletter editor isn't available for this role.</div></div>}
            {tab === "team" && !perms.editMembers && <div style={styles.card}><div style={styles.empty}>Team management isn't available for this role.</div></div>}
          </>
        )}
      </main>
        </>
      )}

      {showGroups && <GroupManager tags={allTags} onChange={setTags} onClose={() => setShowGroups(false)} />}
      {tourOpen && !tourMin && <TourCard step={TOUR_STEPS[tourStep]} index={tourStep} total={TOUR_STEPS.length} onBack={() => goStep(tourStep - 1)} onNext={() => goStep(tourStep + 1)} onCheck={() => setTourMin(true)} onClose={endTour} pos={tourPos} onDrag={setTourPos} />}
      {tourOpen && tourMin && <TourPill index={tourStep} total={TOUR_STEPS.length} onResume={resumeTour} />}
    </div>
  );
}
